// ════════════════════════════════════════════════════════════
//  ieTerrorSpecies.jsx — THE TERROR SPECIES KIT
// ════════════════════════════════════════════════════════════
//  Forty-one beings out of the Final Boss vault that Terror named
//  and nothing ever built. Same recipe as `ieBeingModels.jsx`,
//  same laws, same 4xS bar — a SECOND kit rather than an edit,
//  because the first one is cross-game canonical and the
//  anti-drift law says new beings are an APPEND, never a refactor.
//
//  ── WHERE EVERY FACT ON THESE CARDS CAME FROM ───────────────
//  `Final Boss/04 Beings/Species/*.md` (one file per species),
//  `Classes/*.md` (the four Schools + their rosters and epithets),
//  `Schools/N'ihil (Terror).md` (the Radiant/Twinkling/Lustered/
//  Resplendant/Dazzling epithets) and `The Fifty-Three` (the
//  Ilk-rides-1:1-with-School ruling). READ, not remembered.
//
//  ── THE THREE AXES, PER THE SESSION-7 RULING ────────────────
//    ilk   = the top-RIGHT stamp — Phantom / Space / Dragon / Time
//            (Terror calls this the SCHOOL; Rush's field has been
//            called `ilk` since the slice and is not renamed here)
//    cls   = the top-LEFT stamp — Beast / Humanoid / Machine /
//            Dragon. Rides 1:1 with the School in current Terror.
//    trad  = the deeper tradition underneath — N'ihil, O'mnia,
//            Solaea, Lunaea, Astraea, Arcane Art, Dark Art,
//            Avarice, Shadow Gambling, Infinaite, Aeternal.
//            VERBATIM off the species' own vault note.
//
//  ⚑ WHAT IS AUTHORED HERE AND NOT TRANSCRIBED: the Lyf/Lyt line.
//  The vault gives these species their School, their tradition and
//  their nature — it does not give them a stat line, because they
//  have no printed cards yet. Every `lyf`/`lyt` below is therefore
//  RUSH-AUTHORED from what the note actually says (a tank is a
//  tank, a healer holds Lyf, a thing built on destruction spends
//  everything on Lyt) and every row carries `provisional: true`.
//  The instant a real card exists, that row is overwritten and
//  NOTHING else in the game changes — the whole handling model
//  derives from these two numbers (RR L2).
//
//  ── THE ASCENT ──────────────────────────────────────────────
//  Beb bodies are hand-authored, one per species. Champion and
//  Lord are STATION, not scale: `ASCENT` gives each species its
//  own regalia — what it rises into, what it wears, what it is
//  crowned with — and `<Station/>` renders that over the body.
//  This is the same order `ieBeingModels` shipped in (the other
//  eight were Beb-only for two batches before 3D-24/25 carved
//  their tiers), and hand-carving these 82 tier bodies is the
//  next batch, not a missing one.
//
//  ── REQUIREMENTS ────────────────────────────────────────────
//  react · three · @react-three/fiber · and ieBeingModels.jsx for
//  the shared seeded-PRNG / texture-cache / rig helpers ONLY. It
//  imports NO game files, so it ports exactly like the first kit.
// ════════════════════════════════════════════════════════════
import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { canvasTex, mulberry32, hashId, useBeingRig, TIERS } from "./ieBeingModels";

export const SPECIES_KIT_VERSION = "1.0.0";

// ════════════════════════════════════════════════════════════
//  THE CARD FACTS — 41 species, read off the vault
// ════════════════════════════════════════════════════════════
export const SPECIES_CARD = {
  // ── PHANTOM · Beast ───────────────────────────────────────
  xolibrix: { name: "Spectral Xolibrix", ilk: "Phantom", cls: "Beast", lyf: 5, lyt: 3, provisional: true,
    trad: "Spectral", flavor: "Colibri — the hummingbird. A healer that never once stops moving, and heals in the half-second it is holding still." },
  ursaeva: { name: "Shadow Ursaeva", ilk: "Phantom", cls: "Beast", lyf: 7, lyt: 1, provisional: true,
    trad: "Shadow", flavor: "The bear of the Shadow Spheres. Nothing on the roster is harder to move and nothing is slower to arrive." },
  bunnovian: { name: "Spectral Bunnovian", ilk: "Phantom", cls: "Beast", lyf: 6, lyt: 2, provisional: true,
    trad: "Spectral", flavor: "A healer with a hare's heart. It does not fight you; it simply outlasts the part of you that wanted to fight." },
  skuryz: { name: "Skuryz", ilk: "Phantom", cls: "Beast", lyf: 3, lyt: 5, provisional: true,
    trad: "Shadow Gambling", flavor: "Rats of the wager. They do not steal what you are holding — they steal the odds you were counting on." },
  trunaran: { name: "Trunaran", ilk: "Phantom", cls: "Beast", lyf: 7, lyt: 1, provisional: true,
    trad: "Phantom", flavor: "The elephant. A tank in a world that has not yet worked out what to do about one." },
  kyrium: { name: "Kyrium", ilk: "Phantom", cls: "Beast", lyf: 5, lyt: 3, provisional: true,
    trad: "Arcane Art · Dark Art", flavor: "Born from the essence of darkness itself. Amorphous, tall, absorbing the light around them — and their Archons keep the Great Library of Shadows." },
  slijj: { name: "Slijj", ilk: "Phantom", cls: "Beast", lyf: 6, lyt: 2, provisional: true,
    trad: "Dark Art", flavor: "Skin like rich soil, eyes that hold a faint candlelit glow as if the light of the dead lingers in them, and hair woven from the threads of shadow itself." },
  velkor: { name: "Velkor", ilk: "Phantom", cls: "Beast", lyf: 2, lyt: 6, provisional: true,
    trad: "Arcane Art", flavor: "Mist creatures. There is a great deal less of a Velkor than there appears to be, and all of it is the dangerous part." },
  noxura: { name: "Noxura", ilk: "Phantom", cls: "Beast", lyf: 4, lyt: 4, provisional: true,
    trad: "Lunaea · Shadow Gambling", flavor: "They never wanted the truth. They saw past their own world to the Writer, and worship the Writer alone — creator regardless of who stands above him." },
  abyssia: { name: "Abyssia", ilk: "Phantom", cls: "Beast", lyf: 3, lyt: 5, provisional: true,
    trad: "Shadow Gambling · Lunaea", flavor: "The Nightshade species. Beautiful, patient, and entirely poisonous — the dual and the opposite of Embearaea." },
  embearaea: { name: "Embearaea", ilk: "Phantom", cls: "Beast", lyf: 5, lyt: 3, provisional: true,
    trad: "Shadow Gambling · Solaea", flavor: "Firecracker and bear and flowering thing all at once. Like Noxura, they believe in the Writer. Unlike Noxura, they are loud about it." },
  carcharion: { name: "Resplendant Carcharion", ilk: "Phantom", cls: "Beast", lyf: 2, lyt: 6, provisional: true,
    trad: "N'ihil · Arcane Art", flavor: "The shark. The dual of Carnifex — one of them is honest about what it is for." },
  carnifex: { name: "Lustered Carnifex", ilk: "Phantom", cls: "Beast", lyf: 7, lyt: 1, provisional: true,
    trad: "O'mnia · Dark Art", flavor: "A habit, a hood, and something under it that the vault only ever describes by what it is not." },
  geulaoin: { name: "Lustered Geulaoin", ilk: "Phantom", cls: "Beast", lyf: 3, lyt: 5, provisional: true,
    trad: "Avarice · O'mnia", flavor: "Red, and avian, and dual to Rapaxi. One hunts from above and one hunts from below and neither has ever gone hungry." },
  rapaxi: { name: "Radiant Rapaxi", ilk: "Phantom", cls: "Beast", lyf: 4, lyt: 4, provisional: true,
    trad: "Avarice · N'ihil", flavor: "Blue, reptile, and one of the many species that worship Avarice as a religion rather than a practice — collectively, the Voracians." },
  gorgxonox: { name: "Gorgxonox", ilk: "Phantom", cls: "Beast", lyf: 7, lyt: 1, provisional: true,
    trad: "Avarice · Dark Art", flavor: "Giant. Nasty. Beast. The vault says exactly that much and stops, which is itself a warning." },
  aeternavis: { name: "Radiant Aeternavis", ilk: "Phantom", cls: "Beast", lyf: 1, lyt: 7, provisional: true,
    trad: "O'mnia · Solaea", flavor: "Eagle and phoenix at once, and the dual of Vyren. It spends everything it has every single time and has never once run out." },
  tzhahws: { name: "Tzhahws", ilk: "Phantom", cls: "Beast", lyf: 4, lyt: 4, provisional: true,
    trad: "Avarice", flavor: "Thieves. Their attackers are pirates, their Lord is the Captain and their tank is the Buccaneer, and none of that was ever a metaphor." },
  valtorix: { name: "Valtorix", ilk: "Phantom", cls: "Beast", lyf: 5, lyt: 3, provisional: true,
    trad: "Dark Art · Solaea", flavor: "Skeleton and horse and neither. It was already running when the flesh came off and it did not notice." },

  // ── SPACE · Humanoid ──────────────────────────────────────
  lyraei: { name: "Radiant Lyraei", ilk: "Space", cls: "Humanoid", lyf: 5, lyt: 3, provisional: true,
    trad: "Arcane Art · N'ihil", flavor: "Tiny, delicate, luminous. They are Caelestis — a religion first and a school of magic second — and they heal wounds and calm troubled minds with the same light." },
  malumora: { name: "Radiant Malumora", ilk: "Space", cls: "Humanoid", lyf: 2, lyt: 6, provisional: true,
    trad: "Dark Art · O'mnia", flavor: "A tiny devil, and the exact opposite of Lyraei — same size, same light, nothing else in common." },
  aealix: { name: "Twinkling Aealix", ilk: "Space", cls: "Humanoid", lyf: 1, lyt: 7, provisional: true,
    trad: "N'ihil · Lunaea", flavor: "Built on a lie. They are not the power of the sun — their brightness is not sunlight at all but the collision of particles, and every spectacular light they make is something being destroyed." },
  aeolix: { name: "Twinkling Aeolix", ilk: "Space", cls: "Humanoid", lyf: 3, lyt: 5, provisional: true,
    trad: "O'mnia · Solaea", flavor: "Niloxia — the worship of X'Aol. Somewhere the worship reversed itself, and so an O'mnia species of fire and sun represents its god as the moon and the cold." },
  calypsis: { name: "Calypsis", ilk: "Space", cls: "Humanoid", lyf: 4, lyt: 4, provisional: true,
    trad: "Lunaea · Solaea", flavor: "The mermaid. Sun above the water and moon below it, and it answers to whichever one it is nearer." },
  caelumari: { name: "Caelumari", ilk: "Space", cls: "Humanoid", lyf: 5, lyt: 3, provisional: true,
    trad: "Lunaea", flavor: "Sky and sea, named together because a Caelumari has never accepted that they are two different things." },
  zriidians: { name: "Radiant Zriidians", ilk: "Space", cls: "Humanoid", lyf: 6, lyt: 2, provisional: true,
    trad: "Astraea · O'mnia", flavor: "The opposite of Hyalayri: where one is clear, the Zriidians are filled — and what they are filled with is starlight." },
  hyalayri: { name: "Radiant Hyalayri", ilk: "Space", cls: "Humanoid", lyf: 2, lyt: 6, provisional: true,
    trad: "Infinaite · N'ihil", flavor: "Glass all the way through. You can see everything a Hyalayri is about to do and it does not help you at all." },
  riven: { name: "Riven", ilk: "Space", cls: "Humanoid", lyf: 3, lyt: 5, provisional: true,
    trad: "Arcane Art · Astraea", flavor: "Enemies to Kraalok — a being that is genuinely two pieces, held apart by the same force that holds them together." },
  devorix: { name: "Devorix", ilk: "Space", cls: "Humanoid", lyf: 5, lyt: 3, provisional: true,
    trad: "Aeternal (Astraea) · Avarice", flavor: "Aeternal appetite. There is no version of a Devorix that is finished eating." },

  // ── TIME · Machine ────────────────────────────────────────
  kraalok: { name: "Kraalok", ilk: "Time", cls: "Machine", lyf: 6, lyt: 2, provisional: true,
    trad: "Dark Art · Time", flavor: "Spongelike, and with no real ambition — but whatever a Kraalok does pursue becomes its sole purpose, and it puts everything it has into it." },
  lyrixi: { name: "Lyrixi", ilk: "Time", cls: "Machine", lyf: 2, lyt: 6, provisional: true,
    trad: "Arcane Art · Time", flavor: "A realm of eternal twilight, skies painted sapphire and amethyst, and crystalline homes that resonate with the harmonies of the universe. Their songs heal. Their society is a network of resonant frequencies." },
  mygzori: { name: "Mygzori", ilk: "Time", cls: "Machine", lyf: 3, lyt: 5, provisional: true,
    trad: "Astraea · Time", flavor: "They believe there is always a greater god above the Writer — and that their own existence proves it. So they love the Writer back, and the loop closes, and they know it does." },
  rosaluna: { name: "Rosaluna", ilk: "Time", cls: "Machine", lyf: 4, lyt: 4, provisional: true,
    trad: "Lunaea · Time", flavor: "The Moon Rose. Its move is Malarosa, and it is the opposite of Auroraeth in every way except the one that matters." },
  auroraeth: { name: "Auroraeth", ilk: "Time", cls: "Machine", lyf: 4, lyt: 4, provisional: true,
    trad: "Astraea · Solaea", flavor: "The Sun Rose, and the opposite of Rosaluna. Two roses, one clock, twelve hours apart forever." },
  zylaraeon: { name: "Zylaraeon", ilk: "Time", cls: "Machine", lyf: 1, lyt: 7, provisional: true,
    trad: "Avarice · Shadow Gambling", flavor: "They believe there is a greater devil above the Writer, and that he is wrongly accused. They understand the loop exactly as Mygzori does — and they mean to ruin it." },
  magmantus: { name: "Magmantus", ilk: "Time", cls: "Machine", lyf: 7, lyt: 1, provisional: true,
    trad: "Solaea", flavor: "The Lava Golem. Magmantus is the Lord; the small ones are called Magmori, and there is no gentle way to be either." },

  // ── DRAGON · Dragon ───────────────────────────────────────
  vivus_dragon: { name: "V'ivus Dragon", ilk: "Dragon", cls: "Dragon", lyf: 4, lyt: 4, provisional: true,
    trad: "Enlightened", flavor: "The fourth Dragon and the last to be named: Premier is holy, Chaos is evil, Lucky is J'aeonxi, and V'ivus is Enlightened." },
};
export const SPECIES_IDS = Object.keys(SPECIES_CARD);

// ════════════════════════════════════════════════════════════
//  THE PALETTES — main / deep / glow / accent, same four keys
//  the first kit uses, so a host reads one shape for 53 beings.
// ════════════════════════════════════════════════════════════
const SPECIES_PALETTE = {
  xolibrix:    { main: "#2ee8a8", deep: "#0a2a26", glow: "#a8ffe0", accent: "#ff5aa8" },
  ursaeva:     { main: "#4a4468", deep: "#0d0b16", glow: "#b8a8ff", accent: "#ffb02e" },
  bunnovian:   { main: "#f2ecff", deep: "#2a2438", glow: "#ffffff", accent: "#ff9ec4" },
  skuryz:      { main: "#8a7f6a", deep: "#181410", glow: "#ffd24a", accent: "#c4453c" },
  trunaran:    { main: "#6a6f7a", deep: "#16181e", glow: "#c8d4e0", accent: "#e8b04a" },
  kyrium:      { main: "#241a3a", deep: "#06040c", glow: "#8a5aff", accent: "#c8a8ff" },
  slijj:       { main: "#4a3524", deep: "#140d08", glow: "#ffb04a", accent: "#2a1c14" },
  velkor:      { main: "#b8c4d0", deep: "#2a323c", glow: "#e8f4ff", accent: "#7fa8c8" },
  noxura:      { main: "#2a2a4a", deep: "#0a0a16", glow: "#e8e0ff", accent: "#ffd88a" },
  abyssia:     { main: "#3a2450", deep: "#0e0818", glow: "#a86aff", accent: "#5aff9e" },
  embearaea:   { main: "#c45a2a", deep: "#3a1408", glow: "#ffd24a", accent: "#4a8a3a" },
  carcharion:  { main: "#5a7a8a", deep: "#0e1a20", glow: "#d8f0ff", accent: "#ff4a4a" },
  carnifex:    { main: "#3a3630", deep: "#0c0a08", glow: "#e8d8a8", accent: "#8a1a1a" },
  geulaoin:    { main: "#c8342a", deep: "#3a0c08", glow: "#ffb02e", accent: "#f0d8a8" },
  rapaxi:      { main: "#2a6ac8", deep: "#0a1c3a", glow: "#4ad8ff", accent: "#ffd24a" },
  gorgxonox:   { main: "#5a4a2a", deep: "#161008", glow: "#8ae23c", accent: "#c4453c" },
  aeternavis:  { main: "#ffb02e", deep: "#5a2408", glow: "#ffe8a8", accent: "#ff5a1a" },
  tzhahws:     { main: "#7a5a3a", deep: "#1c1208", glow: "#ffd24a", accent: "#c8342a" },
  valtorix:    { main: "#d8d0bc", deep: "#241c14", glow: "#ff8a2e", accent: "#3a2a1c" },
  lyraei:      { main: "#fff0c8", deep: "#3a3020", glow: "#ffffff", accent: "#8ad8ff" },
  malumora:    { main: "#c8344a", deep: "#2a0810", glow: "#ff8a5a", accent: "#241820" },
  aealix:      { main: "#e8f0ff", deep: "#1a2038", glow: "#ffffff", accent: "#8a5aff" },
  aeolix:      { main: "#8ad8ff", deep: "#0e2038", glow: "#e8f8ff", accent: "#ffb02e" },
  calypsis:    { main: "#2ec8b4", deep: "#08282a", glow: "#a8ffe8", accent: "#ffd88a" },
  caelumari:   { main: "#4a9ad8", deep: "#0a1e34", glow: "#c8e8ff", accent: "#f0e8c8" },
  zriidians:   { main: "#ffd88a", deep: "#3a2c10", glow: "#fff4d8", accent: "#4ad8ff" },
  hyalayri:    { main: "#c8e8f0", deep: "#1c2a30", glow: "#ffffff", accent: "#8affe0" },
  riven:       { main: "#8a6ad8", deep: "#1a1030", glow: "#c8b0ff", accent: "#ff5a8a" },
  devorix:     { main: "#3a2a4a", deep: "#0c0812", glow: "#ff8a2e", accent: "#8ae23c" },
  kraalok:     { main: "#6a8a5a", deep: "#141c10", glow: "#c8f08a", accent: "#8a5a3a" },
  lyrixi:      { main: "#6a5ad8", deep: "#120e30", glow: "#a8ffe8", accent: "#e8a8ff" },
  mygzori:     { main: "#3a5a7a", deep: "#0a1420", glow: "#8ad8ff", accent: "#ffd24a" },
  rosaluna:    { main: "#c86a9a", deep: "#2c0e1e", glow: "#e8c8ff", accent: "#8ad8c8" },
  auroraeth:   { main: "#e88a3a", deep: "#3a1a08", glow: "#ffe0a8", accent: "#ff5a5a" },
  zylaraeon:   { main: "#1a1a22", deep: "#050508", glow: "#ff2e4a", accent: "#8a1a2e" },
  magmantus:   { main: "#3a2420", deep: "#0e0808", glow: "#ff6a1a", accent: "#ffd24a" },
  vivus_dragon:{ main: "#4ad8b4", deep: "#0c2c28", glow: "#e8fff8", accent: "#ffd88a" },
};
export const getSpeciesPalette = (id) =>
  SPECIES_PALETTE[id] || { main: "#c9a84c", deep: "#241c12", glow: "#f0d060", accent: "#e8d8a8" };

// ════════════════════════════════════════════════════════════
//  THE SHARED PAINT BOX
// ════════════════════════════════════════════════════════════
//  Every texture below is one cached canvas, seeded, drawn once.
//  These are the primitives the 41 bodies are painted with — the
//  same call `plateTex` and `eyeDisc` make in the first kit.

// the leaf stroke — flames, stripes, scale rows, petals, folds
export const leaf = (g, x, y, len, wd, ang, fill, alpha) => {
  g.save(); g.translate(x, y); g.rotate(ang);
  g.beginPath(); g.moveTo(0, -len / 2);
  g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
  g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
  g.closePath(); g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
  g.restore(); g.globalAlpha = 1;
};

// THE EYE. A painted disc beats eye geometry every time.
export function eyeDisc(key, core, iris, rim, pupil, slit) {
  return canvasTex(key, 128, 128, (g) => {
    const r = g.createRadialGradient(64, 58, 3, 64, 64, 62);
    r.addColorStop(0, core); r.addColorStop(0.36, iris);
    r.addColorStop(0.8, rim); r.addColorStop(1, "#0d0a08");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    if (pupil) {
      g.fillStyle = pupil; g.beginPath();
      if (slit) g.ellipse(64, 66, 8, 32, 0, 0, 7); else g.ellipse(64, 66, 20, 22, 0, 0, 7);
      g.fill();
    }
    g.fillStyle = "rgba(255,255,255,0.9)"; g.beginPath(); g.arc(47, 43, 7, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.45)"; g.beginPath(); g.arc(79, 83, 3.2, 0, 7); g.fill();
  });
}

// hide / fur / coat — a gradient ground, a seeded mottle, then
// whatever signature pass the caller paints on top of it.
export function hideTex(key, hi, mid, lo, opt = {}) {
  return canvasTex(key, 512, 512, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, hi); b.addColorStop(0.58, mid); b.addColorStop(1, lo);
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 11);
    for (let i = 0; i < (opt.grain ?? 1100); i++) {
      g.fillStyle = `rgba(${R() > 0.5 ? "255,255,255" : "0,0,0"},${0.02 + R() * 0.07})`;
      g.fillRect(R() * w, R() * h, 2 + R() * 7, 1 + R() * 3);
    }
    if (opt.fur) for (let i = 0; i < 260; i++)
      leaf(g, R() * w, R() * h, 26 + R() * 54, 2 + R() * 3, (R() - 0.5) * 2.4, opt.fur, 0.10 + R() * 0.16);
    if (opt.speck) for (let i = 0; i < 200; i++) {
      g.fillStyle = opt.speck; g.globalAlpha = 0.2 + R() * 0.6;
      g.beginPath(); g.arc(R() * w, R() * h, 0.7 + R() * 2.2, 0, 7); g.fill();
    }
    g.globalAlpha = 1;
    if (opt.after) opt.after(g, w, h, R);
  });
}

// riveted / bevelled plate — every Machine-ilk surface
export function plateTex(key, hi, mid, lo, rivet) {
  return canvasTex(key, 256, 256, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, hi); b.addColorStop(0.55, mid); b.addColorStop(1, lo);
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 5);
    for (let i = 0; i < 700; i++) {
      g.fillStyle = `rgba(${R() > 0.5 ? "255,255,255" : "0,0,0"},${0.02 + R() * 0.05})`;
      g.fillRect(R() * w, R() * h, 2 + R() * 6, 1 + R() * 2);
    }
    for (let i = 0; i < 8; i++) {
      const y = R() * h;
      g.strokeStyle = "rgba(0,0,0,0.45)"; g.lineWidth = 2.5;
      g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke();
    }
    for (let i = 0; i < 70; i++) {
      const x = R() * w, y = R() * h, rr = 2.5 + R() * 2;
      const s = g.createRadialGradient(x - rr * .3, y - rr * .3, .5, x, y, rr);
      s.addColorStop(0, rivet); s.addColorStop(1, "rgba(0,0,0,0.55)");
      g.fillStyle = s; g.beginPath(); g.arc(x, y, rr, 0, 7); g.fill();
    }
  });
}

// scale rows — leaf strokes laid in staggered courses
export function scaleTex(key, hi, mid, lo, edge, rows = 15) {
  return canvasTex(key, 512, 512, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, hi); b.addColorStop(0.6, mid); b.addColorStop(1, lo);
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 3);
    const sy = h / rows;
    for (let r = 0; r < rows; r++) {
      const cols = 13, off = (r % 2) * (w / cols / 2);
      for (let c = 0; c < cols; c++) {
        const x = off + c * (w / cols), y = r * sy + sy * 0.5;
        g.beginPath();
        g.moveTo(x, y - sy * 0.52);
        g.quadraticCurveTo(x + w / cols * 0.5, y, x, y + sy * 0.52);
        g.quadraticCurveTo(x - w / cols * 0.5, y, x, y - sy * 0.52);
        g.closePath();
        g.fillStyle = `rgba(0,0,0,${0.06 + R() * 0.10})`; g.fill();
        g.strokeStyle = edge; g.globalAlpha = 0.18 + R() * 0.22; g.lineWidth = 1.4; g.stroke();
        g.globalAlpha = 1;
      }
    }
  });
}

// feathers — long tapered strokes in courses, with a shaft
export function featherTex(key, hi, mid, lo, quill) {
  return canvasTex(key, 512, 512, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, hi); b.addColorStop(0.55, mid); b.addColorStop(1, lo);
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 7);
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 11; c++) {
        const x = (c + (r % 2) * 0.5) * (w / 11), y = r * (h / 9) + 26;
        leaf(g, x, y, 62 + R() * 26, 9 + R() * 5, 0.04 * (R() - 0.5), `rgba(0,0,0,${0.10 + R() * 0.12})`, 1);
        g.strokeStyle = quill; g.globalAlpha = 0.28 + R() * 0.3; g.lineWidth = 1.2;
        g.beginPath(); g.moveTo(x, y - 30); g.lineTo(x, y + 30); g.stroke(); g.globalAlpha = 1;
      }
    }
  });
}

// cloth — folds and a hem, for every robed and vested body
export function clothTex(key, hi, mid, lo, trim) {
  return canvasTex(key, 512, 512, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, hi); b.addColorStop(0.5, mid); b.addColorStop(1, lo);
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 13);
    for (let i = 0; i < 42; i++) {
      const x = R() * w;
      const grad = g.createLinearGradient(x - 16, 0, x + 16, 0);
      grad.addColorStop(0, "rgba(0,0,0,0)"); grad.addColorStop(0.5, `rgba(0,0,0,${0.10 + R() * 0.16})`);
      grad.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = grad; g.fillRect(x - 16, 0, 32, h);
    }
    if (trim) { g.fillStyle = trim; g.globalAlpha = 0.7; g.fillRect(0, h - 34, w, 16); g.globalAlpha = 1; }
  });
}

// crystal / glass — facets and internal light
export function crystalTex(key, hi, mid, glow) {
  return canvasTex(key, 512, 512, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, w, h);
    b.addColorStop(0, hi); b.addColorStop(0.5, mid); b.addColorStop(1, hi);
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 17);
    for (let i = 0; i < 90; i++) {
      const x = R() * w, y = R() * h, s = 30 + R() * 90, a = R() * 6.28;
      g.save(); g.translate(x, y); g.rotate(a);
      g.beginPath(); g.moveTo(0, -s * 0.5); g.lineTo(s * 0.24, 0); g.lineTo(0, s * 0.5); g.lineTo(-s * 0.24, 0);
      g.closePath();
      g.fillStyle = `rgba(255,255,255,${0.03 + R() * 0.09})`; g.fill();
      g.strokeStyle = glow; g.globalAlpha = 0.10 + R() * 0.18; g.lineWidth = 1.1; g.stroke();
      g.globalAlpha = 1; g.restore();
    }
  });
}

// the night-sky / void coat — a dark ground under real stars
export function voidTex(key, base, star, hot) {
  return canvasTex(key, 512, 512, (g, w, h) => {
    const b = g.createRadialGradient(w * 0.5, h * 0.4, 10, w * 0.5, h * 0.5, w * 0.7);
    b.addColorStop(0, hot); b.addColorStop(0.4, base); b.addColorStop(1, "#03030a");
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(hashId(key) + 23);
    for (let i = 0; i < 300; i++) {
      const x = R() * w, y = R() * h, s = 0.6 + R() * 2.2;
      g.fillStyle = star; g.globalAlpha = 0.2 + R() * 0.7;
      g.beginPath(); g.arc(x, y, s, 0, 7); g.fill();
    }
    g.globalAlpha = 1;
  });
}

// a face, painted. Brow shadow is what makes a sphere read as a head.
export function faceTex(key, skin, brow, iris, mouth, opt = {}) {
  return canvasTex(key, 256, 256, (g, w, h) => {
    g.fillStyle = skin; g.fillRect(0, 0, w, h);
    const sh = g.createLinearGradient(0, 60, 0, 128);
    sh.addColorStop(0, "rgba(0,0,0,0.42)"); sh.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = sh; g.fillRect(0, 60, w, 68);
    if (!opt.blank) {
      [88, 168].forEach((x) => {
        g.fillStyle = "#f4f0e8"; g.beginPath(); g.ellipse(x, 122, 20, 12, 0, 0, 7); g.fill();
        g.fillStyle = iris; g.beginPath(); g.arc(x, 122, 8.5, 0, 7); g.fill();
        g.fillStyle = "#0a0808"; g.beginPath(); g.arc(x, 122, opt.slit ? 3 : 4.2, 0, 7); g.fill();
        g.fillStyle = "rgba(255,255,255,0.85)"; g.beginPath(); g.arc(x - 3, 118, 2.4, 0, 7); g.fill();
        g.strokeStyle = brow; g.lineWidth = 6; g.lineCap = "round";
        g.beginPath(); g.moveTo(x - 20, 100); g.lineTo(x + 18, 96 + (opt.angry ? 10 : 0)); g.stroke();
      });
      g.strokeStyle = "rgba(0,0,0,0.30)"; g.lineWidth = 3;
      g.beginPath(); g.moveTo(128, 132); g.lineTo(124, 158); g.stroke();
      g.strokeStyle = mouth; g.lineWidth = 5;
      g.beginPath(); g.moveTo(104, 182); g.quadraticCurveTo(128, 182 + (opt.frown ? -10 : 10), 152, 182); g.stroke();
    }
    if (opt.after) opt.after(g, w, h);
  });
}

// ════════════════════════════════════════════════════════════
//  THE SHARED SHAPE BOX — kin geometry across 41 bodies
// ════════════════════════════════════════════════════════════

// ⚑ THE BEAD-CHAIN IS NOT IN THIS KIT, ON PURPOSE. The first kit's house
// tail primitive is a flat list of spheres; every tail, trunk and whip on
// these forty-one is a `Chain` instead, because a NESTED chain carries a
// rotation at segment i through every segment after it and a flat one does
// not — which is the difference between a limb and seven loose beads.
// (The kart side still uses the flat `BeadChain`, where it is bolted to a
// rigid chassis and nothing needs to curl.)
// the house wing — bone spars plus a draped membrane, mirrored
export function Membrane({ side = 1, span = 0.42, bones = 4, boneMat, memMat, sweep = 0.35 }) {
  return (
    <group rotation={[0, 0, side * sweep]}>
      {Array.from({ length: bones }).map((_, i) => (
        <mesh key={i} position={[side * span * 0.5 * (1 - i * 0.04), -i * 0.05, -i * 0.06]}
          rotation={[0, 0, side * (0.30 - i * 0.22)]}>
          <capsuleGeometry args={[0.014 - i * 0.002, span * (0.94 - i * 0.10), 3, 6]} />
          <meshStandardMaterial {...boneMat} />
        </mesh>
      ))}
      {Array.from({ length: bones - 1 }).map((_, i) => (
        <mesh key={"m" + i} position={[side * span * (0.42 + i * 0.06), -0.05 - i * 0.06, -0.03 - i * 0.06]}
          rotation={[Math.PI / 2, 0, side * 0.2]}>
          <circleGeometry args={[span * (0.34 - i * 0.05), 12]} />
          <meshBasicMaterial {...memMat} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}

// feathered wing — courses of tapered quills, no membrane
export function Pinion({ side = 1, span = 0.46, rows = 3, n = 7, mat, sweep = 0.30 }) {
  return (
    <group rotation={[0, 0, side * sweep]}>
      {Array.from({ length: rows }).map((_, r) =>
        Array.from({ length: n }).map((_, i) => {
          const f = i / (n - 1), L = span * (0.42 + f * 0.72) * (1 - r * 0.16);
          return (
            <mesh key={r + "_" + i}
              position={[side * (0.06 + f * span * 0.86), -r * 0.045 - f * 0.10, -0.02 - r * 0.05]}
              rotation={[0, 0, side * (0.5 - f * 1.15)]}>
              <capsuleGeometry args={[0.017 - r * 0.003, L, 3, 6]} />
              <meshStandardMaterial {...mat} />
            </mesh>
          );
        })
      )}
    </group>
  );
}

// a petal ring — roses, blooms, coronas, fans, halos of matter
export function Petals({ n = 8, r = 0.16, len = 0.20, wide = 0.10, y = 0, tilt = 0.5, mat, phase = 0 }) {
  return Array.from({ length: n }).map((_, i) => {
    const a = phase + (i / n) * Math.PI * 2;
    return (
      <mesh key={i} position={[Math.cos(a) * r, y, Math.sin(a) * r]}
        rotation={[tilt * Math.cos(a) + Math.PI / 2, -a, tilt * Math.sin(a)]}>
        <circleGeometry args={[len, 10, 0, Math.PI]} />
        <meshStandardMaterial {...mat} side={THREE.DoubleSide} />
      </mesh>
    );
  });
}

// ⚑ AND THERE IS NO SHARED `Billows` HELPER EITHER. Every gaseous element
// here (Velkor's weather, Kyrium's writhe, Carnifex's censer smoke, the
// Slijj hair, Magmantus's drip) drives its OWN mesh state every frame, and
// a shared helper would have handed them a shared material — five writes to
// one `.opacity` a frame, four thrown away. The material distinction of §4d
// still holds; it is just written per body, where the animation lives.
// the rim aura — one oversized BackSide shell, zero lights spent
export function Aura({ r = 0.62, color = "#ffffff", opacity = 0.06, y = 0.4, scale = [1, 1, 1] }) {
  return (
    <mesh position={[0, y, 0]} scale={scale}>
      <sphereGeometry args={[r, 14, 10]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
    </mesh>
  );
}

// THE CHAIN — nested groups, so a rotation at segment i carries
// every segment after it. This is what makes a trunk or a whip
// tail read as one limb instead of seven loose beads: each link
// is the CHILD of the one before it, and the refs array lets a
// useFrame drive the whole curl from the root outward.
export function Chain({ refs, n, step, render }) {
  const build = (i) => {
    if (i >= n) return null;
    return (
      <group ref={(el) => { if (refs) refs.current[i] = el; }}
        position={i === 0 ? [0, 0, 0] : step}>
        {render(i)}
        {build(i + 1)}
      </group>
    );
  };
  return build(0);
}

// a pair of painted eyes on a skull, angled outward on its curve
export function Eyes({ map, x = 0.072, y = 0.02, z = 0.10, r = 0.033, yaw = 0.44, socket = "#08080c" }) {
  return [-1, 1].map((sd) => (
    <group key={sd} position={[sd * x, y, z]} rotation={[0, sd * yaw, 0]}>
      <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[r * 1.22, 8, 8]} /><meshStandardMaterial color={socket} roughness={1} /></mesh>
      <mesh position={[0, 0, r * 0.72]}><circleGeometry args={[r, 18]} /><meshBasicMaterial map={map} toneMapped={false} /></mesh>
    </group>
  ));
}

// ════════════════════════════════════════════════════════════
//  ① SPECTRAL XOLIBRIX · Phantom · Beast · Lyf 5 / Lyt 3
// ════════════════════════════════════════════════════════════
//  "Colibri" — the hummingbird, and the Phantom school's healer.
//  The signature is not the bird, it is the STILLNESS AT SPEED:
//  the wings are gone (a blurred disc where a wing should be),
//  the body hangs perfectly motionless in the middle of it, and
//  the long bill is a drawn needle. Iridescent gorget throat,
//  because a hummingbird's colour is an angle, not a pigment.
const XOL = { back: "#0d3a30", belly: "#7fe8c8", gorget: "#ff2e7a", bill: "#161a1c", crown: "#2ee8a8" };
function xolCoat() {
  return hideTex("xol_coat_v1", "#2ee8a8", "#12604e", "#07231e", {
    fur: "rgba(200,255,238,0.5)", speck: "rgba(255,255,255,0.5)",
    after: (g, w, h, R) => {
      for (let i = 0; i < 140; i++) leaf(g, R() * w, R() * h, 18 + R() * 26, 4 + R() * 4, (R() - .5) * 1.4, "#0a4a3c", 0.35);
    },
  });
}
export function XolibrixBody({ pal }) {
  pal = pal || getSpeciesPalette("xolibrix");
  const coat = xolCoat();
  const gorget = hideTex("xol_gorget_v1", "#ff5a9e", "#c81a5a", "#5a0824", { speck: "rgba(255,220,240,0.7)" });
  const eye = eyeDisc("xol_eye_v1", "#ffffff", "#1a1210", "#000000", "rgba(0,0,0,0.95)", false);
  const rig = useBeingRig();
  const blurL = useRef(); const blurR = useRef(); const hover = useRef(); const tail = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the wings are not animated as wings — they are DISCS that
    // spin far too fast to read, which is what a hummingbird is.
    if (blurL.current) blurL.current.rotation.x = t * 34;
    if (blurR.current) blurR.current.rotation.x = -t * 34;
    // and the body is the opposite of that: it barely moves at all
    if (hover.current) hover.current.position.y = Math.sin(t * 5.6) * 0.006;
    if (tail.current) tail.current.rotation.x = Math.sin(t * 2.2) * 0.10;
  });
  const skin = { map: coat, roughness: 0.62, metalness: 0.25, emissive: XOL.crown, emissiveIntensity: 0.10 };
  const throat = { map: gorget, roughness: 0.28, metalness: 0.6, emissive: XOL.gorget, emissiveIntensity: 0.55, toneMapped: false };
  return (
    <group>
      <group ref={hover} position={[0, 0.44, 0]}>
        <group ref={rig.torso}>
          <mesh scale={[0.86, 0.90, 1.30]} castShadow><sphereGeometry args={[0.115, 14, 12]} /><meshStandardMaterial {...skin} /></mesh>
          <mesh position={[0, -0.020, 0.052]} scale={[0.80, 0.72, 0.96]}>
            <sphereGeometry args={[0.098, 12, 10]} /><meshStandardMaterial {...throat} />
          </mesh>
        </group>
        <group ref={rig.head} position={[0, 0.088, 0.088]}>
          <mesh scale={[0.96, 0.94, 1.02]}><sphereGeometry args={[0.070, 12, 10]} /><meshStandardMaterial {...skin} /></mesh>
          {/* THE BILL — a needle, and it is the whole silhouette */}
          <mesh position={[0, -0.010, 0.150]} rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.011, 0.185, 8]} /><meshStandardMaterial color={XOL.bill} roughness={0.4} metalness={0.5} />
          </mesh>
          <mesh position={[0, 0.048, -0.006]} scale={[1.05, 0.5, 1.05]}>
            <sphereGeometry args={[0.062, 12, 8]} />
            <meshStandardMaterial color={XOL.crown} roughness={0.3} metalness={0.7} emissive={XOL.crown} emissiveIntensity={0.5} />
          </mesh>
          {Eyes({ map: eye, x: 0.048, y: 0.012, z: 0.044, r: 0.021, yaw: 0.72 })}
        </group>
        {/* THE BLUR — a disc, not a wing. Held stiff, spun stupid fast. */}
        {[[-1, blurL], [1, blurR]].map(([sd, r]) => (
          <group key={sd} ref={r} position={[sd * 0.105, 0.020, -0.010]} rotation={[0, 0, sd * 0.30]}>
            <mesh rotation={[0, Math.PI / 2, 0]}>
              <circleGeometry args={[0.185, 22]} />
              <meshBasicMaterial color={pal.glow} transparent opacity={0.13} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
            </mesh>
            <mesh rotation={[0, Math.PI / 2, 0]} scale={0.66}>
              <circleGeometry args={[0.185, 18]} />
              <meshBasicMaterial color="#ffffff" transparent opacity={0.09} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
            </mesh>
            {[0, 1].map((i) => (
              <mesh key={i} rotation={[i * 2.1, Math.PI / 2, 0]} position={[0, 0, 0]}>
                <capsuleGeometry args={[0.012, 0.30, 3, 6]} /><meshStandardMaterial {...skin} />
              </mesh>
            ))}
          </group>
        ))}
        <group ref={tail} position={[0, -0.010, -0.135]}>
          {[-2, -1, 0, 1, 2].map((i) => (
            <mesh key={i} position={[i * 0.016, 0, -0.045]} rotation={[0.14, i * 0.14, i * 0.06]}>
              <capsuleGeometry args={[0.010, 0.135, 3, 6]} /><meshStandardMaterial {...skin} />
            </mesh>
          ))}
        </group>
        {/* the two needle feet, tucked — it does not land */}
        {[-1, 1].map((sd) => (
          <group key={"f" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.035, -0.088, 0.012]}>
            <mesh rotation={[0.5, 0, 0]}><capsuleGeometry args={[0.008, 0.042, 3, 6]} /><meshStandardMaterial color={XOL.bill} roughness={0.5} /></mesh>
          </group>
        ))}
      </group>
      {Aura({ r: 0.40, color: pal.glow, opacity: 0.075, y: 0.44 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ② SHADOW URSAEVA · Phantom · Beast · Lyf 7 / Lyt 1
// ════════════════════════════════════════════════════════════
//  The bear, and the hardest thing on the grid to move. It is
//  built as WEIGHT: the shoulder hump higher than the skull (the
//  one proportion that makes a bear a bear), forelimbs thicker
//  than the hindquarters, and a head carried LOW. Shadow-school,
//  so the coat is a night that has fur in it, and the amber lamps
//  are the only warm thing about the animal.
const URS = { coat: "#1a1730", hump: "#2e2850", claw: "#e8dcc8", muzzle: "#3a3454", scar: "#b8a8ff" };
function ursCoat() {
  return hideTex("urs_coat_v1", "#3a3460", "#1a1730", "#08060f", {
    fur: "rgba(184,168,255,0.30)", grain: 1400,
    after: (g, w, h, R) => {
      for (let i = 0; i < 420; i++) leaf(g, R() * w, R() * h, 40 + R() * 90, 5 + R() * 6, (R() - .5) * 0.7, "#05040a", 0.30);
      for (let i = 0; i < 60; i++) leaf(g, R() * w, R() * h, 60 + R() * 70, 3 + R() * 3, (R() - .5) * 0.5, "rgba(184,168,255,0.5)", 0.22);
    },
  });
}
export function UrsaevaBody({ pal }) {
  pal = pal || getSpeciesPalette("ursaeva");
  const coat = ursCoat();
  const eye = eyeDisc("urs_eye_v1", "#ffe8a8", "#ffb02e", "#3a1c04", "rgba(8,4,2,0.95)", false);
  const rig = useBeingRig();
  const legs = [rig.armL, rig.armR, rig.legL, rig.legR];
  const head = useRef(); const breath = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // slow. Everything about this animal is slow.
    if (breath.current) breath.current.scale.setScalar(1 + Math.sin(t * 1.05) * 0.014);
    if (head.current) head.current.rotation.y = Math.sin(t * 0.62) * 0.13;
  });
  const fur = { map: coat, roughness: 1, emissive: URS.scar, emissiveIntensity: 0.04 };
  return (
    <group>
      {/* THE HUMP — above the shoulder, higher than the skull */}
      <mesh ref={breath} position={[0, 0.62, 0.14]} scale={[1.06, 0.92, 1.02]} castShadow>
        <sphereGeometry args={[0.255, 16, 14]} /><meshStandardMaterial {...fur} />
      </mesh>
      <group ref={rig.torso}>
        <mesh position={[0, 0.50, -0.06]} scale={[1.02, 0.94, 1.42]} castShadow>
          <sphereGeometry args={[0.245, 16, 14]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh position={[0, 0.46, -0.34]} scale={[0.94, 0.90, 0.98]}>
          <sphereGeometry args={[0.215, 14, 12]} /><meshStandardMaterial {...fur} />
        </mesh>
      </group>
      {/* the head hangs LOW and forward, off the front of the hump */}
      <group ref={rig.head} position={[0, 0.50, 0.40]}>
        <group ref={head}>
          <mesh scale={[1.0, 0.94, 1.06]}><sphereGeometry args={[0.170, 14, 12]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, -0.052, 0.140]} scale={[0.80, 0.70, 1.30]}>
            <sphereGeometry args={[0.110, 12, 10]} /><meshStandardMaterial color={URS.muzzle} roughness={0.95} map={coat} />
          </mesh>
          <mesh position={[0, -0.046, 0.238]}><sphereGeometry args={[0.036, 10, 8]} /><meshStandardMaterial color="#0d0a14" roughness={0.5} /></mesh>
          {[-1, 1].map((sd) => (
            <mesh key={"ear" + sd} position={[sd * 0.126, 0.116, -0.028]} scale={[1, 1, 0.45]}>
              <sphereGeometry args={[0.062, 10, 8]} /><meshStandardMaterial {...fur} />
            </mesh>
          ))}
          {Eyes({ map: eye, x: 0.082, y: 0.026, z: 0.126, r: 0.028, yaw: 0.40, socket: "#0b0918" })}
        </group>
      </group>
      {/* four columns. The front pair are thicker — bears are front-heavy. */}
      {[[-0.175, 0.285, 1.14], [0.175, 0.285, 1.14], [-0.165, -0.245, 0.98], [0.165, -0.245, 0.98]].map(([ox, oz, k], i) => (
        <group key={"lg" + i} ref={legs[i]} position={[ox, 0, oz]}>
          <mesh position={[0, 0.31, 0]} scale={[k, 1, k]}><capsuleGeometry args={[0.078, 0.22, 4, 10]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.11, 0.020]} scale={[k, 1, k]}><capsuleGeometry args={[0.070, 0.14, 4, 10]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.042, 0.052]} scale={[1.2 * k, 0.66, 1.5]}><sphereGeometry args={[0.070, 10, 8]} /><meshStandardMaterial {...fur} /></mesh>
          {[-1, 0, 1].map((c) => (
            <mesh key={c} position={[c * 0.030, 0.030, 0.112]} rotation={[1.15, 0, 0]}>
              <coneGeometry args={[0.013, 0.055, 6]} />
              <meshStandardMaterial color={URS.claw} roughness={0.35} metalness={0.3} />
            </mesh>
          ))}
        </group>
      ))}
      {Aura({ r: 0.80, color: pal.glow, opacity: 0.055, y: 0.50, scale: [1, 0.85, 1.2] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ③ SPECTRAL BUNNOVIAN · Phantom · Beast · Lyf 6 / Lyt 2
// ════════════════════════════════════════════════════════════
//  The Phantom school's other healer, and the softest silhouette
//  in the Empire — which is exactly why it needs ONE strange
//  detail to stop it reading as a toy. It has two: the ears are
//  TRANSLUCENT and lit from inside (you can see the light through
//  them, the way you can through a real rabbit's), and it has no
//  feet — the hindquarters dissolve into a pooled spectral drift.
const BUN = { coat: "#f2ecff", shade: "#c8bcdc", inner: "#ff9ec4", nose: "#ff7aa8" };
function bunCoat() {
  return hideTex("bun_coat_v1", "#ffffff", "#ece4fa", "#b8aed0", {
    fur: "rgba(255,255,255,0.75)", grain: 900,
    after: (g, w, h, R) => {
      for (let i = 0; i < 500; i++) leaf(g, R() * w, R() * h, 22 + R() * 40, 3 + R() * 3, (R() - .5) * 2.6, "rgba(200,188,220,0.55)", 0.22);
    },
  });
}
export function BunnovianBody({ pal }) {
  pal = pal || getSpeciesPalette("bunnovian");
  const coat = bunCoat();
  const eye = eyeDisc("bun_eye_v1", "#ffd8ec", "#c8348a", "#3a0820", "rgba(20,4,12,0.9)", false);
  const rig = useBeingRig();
  const earL = useRef(); const earR = useRef(); const nose = useRef(); const drift = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (earL.current) { earL.current.rotation.z = 0.16 + Math.sin(t * 1.7) * 0.11; earL.current.rotation.x = Math.sin(t * 2.3) * 0.07; }
    if (earR.current) { earR.current.rotation.z = -0.16 - Math.sin(t * 1.7 + 1.1) * 0.11; earR.current.rotation.x = Math.sin(t * 2.3 + 0.6) * 0.07; }
    // the nose never stops. A rabbit's nose is its idle.
    if (nose.current) nose.current.scale.set(1, 1 + Math.sin(t * 9.5) * 0.10, 1);
    drift.current.forEach((m, i) => {
      if (!m) return;
      const a = t * 0.5 + i * 1.4;
      m.position.set(Math.cos(a) * (0.10 + i * 0.02), 0.10 + Math.sin(t * 1.1 + i) * 0.03, Math.sin(a) * (0.10 + i * 0.02) - 0.10);
      m.scale.setScalar(0.9 + Math.sin(t * 1.6 + i * 2) * 0.16);
    });
  });
  const fur = { map: coat, roughness: 1, emissive: "#ffffff", emissiveIntensity: 0.07 };
  const ear = { color: BUN.inner, emissive: BUN.inner, emissiveIntensity: 0.75, transparent: true, opacity: 0.66, roughness: 0.9, toneMapped: false };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.36, -0.02]} scale={[0.94, 0.98, 1.16]} castShadow>
          <sphereGeometry args={[0.190, 14, 12]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh position={[0, 0.30, -0.20]} scale={[1.06, 0.92, 1.0]}>
          <sphereGeometry args={[0.170, 14, 12]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh position={[0, 0.28, -0.34]}><sphereGeometry args={[0.078, 10, 8]} /><meshStandardMaterial {...fur} /></mesh>
      </group>
      <group ref={rig.head} position={[0, 0.56, 0.10]}>
        <mesh scale={[0.98, 0.94, 1.04]} castShadow><sphereGeometry args={[0.145, 14, 12]} /><meshStandardMaterial {...fur} /></mesh>
        <mesh position={[0, -0.040, 0.116]} scale={[0.80, 0.66, 0.92]}>
          <sphereGeometry args={[0.098, 12, 10]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh ref={nose} position={[0, -0.034, 0.190]}>
          <sphereGeometry args={[0.020, 8, 8]} /><meshStandardMaterial color={BUN.nose} roughness={0.6} emissive={BUN.nose} emissiveIntensity={0.3} />
        </mesh>
        {/* the ears — lit from inside, which is the whole trick */}
        <group ref={earL} position={[-0.062, 0.126, -0.010]}>
          <mesh position={[0, 0.150, 0]} scale={[0.55, 1, 0.34]}><capsuleGeometry args={[0.052, 0.230, 4, 10]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.150, 0.014]} scale={[0.40, 0.94, 0.30]}><capsuleGeometry args={[0.050, 0.220, 4, 10]} /><meshStandardMaterial {...ear} /></mesh>
        </group>
        <group ref={earR} position={[0.062, 0.126, -0.010]}>
          <mesh position={[0, 0.150, 0]} scale={[0.55, 1, 0.34]}><capsuleGeometry args={[0.052, 0.230, 4, 10]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.150, 0.014]} scale={[0.40, 0.94, 0.30]}><capsuleGeometry args={[0.050, 0.220, 4, 10]} /><meshStandardMaterial {...ear} /></mesh>
        </group>
        {Eyes({ map: eye, x: 0.086, y: 0.014, z: 0.088, r: 0.036, yaw: 0.62, socket: "#2a2038" })}
      </group>
      {/* forepaws only. The back half is drift. */}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.098, 0.22, 0.16]}>
          <mesh rotation={[0.3, 0, 0]}><capsuleGeometry args={[0.036, 0.10, 4, 8]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, -0.075, 0.028]} scale={[1, 0.7, 1.3]}><sphereGeometry args={[0.040, 10, 8]} /><meshStandardMaterial {...fur} /></mesh>
        </group>
      ))}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={"d" + i} ref={(el) => (drift.current[i] = el)}>
          <sphereGeometry args={[0.085 - i * 0.008, 10, 8]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.20} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.52, color: pal.glow, opacity: 0.09, y: 0.38 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ④ SKURYZ · Phantom · Beast · Lyf 3 / Lyt 5
// ════════════════════════════════════════════════════════════
//  Rats of Shadow Gambling: "they do not steal what you are
//  holding — they steal the odds you were counting on." So the
//  signature is the WAGER, worn: a set of dice-pips burned into
//  the flank that reroll on their own, a coin held permanently in
//  one paw, and a naked whip tail that is longer than the animal.
const SKZ = { coat: "#6a5f4a", belly: "#b8ac90", tail: "#c8a89a", pip: "#ffd24a", ear: "#8a6a68" };
function skzCoat() {
  return hideTex("skz_coat_v1", "#8a7f6a", "#4a4234", "#1a1610", {
    fur: "rgba(0,0,0,0.45)", grain: 1500,
    after: (g, w, h, R) => {
      for (let i = 0; i < 700; i++) leaf(g, R() * w, R() * h, 16 + R() * 30, 2 + R() * 2, (R() - .5) * 2.8, "rgba(30,24,16,0.7)", 0.3);
      // the pips, burned in
      for (let d = 0; d < 6; d++) {
        const cx = 60 + (d % 3) * 170, cy = 120 + Math.floor(d / 3) * 230;
        for (let p = 0; p < 1 + d; p++) {
          g.fillStyle = "rgba(255,210,74,0.55)";
          g.beginPath(); g.arc(cx + (p % 3) * 26, cy + Math.floor(p / 3) * 26, 7, 0, 7); g.fill();
        }
      }
    },
  });
}
export function SkuryzBody({ pal }) {
  pal = pal || getSpeciesPalette("skuryz");
  const coat = skzCoat();
  const eye = eyeDisc("skz_eye_v1", "#ffd8a8", "#c4453c", "#2a0806", "rgba(0,0,0,0.9)", false);
  const rig = useBeingRig();
  const legs = [rig.armL, rig.armR, rig.legL, rig.legR];
  const tail = useRef([]); const coin = useRef(); const whisk = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    tail.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.y = Math.sin(t * 2.4 - i * 0.55) * 0.34;
      m.rotation.x = Math.sin(t * 1.7 - i * 0.4) * 0.12;
    });
    // the coin is always turning. It has never been put down.
    if (coin.current) { coin.current.rotation.y = t * 5.2; coin.current.position.y = 0.30 + Math.abs(Math.sin(t * 2.6)) * 0.03; }
    if (whisk.current) whisk.current.rotation.z = Math.sin(t * 6.5) * 0.06;
  });
  const fur = { map: coat, roughness: 1 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.30, -0.10]} scale={[0.86, 0.84, 1.46]} castShadow>
          <sphereGeometry args={[0.155, 14, 12]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh position={[0, 0.28, 0.11]} scale={[0.80, 0.78, 0.96]}>
          <sphereGeometry args={[0.135, 12, 10]} /><meshStandardMaterial {...fur} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 0.36, 0.27]}>
        <mesh scale={[0.90, 0.88, 1.10]}><sphereGeometry args={[0.108, 12, 10]} /><meshStandardMaterial {...fur} /></mesh>
        <mesh position={[0, -0.034, 0.108]} scale={[0.56, 0.50, 1.70]}>
          <sphereGeometry args={[0.070, 10, 8]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh position={[0, -0.030, 0.196]}><sphereGeometry args={[0.018, 8, 8]} /><meshStandardMaterial color="#e88a9a" roughness={0.5} /></mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"ear" + sd} position={[sd * 0.088, 0.084, -0.020]} rotation={[0, sd * 0.5, sd * 0.2]} scale={[1, 1, 0.30]}>
            <sphereGeometry args={[0.062, 12, 10]} /><meshStandardMaterial color={SKZ.ear} roughness={0.9} map={coat} />
          </mesh>
        ))}
        <group ref={whisk} position={[0, -0.036, 0.150]}>
          {[-1, 1].map((sd) => [0, 1, 2].map((i) => (
            <mesh key={sd + "_" + i} position={[sd * 0.03, 0.006 - i * 0.010, 0.02]} rotation={[0, sd * (0.7 + i * 0.14), i * 0.12 - 0.1]}>
              <boxGeometry args={[0.16, 0.0016, 0.0016]} /><meshBasicMaterial color="#e8e0d0" toneMapped={false} />
            </mesh>
          )))}
        </group>
        {Eyes({ map: eye, x: 0.062, y: 0.020, z: 0.070, r: 0.024, yaw: 0.66, socket: "#100c08" })}
      </group>
      {[[-0.095, 0.150], [0.095, 0.150], [-0.100, -0.190], [0.100, -0.190]].map(([ox, oz], i) => (
        <group key={"lg" + i} ref={legs[i]} position={[ox, 0, oz]}>
          <mesh position={[0, 0.185, 0]}><capsuleGeometry args={[0.030, 0.13, 4, 8]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.075, 0.016]}><capsuleGeometry args={[0.024, 0.10, 4, 8]} /><meshStandardMaterial color={SKZ.tail} roughness={0.8} /></mesh>
          <mesh position={[0, 0.026, 0.040]} scale={[1, 0.6, 1.5]}><sphereGeometry args={[0.028, 8, 8]} /><meshStandardMaterial color={SKZ.tail} roughness={0.8} /></mesh>
        </group>
      ))}
      {/* THE COIN — held, spinning, never once put down */}
      <group ref={coin} position={[0.13, 0.30, 0.16]}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.045, 0.045, 0.008, 18]} />
          <meshStandardMaterial color={SKZ.pip} metalness={0.95} roughness={0.22} emissive={SKZ.pip} emissiveIntensity={0.35} />
        </mesh>
      </group>
      {/* THE TAIL — naked, segmented, longer than the animal */}
      <group position={[0, 0.28, -0.30]}>
        <Chain refs={tail} n={7} step={[0, 0, -0.115]} render={(i) => (
          <mesh position={[0, 0, -0.058]} rotation={[Math.PI / 2, 0, 0]}>
            <capsuleGeometry args={[0.026 - i * 0.0032, 0.10, 3, 8]} />
            <meshStandardMaterial color={SKZ.tail} roughness={0.72} />
          </mesh>
        )} />
      </group>
      {Aura({ r: 0.50, color: pal.accent, opacity: 0.05, y: 0.30 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑤ TRUNARAN · Phantom · Beast · Lyf 7 / Lyt 1
// ════════════════════════════════════════════════════════════
//  The elephant, and the second tank. Ursaeva is weight that
//  crouches; Trunaran is weight that STANDS — four pillars, a
//  head carried high, and the trunk as a live, curling limb that
//  is doing something in every single frame. The ears are the
//  silhouette: enormous, thin enough to backlight, and veined.
const TRN = { hide: "#6a6f7a", deep: "#2a2e36", tusk: "#f0e8d0", vein: "#8a5a5a", pad: "#3a3e46" };
function trnHide() {
  return hideTex("trn_hide_v1", "#8a909c", "#5a606c", "#20242c", {
    grain: 1800,
    after: (g, w, h, R) => {
      // elephant hide is CRACKS, not fur
      g.lineWidth = 1.6;
      for (let i = 0; i < 260; i++) {
        const x = R() * w, y = R() * h;
        g.strokeStyle = `rgba(20,22,28,${0.25 + R() * 0.35})`;
        g.beginPath(); g.moveTo(x, y);
        g.lineTo(x + (R() - .5) * 60, y + (R() - .5) * 60);
        g.lineTo(x + (R() - .5) * 100, y + (R() - .5) * 100);
        g.stroke();
      }
      for (let i = 0; i < 200; i++) {
        g.fillStyle = `rgba(255,255,255,${0.02 + R() * 0.05})`;
        g.beginPath(); g.arc(R() * w, R() * h, 3 + R() * 8, 0, 7); g.fill();
      }
    },
  });
}
export function TrunaranBody({ pal }) {
  pal = pal || getSpeciesPalette("trunaran");
  const hide = trnHide();
  const eye = eyeDisc("trn_eye_v1", "#ffe8c8", "#c8903a", "#3a2408", "rgba(10,6,2,0.92)", false);
  const rig = useBeingRig();
  const legs = [rig.armL, rig.armR, rig.legL, rig.legR];
  const trunk = useRef([]); const earL = useRef(); const earR = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    trunk.current.forEach((m, i) => {
      if (!m) return;
      const f = i / 6;
      m.rotation.x = 0.30 + Math.sin(t * 1.25 + i * 0.5) * (0.10 + f * 0.24);
      m.rotation.z = Math.sin(t * 0.9 + i * 0.4) * (0.05 + f * 0.16);
    });
    if (earL.current) earL.current.rotation.y = -0.55 + Math.sin(t * 1.1) * 0.16;
    if (earR.current) earR.current.rotation.y = 0.55 - Math.sin(t * 1.1 + 0.8) * 0.16;
  });
  const skin = { map: hide, roughness: 1, metalness: 0.05 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.74, -0.06]} scale={[1.0, 1.02, 1.34]} castShadow>
          <sphereGeometry args={[0.310, 16, 14]} /><meshStandardMaterial {...skin} />
        </mesh>
        <mesh position={[0, 0.72, -0.36]} scale={[0.92, 0.94, 0.92]}>
          <sphereGeometry args={[0.270, 14, 12]} /><meshStandardMaterial {...skin} />
        </mesh>
        <mesh position={[0, 0.62, -0.56]} rotation={[0.5, 0, 0]}>
          <capsuleGeometry args={[0.032, 0.16, 4, 8]} /><meshStandardMaterial {...skin} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 0.86, 0.32]}>
        <mesh scale={[1.02, 1.06, 0.96]} castShadow><sphereGeometry args={[0.215, 16, 14]} /><meshStandardMaterial {...skin} /></mesh>
        <mesh position={[0, 0.130, -0.02]} scale={[1.0, 0.62, 0.92]}>
          <sphereGeometry args={[0.180, 14, 12]} /><meshStandardMaterial {...skin} />
        </mesh>
        {/* THE EARS — huge, thin, veined, backlit */}
        {[[-1, earL], [1, earR]].map(([sd, r]) => (
          <group key={sd} ref={r} position={[sd * 0.175, 0.030, -0.060]}>
            <mesh rotation={[0, sd * -0.55, sd * 0.22]} scale={[0.10, 1, 1]}>
              <sphereGeometry args={[0.245, 14, 12]} />
              <meshStandardMaterial map={hide} color="#9aa0ac" roughness={0.95} side={THREE.DoubleSide}
                transparent opacity={0.94} emissive={TRN.vein} emissiveIntensity={0.10} />
            </mesh>
          </group>
        ))}
        {/* the tusks */}
        {[-1, 1].map((sd) => (
          <mesh key={"tk" + sd} position={[sd * 0.088, -0.115, 0.150]} rotation={[-0.85, sd * 0.16, 0]}>
            <coneGeometry args={[0.026, 0.240, 8]} />
            <meshStandardMaterial color={TRN.tusk} roughness={0.32} metalness={0.15} />
          </mesh>
        ))}
        {Eyes({ map: eye, x: 0.150, y: 0.020, z: 0.130, r: 0.026, yaw: 0.62, socket: "#1a1c22" })}
        {/* THE TRUNK — a live limb, and the one thing always moving */}
        <group position={[0, -0.140, 0.170]}>
          <Chain refs={trunk} n={7} step={[0, -0.078, 0.012]} render={(i) => (
            <mesh position={[0, -0.042, 0]}>
              <capsuleGeometry args={[0.062 - i * 0.0062, 0.062, 4, 10]} />
              <meshStandardMaterial {...skin} />
            </mesh>
          )} />
        </group>
      </group>
      {/* four pillars, and they are genuinely columns */}
      {[[-0.205, 0.230], [0.205, 0.230], [-0.215, -0.245], [0.215, -0.245]].map(([ox, oz], i) => (
        <group key={"lg" + i} ref={legs[i]} position={[ox, 0, oz]}>
          <mesh position={[0, 0.42, 0]}><capsuleGeometry args={[0.090, 0.34, 4, 12]} /><meshStandardMaterial {...skin} /></mesh>
          <mesh position={[0, 0.10, 0.008]} scale={[1.15, 0.55, 1.15]}>
            <sphereGeometry args={[0.098, 12, 10]} /><meshStandardMaterial color={TRN.pad} roughness={1} map={hide} />
          </mesh>
          {[-1, 0, 1].map((c) => (
            <mesh key={c} position={[c * 0.042, 0.075, 0.078]}>
              <sphereGeometry args={[0.020, 8, 8]} /><meshStandardMaterial color={TRN.tusk} roughness={0.5} />
            </mesh>
          ))}
        </group>
      ))}
      {Aura({ r: 0.95, color: pal.glow, opacity: 0.045, y: 0.70, scale: [1, 0.95, 1.15] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑥ KYRIUM · Phantom · Beast · Lyf 5 / Lyt 3
// ════════════════════════════════════════════════════════════
//  "Bodies composed of a shimmering dark energy that shifts and
//   writhes like living shadows. Their forms are AMORPHOUS,
//   making it impossible to discern distinct features or limbs."
//  Read that literally and the model writes itself: there is no
//  anatomy. There is a tall column of shadow that is genuinely
//  eating the light around it, six Archon-lights adrift inside
//  it where a face would be, and a single grimoire it never puts
//  down — the Great Library of Shadows, carried.
const KYR = { core: "#150e28", edge: "#3a2460", lamp: "#a86aff", tome: "#2a1a40", gild: "#c8a8ff" };
function kyrShade() {
  return voidTex("kyr_void_v1", "#1a1030", "#c8a8ff", "#3a2060");
}
export function KyriumBody({ pal }) {
  pal = pal || getSpeciesPalette("kyrium");
  const shade = kyrShade();
  const rig = useBeingRig();
  const veils = useRef([]); const lamps = useRef([]); const tome = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    veils.current.forEach((m, i) => {
      if (!m) return;
      const a = t * (0.32 + i * 0.07) + i * 1.9;
      m.position.set(Math.cos(a) * (0.16 + i * 0.012), 0.34 + i * 0.155 + Math.sin(t * 0.9 + i) * 0.02, Math.sin(a) * (0.16 + i * 0.012));
      m.scale.setScalar(1 + Math.sin(t * 1.15 + i * 1.3) * 0.14);
      m.rotation.y = a * 0.6;
    });
    lamps.current.forEach((m, i) => {
      if (!m) return;
      const a = t * 0.85 + (i / 6) * Math.PI * 2;
      m.position.set(Math.cos(a) * 0.105, 1.16 + Math.sin(t * 1.4 + i) * 0.045, 0.06 + Math.sin(a) * 0.045);
      if (m.material) m.material.opacity = 0.55 + Math.sin(t * 2.1 + i * 1.7) * 0.32;
    });
    if (tome.current) { tome.current.rotation.y = Math.sin(t * 0.7) * 0.22; tome.current.position.y = 0.66 + Math.sin(t * 1.1) * 0.02; }
  });
  const dark = { map: shade, color: KYR.core, roughness: 1, metalness: 0.0, emissive: KYR.lamp, emissiveIntensity: 0.06 };
  return (
    <group>
      {/* the column. Wider at the floor, narrowing to a shoulder-
          less crest — the shape of a thing that pooled upward. */}
      <group ref={rig.torso}>
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const f = i / 5;
          return (
            <mesh key={i} position={[0, 0.20 + f * 0.86, 0]} scale={[1 - f * 0.34, 1, 1 - f * 0.34]} castShadow={i < 2}>
              <sphereGeometry args={[0.26 - f * 0.08, 14, 12]} /><meshStandardMaterial {...dark} />
            </mesh>
          );
        })}
      </group>
      {/* THE WRITHE — six shadow veils circling the column. This is
          the "impossible to discern limbs" clause, made geometry. */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={"v" + i} ref={(el) => (veils.current[i] = el)}>
          <sphereGeometry args={[0.19 - i * 0.012, 12, 10]} />
          <meshBasicMaterial color={KYR.edge} transparent opacity={0.26} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {/* THE ARCHON LIGHTS — six, where a face is not */}
      <group ref={rig.head} position={[0, 0, 0]}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <mesh key={"l" + i} ref={(el) => (lamps.current[i] = el)}>
            <sphereGeometry args={[0.026, 10, 8]} />
            <meshBasicMaterial color={KYR.lamp} transparent opacity={0.7} toneMapped={false} />
          </mesh>
        ))}
        <mesh position={[0, 1.16, 0.02]}>
          <sphereGeometry args={[0.15, 12, 10]} />
          <meshBasicMaterial color={KYR.lamp} transparent opacity={0.10} toneMapped={false} depthWrite={false} />
        </mesh>
      </group>
      {/* THE GRIMOIRE — the Library, carried. It is never closed. */}
      <group ref={rig.prop}>
        <group ref={tome} position={[0.26, 0.66, 0.18]} rotation={[0.35, -0.4, 0.12]}>
          <mesh><boxGeometry args={[0.20, 0.035, 0.26]} /><meshStandardMaterial color={KYR.tome} roughness={0.7} metalness={0.2} /></mesh>
          <mesh position={[0, 0.024, 0]}><boxGeometry args={[0.186, 0.014, 0.246]} /><meshBasicMaterial color="#e8dcc0" toneMapped={false} /></mesh>
          <mesh position={[0, 0.033, 0]}>
            <boxGeometry args={[0.13, 0.002, 0.18]} />
            <meshBasicMaterial color={KYR.gild} transparent opacity={0.55} toneMapped={false} />
          </mesh>
          <mesh position={[0, -0.020, 0]}><boxGeometry args={[0.216, 0.010, 0.276]} /><meshStandardMaterial color={KYR.gild} metalness={0.9} roughness={0.3} /></mesh>
        </group>
      </group>
      {Aura({ r: 0.72, color: KYR.lamp, opacity: 0.07, y: 0.58, scale: [0.9, 1.3, 0.9] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑦ SLIJJ · Phantom · Beast · Lyf 6 / Lyt 2
// ════════════════════════════════════════════════════════════
//  Read straight off its note, which is unusually specific:
//   · skin "a deep, dark brown, like rich soil or decaying leaves"
//   · eyes "a faint, candlelit glow, as if the light of the dead
//     lingers within them"
//   · "tendrils of long, black hair woven from the very threads
//     of darkness and shadow itself"
//  ⚑ And per §4b: hair DESCENDS. These tendrils hang, they do not
//  ring — the note says hair and means hair, so they are built as
//  a curtain from the crown, not a collar.
const SLJ = { soil: "#3a2a1a", leaf: "#5a3a20", hair: "#0a0810", wick: "#ffb04a", bone: "#8a7a5a" };
function sljSoil() {
  return hideTex("slj_soil_v1", "#6a4a2e", "#3a2818", "#150e08", {
    grain: 1600,
    after: (g, w, h, R) => {
      // decaying leaves, pressed into soil
      for (let i = 0; i < 220; i++)
        leaf(g, R() * w, R() * h, 40 + R() * 80, 14 + R() * 16, R() * 6.28, `rgba(${60 + R() * 60 | 0},${34 + R() * 30 | 0},14,0.35)`, 0.5);
      for (let i = 0; i < 400; i++) {
        g.fillStyle = `rgba(10,7,4,${0.1 + R() * 0.3})`;
        g.beginPath(); g.arc(R() * w, R() * h, 1 + R() * 4, 0, 7); g.fill();
      }
    },
  });
}
export function SlijjBody({ pal }) {
  pal = pal || getSpeciesPalette("slijj");
  const soil = sljSoil();
  const eye = eyeDisc("slj_eye_v1", "#fff0c0", "#ff9a2e", "#2a1200", null, false);
  const rig = useBeingRig();
  const hair = useRef([]); const flick = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    hair.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.z = Math.sin(t * 0.85 + i * 0.9) * 0.13;
      m.rotation.x = 0.10 + Math.sin(t * 0.62 + i * 0.6) * 0.09;
    });
    // the candle in the eyes GUTTERS. It is not a steady lamp.
    if (flick.current) flick.current.scale.setScalar(0.94 + Math.abs(Math.sin(t * 6.1) * Math.sin(t * 2.3)) * 0.16);
  });
  const skin = { map: soil, roughness: 1, metalness: 0.02 };
  const strand = { color: SLJ.hair, roughness: 0.9, metalness: 0.1, emissive: "#1a1030", emissiveIntensity: 0.25 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.62, 0]} scale={[0.92, 1.06, 0.82]} castShadow>
          <sphereGeometry args={[0.235, 14, 12]} /><meshStandardMaterial {...skin} />
        </mesh>
        <mesh position={[0, 0.34, 0]} scale={[1.10, 0.92, 1.0]}>
          <sphereGeometry args={[0.230, 14, 12]} /><meshStandardMaterial {...skin} />
        </mesh>
        {/* it does not have feet. It has a base, like something planted. */}
        <mesh position={[0, 0.10, 0]} scale={[1.20, 0.52, 1.20]}>
          <sphereGeometry args={[0.225, 14, 10]} /><meshStandardMaterial {...skin} />
        </mesh>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.215, 0.70, 0.02]}>
          <mesh rotation={[0.2, 0, sd * 0.30]}><capsuleGeometry args={[0.052, 0.24, 4, 10]} /><meshStandardMaterial {...skin} /></mesh>
          <mesh position={[sd * 0.075, -0.22, 0.03]}><sphereGeometry args={[0.056, 10, 8]} /><meshStandardMaterial {...skin} /></mesh>
          {[-1, 0, 1].map((c) => (
            <mesh key={c} position={[sd * 0.085 + c * 0.022, -0.27, 0.045]} rotation={[0.5, 0, 0]}>
              <capsuleGeometry args={[0.011, 0.05, 3, 6]} /><meshStandardMaterial color={SLJ.bone} roughness={0.6} />
            </mesh>
          ))}
        </group>
      ))}
      <group ref={rig.head} position={[0, 0.94, 0.02]}>
        <mesh scale={[0.94, 1.02, 0.94]} castShadow><sphereGeometry args={[0.155, 14, 12]} /><meshStandardMaterial {...skin} /></mesh>
        <mesh position={[0, -0.052, 0.118]} scale={[0.78, 0.60, 0.92]}>
          <sphereGeometry args={[0.098, 12, 10]} /><meshStandardMaterial {...skin} />
        </mesh>
        {/* THE CANDLELIT EYES — no pupil. There is nothing behind
            them but the light of the dead, which is the point. */}
        <group ref={flick}>
          {Eyes({ map: eye, x: 0.070, y: 0.026, z: 0.116, r: 0.030, yaw: 0.40, socket: "#0a0704" })}
        </group>
        {/* THE HAIR — a curtain from the crown. It HANGS. */}
        {Array.from({ length: 14 }).map((_, i) => {
          const a = (i / 14) * Math.PI * 2;
          return (
            <group key={"h" + i} ref={(el) => (hair.current[i] = el)}
              position={[Math.cos(a) * 0.128, 0.100, Math.sin(a) * 0.128 - 0.02]}
              rotation={[0, -a, 0]}>
              <mesh position={[0, -0.20, 0]} rotation={[0.10, 0, Math.cos(a) * 0.14]}>
                <capsuleGeometry args={[0.016, 0.34, 3, 6]} /><meshStandardMaterial {...strand} />
              </mesh>
              <mesh position={[0, -0.42, 0.02]} rotation={[0.24, 0, Math.cos(a) * 0.2]}>
                <capsuleGeometry args={[0.010, 0.22, 3, 6]} /><meshStandardMaterial {...strand} />
              </mesh>
            </group>
          );
        })}
      </group>
      {Aura({ r: 0.66, color: SLJ.wick, opacity: 0.055, y: 0.60, scale: [1, 1.15, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑧ VELKOR · Phantom · Beast · Lyf 2 / Lyt 6
// ════════════════════════════════════════════════════════════
//  "Mist creatures." Four words, and they rule out almost every
//  modelling choice available. There is no hide to paint, no
//  limbs to rig and nothing to cast a shadow. What a Velkor is,
//  is a WEATHER with an intent in it: fourteen billows on one
//  slow-turning axis, all `depthWrite={false}` so they melt into
//  one volume, and a pair of eyes deep inside that never blur.
//  The only solid thing on it is a ring of six claw-hooks that
//  ride the mist at hand height — the part that touches you.
const VLK = { fog: "#c8d8e8", cold: "#7fa8c8", deep: "#3a4a5a", claw: "#e8f4ff" };
export function VelkorBody({ pal }) {
  pal = pal || getSpeciesPalette("velkor");
  const eye = eyeDisc("vlk_eye_v1", "#ffffff", "#8ad8ff", "#0e2030", "rgba(6,12,20,0.85)", true);
  const rig = useBeingRig();
  const puff = useRef([]); const hooks = useRef(); const core = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    puff.current.forEach((m, i) => {
      if (!m) return;
      const f = i / 14, a = t * (0.22 + f * 0.30) + i * 1.27;
      m.position.set(
        Math.cos(a) * (0.10 + f * 0.26),
        0.16 + f * 0.86 + Math.sin(t * 0.7 + i) * 0.035,
        Math.sin(a) * (0.10 + f * 0.24)
      );
      m.scale.setScalar(1 + Math.sin(t * 0.9 + i * 1.6) * 0.22);
      if (m.material) m.material.opacity = 0.13 + 0.10 * (1 - f) + Math.sin(t * 1.3 + i) * 0.03;
    });
    if (hooks.current) hooks.current.rotation.y = t * 0.42;
    if (core.current) core.current.position.y = 0.72 + Math.sin(t * 1.05) * 0.03;
  });
  return (
    <group>
      <group ref={rig.torso}>
        {Array.from({ length: 14 }).map((_, i) => (
          <mesh key={i} ref={(el) => (puff.current[i] = el)}>
            <sphereGeometry args={[0.24 - i * 0.008, 12, 10]} />
            <meshBasicMaterial color={i % 3 ? VLK.fog : VLK.cold} transparent opacity={0.2} toneMapped={false} depthWrite={false} />
          </mesh>
        ))}
        {/* the low band, so the mist never visually breaks at the floor */}
        <mesh position={[0, 0.10, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.26, 0.11, 8, 22]} />
          <meshBasicMaterial color={VLK.fog} transparent opacity={0.13} toneMapped={false} depthWrite={false} />
        </mesh>
      </group>
      {/* THE EYES — deep in the weather, and perfectly sharp */}
      <group ref={rig.head}>
        <group ref={core} position={[0, 0.72, 0.10]}>
          {Eyes({ map: eye, x: 0.070, y: 0, z: 0.030, r: 0.034, yaw: 0.34, socket: "#0c141c" })}
          <mesh><sphereGeometry args={[0.13, 12, 10]} />
            <meshBasicMaterial color={VLK.cold} transparent opacity={0.13} toneMapped={false} depthWrite={false} /></mesh>
        </group>
      </group>
      {/* THE HOOKS — the only solid thing about a Velkor */}
      <group ref={hooks} position={[0, 0.50, 0]}>
        {Array.from({ length: 6 }).map((_, i) => {
          const a = (i / 6) * Math.PI * 2;
          return (
            <mesh key={i} position={[Math.cos(a) * 0.30, Math.sin(i * 1.7) * 0.09, Math.sin(a) * 0.30]}
              rotation={[Math.PI / 2 + Math.sin(a) * 0.4, -a, 0]}>
              <coneGeometry args={[0.020, 0.115, 6]} />
              <meshStandardMaterial color={VLK.claw} roughness={0.22} metalness={0.7} emissive={VLK.claw} emissiveIntensity={0.25} />
            </mesh>
          );
        })}
      </group>
      {Aura({ r: 0.68, color: pal.glow, opacity: 0.05, y: 0.54, scale: [1, 1.2, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑨ NOXURA · Phantom · Beast · Lyf 4 / Lyt 4
// ════════════════════════════════════════════════════════════
//  "They see past their world and see the Writer. They worship
//   the Writer alone — the creator, regardless of who stands
//   above him."  A species whose whole theology is that it can
//   SEE OUT. So the model is built around one detail: it is not
//   looking at you. Its four eyes are on the back of a raised
//   cowl and they are aimed UP AND OUT of the scene — and the
//   thing its front carries is a blank open page, held like a
//   face, because the Writer's word is the face it wants.
const NOX = { night: "#22224a", hood: "#14142c", page: "#efe6cf", ink: "#1a1428", star: "#e8e0ff" };
function noxCloth() {
  return clothTex("nox_cloth_v1", "#3a3a68", "#20204a", "#0b0b1c", "rgba(255,216,138,0.6)");
}
export function NoxuraBody({ pal }) {
  pal = pal || getSpeciesPalette("noxura");
  const cloth = noxCloth();
  const sky = voidTex("nox_void_v1", "#1a1a3a", "#e8e0ff", "#3a3a70");
  const eye = eyeDisc("nox_eye_v1", "#fff8e0", "#e8e0ff", "#2a2450", "rgba(12,10,26,0.9)", false);
  const rig = useBeingRig();
  const page = useRef(); const motes = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (page.current) { page.current.rotation.z = Math.sin(t * 0.8) * 0.06; page.current.position.y = 0.66 + Math.sin(t * 1.2) * 0.014; }
    motes.current.forEach((m, i) => {
      if (!m) return;
      const a = t * 0.55 + i * 1.05;
      m.position.set(Math.cos(a) * 0.30, 0.95 + i * 0.06 + Math.sin(t * 1.1 + i) * 0.05, Math.sin(a) * 0.30);
      if (m.material) m.material.opacity = 0.35 + Math.sin(t * 2 + i) * 0.28;
    });
  });
  const robe = { map: cloth, roughness: 0.94, metalness: 0.05, color: NOX.night };
  const inner = { map: sky, roughness: 1, emissive: NOX.star, emissiveIntensity: 0.18, toneMapped: false };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.36, 0]} scale={[1.12, 1.0, 1.12]} castShadow>
          <coneGeometry args={[0.30, 0.72, 16]} /><meshStandardMaterial {...robe} />
        </mesh>
        <mesh position={[0, 0.72, 0]} scale={[1.0, 0.9, 1.0]}>
          <sphereGeometry args={[0.185, 14, 12]} /><meshStandardMaterial {...robe} />
        </mesh>
      </group>
      {/* THE COWL — raised, and it is the BACK of the head that
          carries the eyes. It is looking out of the picture. */}
      <group ref={rig.head} position={[0, 0.92, -0.02]}>
        <mesh scale={[1.0, 1.10, 0.96]}><sphereGeometry args={[0.165, 14, 12]} /><meshStandardMaterial {...robe} /></mesh>
        <mesh position={[0, 0.02, 0.05]} scale={[0.86, 0.94, 0.86]}>
          <sphereGeometry args={[0.150, 12, 10]} /><meshStandardMaterial {...inner} />
        </mesh>
        <group rotation={[-0.55, Math.PI, 0]}>
          {Eyes({ map: eye, x: 0.062, y: 0.050, z: 0.128, r: 0.028, yaw: 0.30, socket: "#0a0818" })}
          {Eyes({ map: eye, x: 0.096, y: -0.020, z: 0.108, r: 0.020, yaw: 0.44, socket: "#0a0818" })}
        </group>
      </group>
      {/* THE PAGE — held where a face should be, and it is blank */}
      <group ref={rig.prop}>
        <group ref={page} position={[0, 0.66, 0.26]} rotation={[0.16, 0, 0]}>
          <mesh><boxGeometry args={[0.24, 0.30, 0.006]} /><meshBasicMaterial color={NOX.page} toneMapped={false} /></mesh>
          <mesh position={[0, 0, -0.006]}><boxGeometry args={[0.26, 0.32, 0.004]} /><meshStandardMaterial color={NOX.ink} roughness={0.8} /></mesh>
        </group>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.20, 0.72, 0.08]}>
          <mesh rotation={[0.6, 0, sd * 0.40]}><capsuleGeometry args={[0.042, 0.24, 4, 10]} /><meshStandardMaterial {...robe} /></mesh>
          <mesh position={[sd * -0.05, -0.19, 0.14]}><sphereGeometry args={[0.046, 10, 8]} /><meshStandardMaterial {...inner} /></mesh>
        </group>
      ))}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={"m" + i} ref={(el) => (motes.current[i] = el)}>
          <sphereGeometry args={[0.016, 8, 8]} />
          <meshBasicMaterial color={pal.accent} transparent opacity={0.5} toneMapped={false} />
        </mesh>
      ))}
      {Aura({ r: 0.70, color: pal.glow, opacity: 0.06, y: 0.56, scale: [1, 1.1, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑩ ABYSSIA · Phantom · Beast · Lyf 3 / Lyt 5
// ════════════════════════════════════════════════════════════
//  "The Nightshade species", dual to Embearaea. Nightshade is a
//  real plant and it is beautiful and it will kill you, so the
//  body is a plant that has decided to be an animal: a bell of
//  five violet petals for a head, a berry cluster where a heart
//  goes (the berries are the eyes — they open), and roots for
//  legs that grip rather than stand.
const ABY = { stem: "#2a3a24", petal: "#4a2470", petalHot: "#a86aff", berry: "#0e0a14", pollen: "#5aff9e" };
export function AbyssiaBody({ pal }) {
  pal = pal || getSpeciesPalette("abyssia");
  const stemTex = hideTex("aby_stem_v1", "#3a5a30", "#22381c", "#0c1408", { fur: "rgba(90,255,158,0.25)" });
  const petalTex = hideTex("aby_petal_v1", "#7a3ab0", "#3a1c58", "#160a24", {
    speck: "rgba(90,255,158,0.5)",
    after: (g, w, h, R) => { for (let i = 0; i < 60; i++) leaf(g, R() * w, R() * h, 120 + R() * 160, 10 + R() * 10, (R() - .5) * 0.5, "rgba(168,106,255,0.35)", 0.5); },
  });
  const eye = eyeDisc("aby_eye_v1", "#c8ffe0", "#5aff9e", "#0a2a18", "rgba(4,10,6,0.95)", true);
  const rig = useBeingRig();
  const bell = useRef(); const berries = useRef([]); const spores = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (bell.current) { bell.current.rotation.x = 0.20 + Math.sin(t * 0.85) * 0.09; bell.current.rotation.z = Math.sin(t * 0.62) * 0.07; }
    berries.current.forEach((m, i) => { if (m) m.scale.setScalar(1 + Math.sin(t * 1.4 + i * 1.9) * 0.09); });
    spores.current.forEach((m, i) => {
      if (!m) return;
      const a = t * 0.42 + i * 2.0;
      m.position.set(Math.cos(a) * 0.24, 0.30 + ((t * 0.18 + i * 0.3) % 1) * 0.9, Math.sin(a) * 0.24);
      if (m.material) m.material.opacity = 0.4 * (1 - ((t * 0.18 + i * 0.3) % 1));
    });
  });
  const stem = { map: stemTex, roughness: 0.94, metalness: 0.05 };
  const petal = { map: petalTex, roughness: 0.72, metalness: 0.12, emissive: ABY.petalHot, emissiveIntensity: 0.16, side: THREE.DoubleSide };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.44, 0]} scale={[0.66, 1.14, 0.66]} castShadow>
          <capsuleGeometry args={[0.145, 0.32, 5, 12]} /><meshStandardMaterial {...stem} />
        </mesh>
        {/* THE BERRIES — a cluster at the heart, and they are eyes */}
        {Array.from({ length: 5 }).map((_, i) => {
          const a = (i / 5) * Math.PI * 2;
          return (
            <group key={i} ref={(el) => (berries.current[i] = el)}
              position={[Math.cos(a) * 0.115, 0.50 + Math.sin(i * 2.1) * 0.05, 0.10 + Math.sin(a) * 0.06]}
              rotation={[0, Math.cos(a) * 0.5, 0]}>
              <mesh><sphereGeometry args={[0.056, 12, 10]} /><meshStandardMaterial color={ABY.berry} roughness={0.30} metalness={0.35} /></mesh>
              <mesh position={[0, 0, 0.046]}><circleGeometry args={[0.030, 16]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
            </group>
          );
        })}
      </group>
      {/* THE BELL — five nightshade petals, hung, not held up */}
      <group ref={rig.head} position={[0, 0.86, 0]}>
        <group ref={bell}>
          <mesh position={[0, 0.02, 0]}><sphereGeometry args={[0.070, 12, 10]} /><meshStandardMaterial {...stem} /></mesh>
          {Petals({ n: 5, r: 0.085, len: 0.20, y: -0.02, tilt: 1.15, mat: petal })}
          {Petals({ n: 5, r: 0.052, len: 0.13, y: 0.04, tilt: 0.55, mat: { ...petal, emissiveIntensity: 0.35 }, phase: 0.6 })}
          {/* the throat of the flower, lit */}
          <mesh position={[0, -0.05, 0]}>
            <coneGeometry args={[0.065, 0.16, 12, 1, true]} />
            <meshBasicMaterial color={ABY.pollen} transparent opacity={0.42} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
          </mesh>
        </group>
      </group>
      {/* THE ROOTS — they grip, they do not stand */}
      {Array.from({ length: 6 }).map((_, i) => {
        const a = (i / 6) * Math.PI * 2;
        return (
          <group key={"r" + i} ref={i === 0 ? rig.legL : i === 3 ? rig.legR : undefined}
            position={[Math.cos(a) * 0.09, 0.20, Math.sin(a) * 0.09]} rotation={[0, -a, 0]}>
            <mesh position={[0.055, -0.10, 0]} rotation={[0, 0, -0.85]}>
              <capsuleGeometry args={[0.026, 0.16, 4, 8]} /><meshStandardMaterial {...stem} />
            </mesh>
            <mesh position={[0.115, -0.16, 0]} rotation={[0, 0, -1.25]}>
              <capsuleGeometry args={[0.017, 0.12, 3, 6]} /><meshStandardMaterial {...stem} />
            </mesh>
          </group>
        );
      })}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={"s" + i} ref={(el) => (spores.current[i] = el)}>
          <sphereGeometry args={[0.011, 6, 6]} />
          <meshBasicMaterial color={ABY.pollen} transparent opacity={0.4} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.60, color: pal.glow, opacity: 0.07, y: 0.52, scale: [1, 1.15, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑪ EMBEARAEA · Phantom · Beast · Lyf 5 / Lyt 3
// ════════════════════════════════════════════════════════════
//  "(firecracker-beary plant?) — either way they believe in the
//   Writer just like Noxura." The dual of Abyssia, and where
//   Abyssia is a plant pretending to be an animal, Embearaea is
//   an animal that has gone to seed: a small bear whose back is
//   a FUSE — a bundle of lit wicks that burn down and never out.
//   Solaea, so the light is sunlight; Shadow Gambling, so it is
//   burning a fuse it did not choose the length of.
const EMB = { pelt: "#8a4a24", pelthi: "#c45a2a", fuse: "#3a2a18", spark: "#ffd24a", bloom: "#4a8a3a" };
export function EmbearaeaBody({ pal }) {
  pal = pal || getSpeciesPalette("embearaea");
  const pelt = hideTex("emb_pelt_v1", "#c46a34", "#7a3c1c", "#2a1408", {
    fur: "rgba(255,210,74,0.35)", grain: 1300,
    after: (g, w, h, R) => { for (let i = 0; i < 380; i++) leaf(g, R() * w, R() * h, 28 + R() * 56, 4 + R() * 4, (R() - .5) * 1.6, "rgba(58,20,8,0.6)", 0.30); },
  });
  const eye = eyeDisc("emb_eye_v1", "#fff4c8", "#ffd24a", "#5a2a04", "rgba(12,6,2,0.9)", false);
  const rig = useBeingRig();
  const legs = [rig.armL, rig.armR, rig.legL, rig.legR];
  const sparks = useRef([]); const wicks = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    wicks.current.forEach((m, i) => {
      if (!m || !m.material) return;
      m.material.opacity = 0.55 + Math.abs(Math.sin(t * (7 + i) + i)) * 0.42;
      m.scale.setScalar(0.86 + Math.abs(Math.sin(t * (5.4 + i * 0.6))) * 0.30);
    });
    sparks.current.forEach((m, i) => {
      if (!m) return;
      const f = (t * 0.55 + i * 0.23) % 1;
      m.position.set(Math.sin(i * 3.1) * 0.14 * f, 0.72 + f * 0.55, -0.10 + Math.cos(i * 2.4) * 0.12 * f);
      m.scale.setScalar(1 - f * 0.75);
      if (m.material) m.material.opacity = 0.85 * (1 - f);
    });
  });
  const fur = { map: pelt, roughness: 1, emissive: EMB.spark, emissiveIntensity: 0.07 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.42, -0.04]} scale={[0.98, 0.94, 1.28]} castShadow>
          <sphereGeometry args={[0.200, 14, 12]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh position={[0, 0.44, 0.16]} scale={[0.92, 0.90, 0.98]}>
          <sphereGeometry args={[0.180, 14, 12]} /><meshStandardMaterial {...fur} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 0.56, 0.34]}>
        <mesh scale={[1.0, 0.94, 1.02]}><sphereGeometry args={[0.140, 14, 12]} /><meshStandardMaterial {...fur} /></mesh>
        <mesh position={[0, -0.042, 0.118]} scale={[0.76, 0.66, 1.10]}>
          <sphereGeometry args={[0.092, 12, 10]} /><meshStandardMaterial {...fur} />
        </mesh>
        <mesh position={[0, -0.036, 0.190]}><sphereGeometry args={[0.024, 8, 8]} /><meshStandardMaterial color="#241008" roughness={0.5} /></mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"e" + sd} position={[sd * 0.108, 0.098, -0.020]} scale={[1, 1, 0.44]}>
            <sphereGeometry args={[0.052, 10, 8]} /><meshStandardMaterial {...fur} />
          </mesh>
        ))}
        {Eyes({ map: eye, x: 0.070, y: 0.020, z: 0.104, r: 0.026, yaw: 0.48, socket: "#170a04" })}
      </group>
      {/* THE FUSE — seven wicks bundled on the back, all lit */}
      <group position={[0, 0.58, -0.14]}>
        {Array.from({ length: 7 }).map((_, i) => {
          const a = (i / 7) * Math.PI * 2, r = i === 6 ? 0 : 0.062;
          const L = 0.16 + Math.sin(i * 2.3) * 0.05;
          return (
            <group key={i} position={[Math.cos(a) * r, 0, Math.sin(a) * r]} rotation={[Math.sin(a) * 0.28, 0, -Math.cos(a) * 0.28]}>
              <mesh position={[0, L * 0.5, 0]}>
                <capsuleGeometry args={[0.011, L, 3, 6]} /><meshStandardMaterial color={EMB.fuse} roughness={0.9} />
              </mesh>
              <mesh ref={(el) => (wicks.current[i] = el)} position={[0, L + 0.02, 0]}>
                <sphereGeometry args={[0.030, 10, 8]} />
                <meshBasicMaterial color={EMB.spark} transparent opacity={0.8} toneMapped={false} depthWrite={false} />
              </mesh>
            </group>
          );
        })}
        {/* it is flowering as it burns — the Solaea half */}
        {Petals({ n: 6, r: 0.10, len: 0.085, y: -0.03, tilt: 0.9, mat: { color: EMB.bloom, roughness: 0.8, side: THREE.DoubleSide } })}
      </group>
      {[[-0.125, 0.185], [0.125, 0.185], [-0.130, -0.185], [0.130, -0.185]].map(([ox, oz], i) => (
        <group key={"lg" + i} ref={legs[i]} position={[ox, 0, oz]}>
          <mesh position={[0, 0.235, 0]}><capsuleGeometry args={[0.052, 0.15, 4, 10]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.088, 0.024]}><capsuleGeometry args={[0.044, 0.10, 4, 8]} /><meshStandardMaterial {...fur} /></mesh>
          <mesh position={[0, 0.036, 0.048]} scale={[1.1, 0.62, 1.4]}><sphereGeometry args={[0.048, 10, 8]} /><meshStandardMaterial {...fur} /></mesh>
        </group>
      ))}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={"sp" + i} ref={(el) => (sparks.current[i] = el)}>
          <sphereGeometry args={[0.014, 6, 6]} />
          <meshBasicMaterial color={EMB.spark} transparent opacity={0.8} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.58, color: pal.glow, opacity: 0.08, y: 0.46 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑫ RESPLENDANT CARCHARION · Phantom · Beast · Lyf 2 / Lyt 6
// ════════════════════════════════════════════════════════════
//  The shark, and N'ihil gave it the epithet "Resplendant" — so
//  it is not a grey shark, it is a POLISHED one: a countershaded
//  hide that goes white at the belly, and gill slits that glow
//  because a N'ihil creature breathes nothing. It does not have
//  legs; it swims through the air at ankle height, nose down,
//  and the caudal fin is the only thing that ever touches down.
const CAR = { back: "#3a5a6a", belly: "#e8f4ff", gill: "#ff4a4a", tooth: "#fffaf0", eye: "#0a0a0e" };
export function CarcharionBody({ pal }) {
  pal = pal || getSpeciesPalette("carcharion");
  const hide = hideTex("car_hide_v1", "#7a9aaa", "#3a5a6a", "#0e1a20", {
    grain: 1500, speck: "rgba(216,240,255,0.35)",
    after: (g, w, h, R) => {
      // dermal denticles read as a fine directional grain
      for (let i = 0; i < 900; i++) leaf(g, R() * w, R() * h, 10 + R() * 14, 1.6 + R(), 1.57 + (R() - .5) * 0.3, "rgba(255,255,255,0.16)", 0.5);
      const belly = g.createLinearGradient(0, h * 0.62, 0, h);
      belly.addColorStop(0, "rgba(232,244,255,0)"); belly.addColorStop(1, "rgba(232,244,255,0.85)");
      g.fillStyle = belly; g.fillRect(0, h * 0.62, w, h * 0.38);
    },
  });
  const eye = eyeDisc("car_eye_v1", "#3a4a54", "#0e1216", "#000000", "rgba(0,0,0,1)", false);
  const rig = useBeingRig();
  const tail = useRef([]); const gills = useRef([]); const glide = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    tail.current.forEach((m, i) => { if (m) m.rotation.y = Math.sin(t * 2.6 - i * 0.7) * (0.10 + i * 0.09); });
    gills.current.forEach((m, i) => { if (m && m.material) m.material.opacity = 0.35 + Math.sin(t * 2.2 + i * 0.7) * 0.30; });
    if (glide.current) { glide.current.position.y = 0.46 + Math.sin(t * 1.15) * 0.022; glide.current.rotation.z = Math.sin(t * 0.8) * 0.05; }
  });
  const skin = { map: hide, roughness: 0.58, metalness: 0.22 };
  return (
    <group>
      <group ref={glide}>
        <group ref={rig.torso}>
          <mesh position={[0, 0, -0.02]} scale={[0.80, 0.86, 1.62]} castShadow>
            <sphereGeometry args={[0.185, 16, 12]} /><meshStandardMaterial {...skin} />
          </mesh>
          <mesh position={[0, -0.010, -0.28]} scale={[0.60, 0.66, 1.10]}>
            <sphereGeometry args={[0.150, 12, 10]} /><meshStandardMaterial {...skin} />
          </mesh>
        </group>
        {/* the snout — long, conical, and slightly down */}
        <group ref={rig.head} position={[0, 0.004, 0.24]} rotation={[0.16, 0, 0]}>
          <mesh position={[0, 0, 0.05]} scale={[0.74, 0.72, 1.5]}>
            <sphereGeometry args={[0.130, 14, 12]} /><meshStandardMaterial {...skin} />
          </mesh>
          <mesh position={[0, 0.012, 0.190]} rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.062, 0.150, 10]} /><meshStandardMaterial {...skin} />
          </mesh>
          {/* THE JAW — under-slung, open, and full of teeth */}
          <mesh position={[0, -0.062, 0.130]} scale={[0.80, 0.34, 1.05]}>
            <sphereGeometry args={[0.096, 12, 10]} /><meshStandardMaterial color="#2a3a44" roughness={0.8} />
          </mesh>
          {Array.from({ length: 11 }).map((_, i) => {
            const a = -1 + (i / 10) * 2;
            return (
              <mesh key={"t" + i} position={[a * 0.070, -0.030, 0.150 - Math.abs(a) * 0.045]} rotation={[0.2, 0, a * 0.3]}>
                <coneGeometry args={[0.009, 0.036, 4]} />
                <meshStandardMaterial color={CAR.tooth} roughness={0.25} />
              </mesh>
            );
          })}
          {Eyes({ map: eye, x: 0.088, y: 0.032, z: 0.060, r: 0.022, yaw: 0.85, socket: "#0a1218" })}
          {/* the gills, and they are lit — a N'ihil thing breathes nothing */}
          {[0, 1, 2, 3, 4].map((i) => [-1, 1].map((sd) => (
            <mesh key={i + "_" + sd} ref={(el) => { if (sd > 0) gills.current[i] = el; }}
              position={[sd * 0.095, -0.010, -0.10 - i * 0.030]} rotation={[0, sd * 1.3, 0.25]}>
              <planeGeometry args={[0.014, 0.085]} />
              <meshBasicMaterial color={CAR.gill} transparent opacity={0.5} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
            </mesh>
          )))}
        </group>
        {/* the dorsal, the pectorals, and the tail */}
        <mesh position={[0, 0.150, -0.02]} rotation={[-0.35, 0, 0]} scale={[0.16, 1, 1]}>
          <coneGeometry args={[0.115, 0.24, 3] } /><meshStandardMaterial {...skin} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"p" + sd} ref={sd < 0 ? rig.armL : rig.armR}
            position={[sd * 0.145, -0.055, 0.03]} rotation={[0.1, sd * -0.5, sd * -0.9]} scale={[1, 0.14, 1]}>
            <coneGeometry args={[0.075, 0.30, 3]} /><meshStandardMaterial {...skin} />
          </mesh>
        ))}
        <group position={[0, 0, -0.40]}>
          <Chain refs={tail} n={4} step={[0, 0, -0.11]} render={(i) => (
            <mesh position={[0, 0, -0.055]} scale={[0.70 - i * 0.10, 0.80 - i * 0.10, 1]}>
              <sphereGeometry args={[0.090, 10, 8]} /><meshStandardMaterial {...skin} />
            </mesh>
          )} />
          <group position={[0, 0, -0.44]}>
            <mesh position={[0, 0.115, -0.06]} rotation={[-0.45, 0, 0]} scale={[0.10, 1, 1]}>
              <coneGeometry args={[0.090, 0.32, 3]} /><meshStandardMaterial {...skin} />
            </mesh>
            <mesh position={[0, -0.075, -0.03]} rotation={[0.75, 0, 0]} scale={[0.10, 1, 1]}>
              <coneGeometry args={[0.062, 0.18, 3]} /><meshStandardMaterial {...skin} />
            </mesh>
          </group>
        </group>
      </group>
      {Aura({ r: 0.72, color: pal.glow, opacity: 0.055, y: 0.46, scale: [0.8, 0.7, 1.5] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑬ LUSTERED CARNIFEX · Phantom · Beast · Lyf 7 / Lyt 1
// ════════════════════════════════════════════════════════════
//  The vault: "pyramid head type? creepy nun thing?" — and the
//  honest answer to a question mark is to build BOTH and let the
//  overlap be the design. So: a full floor-length habit with a
//  wimple, and under the wimple a smooth mitre of dull iron with
//  no face on it at all. O'mnia gives it the one warm thing it
//  owns — a lit censer, swinging, on a chain it never lets go.
const CNF = { habit: "#26241e", hem: "#8a1a1a", iron: "#4a463e", coal: "#ff6a1a", rope: "#8a7a5a" };
export function CarnifexBody({ pal }) {
  pal = pal || getSpeciesPalette("carnifex");
  const cloth = clothTex("cnf_cloth_v1", "#4a463c", "#26241e", "#0c0b08", "rgba(138,26,26,0.75)");
  const iron = plateTex("cnf_iron_v1", "#6a655a", "#3e3a34", "#16140f", "#9a9284");
  const rig = useBeingRig();
  const censer = useRef(); const smoke = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (censer.current) censer.current.rotation.z = Math.sin(t * 1.35) * 0.52;
    smoke.current.forEach((m, i) => {
      if (!m) return;
      const f = (t * 0.30 + i * 0.2) % 1;
      m.position.set(0.30 + Math.sin(t * 1.35) * 0.22 + Math.sin(i * 2.2) * 0.05, 0.34 + f * 0.85, 0.16 + Math.cos(i * 1.7) * 0.05);
      m.scale.setScalar(0.5 + f * 1.5);
      if (m.material) m.material.opacity = 0.24 * (1 - f);
    });
  });
  const habit = { map: cloth, roughness: 1, metalness: 0.02, color: CNF.habit };
  const metal = { map: iron, roughness: 0.44, metalness: 0.9 };
  return (
    <group>
      {/* the habit is a CONE to the floor. There are no legs and
          the vault never said there were any. */}
      <group ref={rig.torso}>
        <mesh position={[0, 0.46, 0]} castShadow>
          <coneGeometry args={[0.34, 0.94, 18]} /><meshStandardMaterial {...habit} />
        </mesh>
        <mesh position={[0, 0.90, 0]} scale={[1.0, 0.86, 1.0]}>
          <sphereGeometry args={[0.215, 14, 12]} /><meshStandardMaterial {...habit} />
        </mesh>
        {/* the cincture — one rope, three knots, the only waist */}
        <mesh position={[0, 0.72, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.235, 0.016, 6, 22]} /><meshStandardMaterial color={CNF.rope} roughness={0.9} />
        </mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[0.06, 0.62 - i * 0.075, 0.225]}>
            <sphereGeometry args={[0.024, 8, 8]} /><meshStandardMaterial color={CNF.rope} roughness={0.9} />
          </mesh>
        ))}
      </group>
      {/* THE WIMPLE, and the mitre under it with no face on it */}
      <group ref={rig.head} position={[0, 1.12, 0]}>
        <mesh position={[0, 0.02, 0]} scale={[1.12, 1.0, 1.12]}>
          <sphereGeometry args={[0.190, 14, 12]} /><meshStandardMaterial {...habit} />
        </mesh>
        <mesh position={[0, 0.16, -0.02]} rotation={[0.18, 0, 0]}>
          <coneGeometry args={[0.215, 0.30, 14]} /><meshStandardMaterial {...habit} />
        </mesh>
        {/* the mitre: four-sided, dull, and the strange load-bearing
            detail — it is a HEAD SHAPE with nothing on its front */}
        <mesh position={[0, 0.06, 0.045]} rotation={[0.10, Math.PI / 4, 0]}>
          <coneGeometry args={[0.135, 0.34, 4]} /><meshStandardMaterial {...metal} />
        </mesh>
        <mesh position={[0, -0.055, 0.062]} rotation={[0, Math.PI / 4, 0]}>
          <boxGeometry args={[0.17, 0.10, 0.17]} /><meshStandardMaterial {...metal} />
        </mesh>
        {/* one seam of light where a mouth is not */}
        <mesh position={[0, -0.030, 0.152]}>
          <planeGeometry args={[0.13, 0.010]} />
          <meshBasicMaterial color={CNF.coal} transparent opacity={0.75} toneMapped={false} />
        </mesh>
      </group>
      {/* sleeves — one folded in, one out on the chain */}
      <group ref={rig.armL} position={[-0.24, 0.86, 0.06]}>
        <mesh rotation={[0.7, 0, 0.35]}><capsuleGeometry args={[0.062, 0.30, 4, 10]} /><meshStandardMaterial {...habit} /></mesh>
        <mesh position={[0.06, -0.24, 0.16]}><sphereGeometry args={[0.052, 10, 8]} /><meshStandardMaterial color="#6a6458" roughness={0.9} /></mesh>
      </group>
      <group ref={rig.armR} position={[0.26, 0.88, 0.06]}>
        <mesh rotation={[0.5, 0, -0.30]}><capsuleGeometry args={[0.062, 0.30, 4, 10]} /><meshStandardMaterial {...habit} /></mesh>
        <group ref={rig.prop}>
          <group ref={censer} position={[0.04, -0.20, 0.10]}>
            <mesh position={[0, -0.16, 0]}><capsuleGeometry args={[0.004, 0.30, 3, 6]} /><meshStandardMaterial color={CNF.iron} metalness={0.9} roughness={0.4} /></mesh>
            <mesh position={[0, -0.35, 0]}>
              <sphereGeometry args={[0.062, 12, 10]} /><meshStandardMaterial {...metal} />
            </mesh>
            <mesh position={[0, -0.35, 0]}>
              <sphereGeometry args={[0.070, 10, 8]} />
              <meshBasicMaterial color={CNF.coal} transparent opacity={0.35} toneMapped={false} depthWrite={false} />
            </mesh>
          </group>
        </group>
      </group>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={"sm" + i} ref={(el) => (smoke.current[i] = el)}>
          <sphereGeometry args={[0.055, 10, 8]} />
          <meshBasicMaterial color="#cfc8b8" transparent opacity={0.2} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.78, color: CNF.coal, opacity: 0.045, y: 0.66, scale: [1, 1.2, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑭ LUSTERED GEULAOIN · Phantom · Beast · Lyf 3 / Lyt 5
// ════════════════════════════════════════════════════════════
//  "Red kind of avian like", Avarice + O'mnia, dual to Rapaxi.
//  The dual is the design brief: Rapaxi hunts from below, so
//  Geulaoin is built entirely for the STOOP — a raptor caught in
//  the half second before the dive. Wings back and half-folded,
//  head level with the shoulders, and every talon already open.
//  Avarice puts one gold ring on each foot; it has never dropped
//  either of them.
const GEU = { plume: "#c8342a", plumeHi: "#ff6a3a", quill: "#f0d8a8", beak: "#ffb02e", ring: "#ffd24a" };
export function GeulaoinBody({ pal }) {
  pal = pal || getSpeciesPalette("geulaoin");
  const plume = featherTex("geu_plume_v1", "#e8442e", "#a02218", "#3a0c08", "rgba(240,216,168,0.7)");
  const eye = eyeDisc("geu_eye_v1", "#fff8d8", "#ffb02e", "#3a1000", "rgba(6,2,0,0.95)", false);
  const rig = useBeingRig();
  const wingL = useRef(); const wingR = useRef(); const head = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // held, not flapping. A stoop is a decision, not a wingbeat.
    if (wingL.current) wingL.current.rotation.z = 0.36 + Math.sin(t * 1.5) * 0.07;
    if (wingR.current) wingR.current.rotation.z = -0.36 - Math.sin(t * 1.5) * 0.07;
    if (head.current) { head.current.rotation.y = Math.sin(t * 0.9) * 0.30; head.current.rotation.x = -0.10 + Math.sin(t * 1.7) * 0.05; }
  });
  const feath = { map: plume, roughness: 0.80, metalness: 0.12, emissive: GEU.plumeHi, emissiveIntensity: 0.10 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.56, 0.02]} scale={[0.84, 1.0, 1.06]} castShadow>
          <sphereGeometry args={[0.200, 14, 12]} /><meshStandardMaterial {...feath} />
        </mesh>
        <mesh position={[0, 0.38, -0.10]} scale={[0.76, 0.86, 0.92]}>
          <sphereGeometry args={[0.170, 12, 10]} /><meshStandardMaterial {...feath} />
        </mesh>
        {/* the fan tail, folded to a wedge */}
        <group position={[0, 0.34, -0.24]} rotation={[0.42, 0, 0]}>
          {[-3, -2, -1, 0, 1, 2, 3].map((i) => (
            <mesh key={i} position={[i * 0.020, -0.10, -0.03]} rotation={[0, 0, i * 0.075]}>
              <capsuleGeometry args={[0.016, 0.28, 3, 6]} /><meshStandardMaterial {...feath} />
            </mesh>
          ))}
        </group>
      </group>
      <group ref={rig.head} position={[0, 0.78, 0.10]}>
        <group ref={head}>
          <mesh scale={[0.94, 0.96, 1.0]}><sphereGeometry args={[0.120, 12, 10]} /><meshStandardMaterial {...feath} /></mesh>
          {/* THE BEAK — hooked, and the hook is the whole read */}
          <mesh position={[0, -0.012, 0.108]} rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.042, 0.115, 8]} /><meshStandardMaterial color={GEU.beak} roughness={0.35} metalness={0.35} />
          </mesh>
          <mesh position={[0, -0.048, 0.140]} rotation={[2.35, 0, 0]}>
            <coneGeometry args={[0.020, 0.062, 6]} /><meshStandardMaterial color={GEU.beak} roughness={0.35} metalness={0.35} />
          </mesh>
          {/* the crest, swept back */}
          {[-2, -1, 0, 1, 2].map((i) => (
            <mesh key={"c" + i} position={[i * 0.020, 0.098, -0.030]} rotation={[-0.9, 0, i * 0.14]}>
              <capsuleGeometry args={[0.010, 0.140, 3, 6]} /><meshStandardMaterial {...feath} />
            </mesh>
          ))}
          {Eyes({ map: eye, x: 0.078, y: 0.028, z: 0.070, r: 0.028, yaw: 0.72, socket: "#2a0a06" })}
        </group>
      </group>
      {/* THE WINGS — back, half-folded, three courses of quills */}
      <group ref={wingL} position={[-0.155, 0.66, -0.02]}>{Pinion({ side: -1, span: 0.52, rows: 3, n: 7, mat: feath, sweep: 0.22 })}</group>
      <group ref={wingR} position={[0.155, 0.66, -0.02]}>{Pinion({ side: 1, span: 0.52, rows: 3, n: 7, mat: feath, sweep: 0.22 })}</group>
      {/* THE TALONS — open before there is anything to take */}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.085, 0.34, 0.04]}>
          <mesh position={[0, -0.10, 0]}><capsuleGeometry args={[0.030, 0.14, 4, 8]} /><meshStandardMaterial {...feath} /></mesh>
          <mesh position={[0, -0.22, 0.014]}><capsuleGeometry args={[0.020, 0.11, 3, 8]} /><meshStandardMaterial color={GEU.beak} roughness={0.5} /></mesh>
          <mesh position={[0, -0.175, 0.014]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.028, 0.008, 6, 14]} /><meshStandardMaterial color={GEU.ring} metalness={0.95} roughness={0.2} emissive={GEU.ring} emissiveIntensity={0.3} />
          </mesh>
          {[-1, 0, 1, 2].map((c) => (
            <mesh key={c} position={[Math.sin(c * 1.5) * 0.035, -0.295, 0.014 + Math.cos(c * 1.5) * 0.035]}
              rotation={[c === 2 ? -1.0 : 1.0, c * 0.5, 0]}>
              <coneGeometry args={[0.010, 0.062, 5]} /><meshStandardMaterial color="#2a1408" roughness={0.4} metalness={0.4} />
            </mesh>
          ))}
        </group>
      ))}
      {Aura({ r: 0.70, color: pal.glow, opacity: 0.07, y: 0.60 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑮ RADIANT RAPAXI · Phantom · Beast · Lyf 4 / Lyt 4
// ════════════════════════════════════════════════════════════
//  "Blue kind of reptile", one of the Voracians — species that
//  worship Avarice as a RELIGION and not a practice. That is the
//  detail worth building: it is not greedy, it is devout. So the
//  hoard is worn like vestments — gold plate laid over the scale
//  of the chest and forearms in the shape of a breastplate, and
//  a low crouch with the belly on the floor, because everything
//  it has is underneath it.
const RPX = { scale: "#2a6ac8", scaleHi: "#4ad8ff", belly: "#c8e8ff", gold: "#ffd24a", claw: "#0e1a2a" };
export function RapaxiBody({ pal }) {
  pal = pal || getSpeciesPalette("rapaxi");
  const hide = scaleTex("rpx_scale_v1", "#4a9ae8", "#2258aa", "#0a1c3a", "rgba(74,216,255,0.6)", 17);
  const eye = eyeDisc("rpx_eye_v1", "#fff4c8", "#ffd24a", "#2a1800", "rgba(4,4,8,0.95)", true);
  const rig = useBeingRig();
  const legs = [rig.armL, rig.armR, rig.legL, rig.legR];
  const tail = useRef([]); const jaw = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    tail.current.forEach((m, i) => { if (m) m.rotation.y = Math.sin(t * 1.6 - i * 0.6) * 0.24; });
    if (jaw.current) jaw.current.rotation.x = 0.10 + Math.abs(Math.sin(t * 0.75)) * 0.18;
  });
  const skin = { map: hide, roughness: 0.62, metalness: 0.24, emissive: RPX.scaleHi, emissiveIntensity: 0.08 };
  const gold = { color: RPX.gold, metalness: 0.95, roughness: 0.24, emissive: RPX.gold, emissiveIntensity: 0.28 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.26, -0.04]} scale={[0.96, 0.72, 1.52]} castShadow>
          <sphereGeometry args={[0.205, 16, 12]} /><meshStandardMaterial {...skin} />
        </mesh>
        {/* THE BREASTPLATE — the hoard, worn as devotion */}
        <mesh position={[0, 0.30, 0.14]} scale={[0.82, 0.60, 0.70]}>
          <sphereGeometry args={[0.190, 14, 12]} /><meshStandardMaterial {...gold} />
        </mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={"rb" + i} position={[0, 0.20 - i * 0.02, -0.02 - i * 0.14]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.185 - i * 0.020, 0.014, 6, 20]} /><meshStandardMaterial {...gold} />
          </mesh>
        ))}
        {/* the dorsal ridge */}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <mesh key={"d" + i} position={[0, 0.40 - i * 0.006, 0.16 - i * 0.085]} rotation={[0.2, 0, 0]}>
            <coneGeometry args={[0.030 - i * 0.003, 0.090 - i * 0.008, 4]} /><meshStandardMaterial {...skin} />
          </mesh>
        ))}
      </group>
      <group ref={rig.head} position={[0, 0.28, 0.34]}>
        <mesh scale={[0.86, 0.74, 1.30]}><sphereGeometry args={[0.135, 14, 12]} /><meshStandardMaterial {...skin} /></mesh>
        <mesh position={[0, 0.006, 0.135]} scale={[0.70, 0.56, 1.20]}>
          <sphereGeometry args={[0.098, 12, 10]} /><meshStandardMaterial {...skin} />
        </mesh>
        <group ref={jaw} position={[0, -0.052, 0.055]}>
          <mesh position={[0, -0.010, 0.090]} scale={[0.62, 0.30, 1.10]}>
            <sphereGeometry args={[0.090, 12, 10]} /><meshStandardMaterial color="#12304a" roughness={0.8} />
          </mesh>
          {[-2, -1, 0, 1, 2].map((i) => (
            <mesh key={i} position={[i * 0.026, 0.020, 0.155 - Math.abs(i) * 0.022]} rotation={[-0.25, 0, 0]}>
              <coneGeometry args={[0.008, 0.030, 4]} /><meshStandardMaterial color="#e8f4ff" roughness={0.3} />
            </mesh>
          ))}
        </group>
        {/* the gold brow-band — a mitre for a reptile */}
        <mesh position={[0, 0.078, 0.020]} rotation={[Math.PI / 2 - 0.2, 0, 0]}>
          <torusGeometry args={[0.105, 0.014, 6, 18]} /><meshStandardMaterial {...gold} />
        </mesh>
        {Eyes({ map: eye, x: 0.080, y: 0.048, z: 0.076, r: 0.026, yaw: 0.62, socket: "#081428" })}
      </group>
      {[[-0.165, 0.190, 1], [0.165, 0.190, 1], [-0.170, -0.215, 1.1], [0.170, -0.215, 1.1]].map(([ox, oz, k], i) => (
        <group key={"lg" + i} ref={legs[i]} position={[ox, 0, oz]}>
          <mesh position={[0.0, 0.20, 0]} rotation={[0, 0, ox < 0 ? 0.42 : -0.42]}>
            <capsuleGeometry args={[0.048 * k, 0.14, 4, 8]} /><meshStandardMaterial {...skin} />
          </mesh>
          <mesh position={[ox < 0 ? -0.055 : 0.055, 0.095, 0.020]}>
            <capsuleGeometry args={[0.036, 0.11, 4, 8]} /><meshStandardMaterial {...skin} />
          </mesh>
          <mesh position={[ox < 0 ? -0.062 : 0.062, 0.032, 0.048]} scale={[1.2, 0.55, 1.5]}>
            <sphereGeometry args={[0.048, 10, 8]} /><meshStandardMaterial {...skin} />
          </mesh>
          {[-1, 0, 1].map((c) => (
            <mesh key={c} position={[(ox < 0 ? -0.062 : 0.062) + c * 0.028, 0.024, 0.104]} rotation={[1.25, 0, 0]}>
              <coneGeometry args={[0.010, 0.044, 5]} /><meshStandardMaterial color={RPX.claw} roughness={0.4} metalness={0.5} />
            </mesh>
          ))}
        </group>
      ))}
      <group position={[0, 0.26, -0.30]}>
        <Chain refs={tail} n={6} step={[0, -0.012, -0.135]} render={(i) => (
          <mesh position={[0, 0, -0.068]} rotation={[Math.PI / 2, 0, 0]}>
            <capsuleGeometry args={[0.072 - i * 0.010, 0.11, 4, 8]} /><meshStandardMaterial {...skin} />
          </mesh>
        )} />
      </group>
      {Aura({ r: 0.74, color: pal.glow, opacity: 0.06, y: 0.28, scale: [1, 0.8, 1.4] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑯ GORGXONOX · Phantom · Beast · Lyf 7 / Lyt 1
// ════════════════════════════════════════════════════════════
//  "Giant nasty beast." The vault stops there, and the stopping
//  IS the brief — a thing described by nothing but its effect.
//  So it is built with no readable species: six mismatched eyes
//  at different heights, a jaw that runs the full width of the
//  skull, an asymmetric hide (one shoulder plated, one raw), and
//  a hunch that puts the head lower than the back. Avarice and
//  Dark Art both, so it eats and it keeps.
const GRG = { hide: "#5a4a2a", rot: "#2a2010", plate: "#3a3226", bile: "#8ae23c", tusk: "#c8bc9a" };
export function GorgxonoxBody({ pal }) {
  pal = pal || getSpeciesPalette("gorgxonox");
  const hide = hideTex("grg_hide_v1", "#7a6a3e", "#42381e", "#100c06", {
    grain: 2000,
    after: (g, w, h, R) => {
      for (let i = 0; i < 300; i++) leaf(g, R() * w, R() * h, 30 + R() * 110, 8 + R() * 12, R() * 6.28, "rgba(20,14,6,0.55)", 0.45);
      for (let i = 0; i < 60; i++) {
        g.strokeStyle = `rgba(138,226,60,${0.10 + R() * 0.25})`; g.lineWidth = 1 + R() * 2;
        g.beginPath(); g.moveTo(R() * w, R() * h);
        g.bezierCurveTo(R() * w, R() * h, R() * w, R() * h, R() * w, R() * h); g.stroke();
      }
    },
  });
  const plate = plateTex("grg_plate_v1", "#5a5040", "#332c22", "#12100a", "#8a8068");
  const eyeBig = eyeDisc("grg_eyeA_v1", "#e8ffb0", "#8ae23c", "#1a2a06", "rgba(0,0,0,0.95)", true);
  const eyeSm = eyeDisc("grg_eyeB_v1", "#ffd8a8", "#c4453c", "#2a0a06", "rgba(0,0,0,0.95)", false);
  const rig = useBeingRig();
  const legs = [rig.armL, rig.armR, rig.legL, rig.legR];
  const jaw = useRef(); const drool = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (jaw.current) jaw.current.rotation.x = 0.16 + Math.abs(Math.sin(t * 0.9)) * 0.26;
    // it DRIPS. Everything in the Empire rises; this one falls.
    drool.current.forEach((m, i) => {
      if (!m) return;
      const f = (t * 0.7 + i * 0.31) % 1;
      m.position.set(-0.10 + i * 0.07, 0.62 - f * 0.55, 0.42);
      m.scale.setScalar(1 - f * 0.5);
      if (m.material) m.material.opacity = 0.55 * (1 - f * 0.8);
    });
  });
  const skin = { map: hide, roughness: 1, metalness: 0.05 };
  const armor = { map: plate, roughness: 0.62, metalness: 0.55 };
  return (
    <group>
      <group ref={rig.torso}>
        {/* the hunch — back higher than the head, and lopsided */}
        <mesh position={[0.03, 0.86, -0.16]} scale={[1.10, 0.94, 1.18]} castShadow>
          <sphereGeometry args={[0.330, 16, 14]} /><meshStandardMaterial {...skin} />
        </mesh>
        <mesh position={[0, 0.62, 0.02]} scale={[1.04, 0.98, 1.30]} castShadow>
          <sphereGeometry args={[0.310, 16, 14]} /><meshStandardMaterial {...skin} />
        </mesh>
        <mesh position={[-0.02, 0.44, -0.34]} scale={[0.92, 0.86, 0.94]}>
          <sphereGeometry args={[0.250, 12, 10]} /><meshStandardMaterial {...skin} />
        </mesh>
        {/* ONE shoulder plated. The asymmetry is the character. */}
        <mesh position={[-0.24, 0.86, 0.06]} rotation={[0.2, -0.4, 0.3]} scale={[1, 0.72, 1.15]}>
          <sphereGeometry args={[0.215, 12, 10]} /><meshStandardMaterial {...armor} />
        </mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={"sp" + i} position={[-0.30 - i * 0.02, 0.98 - i * 0.10, 0.10 - i * 0.14]} rotation={[0.4, -0.5, 0.9]}>
            <coneGeometry args={[0.038, 0.170, 6]} /><meshStandardMaterial color={GRG.tusk} roughness={0.4} metalness={0.3} />
          </mesh>
        ))}
      </group>
      {/* the head hangs BELOW the back, forward and low */}
      <group ref={rig.head} position={[0.02, 0.60, 0.44]} rotation={[0.22, 0, -0.06]}>
        <mesh scale={[1.28, 0.86, 1.06]} castShadow><sphereGeometry args={[0.215, 14, 12]} /><meshStandardMaterial {...skin} /></mesh>
        {/* the jaw runs the FULL width of the skull */}
        <group ref={jaw} position={[0, -0.10, 0.02]}>
          <mesh position={[0, -0.045, 0.070]} scale={[1.24, 0.42, 1.05]}>
            <sphereGeometry args={[0.200, 14, 10]} /><meshStandardMaterial color={GRG.rot} roughness={0.9} />
          </mesh>
          {Array.from({ length: 13 }).map((_, i) => {
            const x = (i - 6) * 0.040;
            return (
              <mesh key={i} position={[x, 0.010, 0.190 - Math.abs(x) * 0.42]} rotation={[-0.2, 0, x * 1.2]}>
                <coneGeometry args={[0.014, 0.070 + (i % 3) * 0.024, 5]} />
                <meshStandardMaterial color={GRG.tusk} roughness={0.45} />
              </mesh>
            );
          })}
        </group>
        {/* six eyes, no two the same, no two level */}
        <group position={[0, 0.05, 0.10]}>
          {Eyes({ map: eyeBig, x: 0.115, y: 0.055, z: 0.100, r: 0.038, yaw: 0.55, socket: "#14100a" })}
          {Eyes({ map: eyeSm, x: 0.165, y: -0.020, z: 0.055, r: 0.022, yaw: 0.85, socket: "#14100a" })}
          <mesh position={[-0.055, 0.115, 0.150]}><circleGeometry args={[0.017, 12]} /><meshBasicMaterial map={eyeSm} toneMapped={false} /></mesh>
          <mesh position={[0.070, 0.132, 0.135]}><circleGeometry args={[0.014, 12]} /><meshBasicMaterial map={eyeSm} toneMapped={false} /></mesh>
        </group>
      </group>
      {[[-0.235, 0.235, 1.20], [0.235, 0.235, 1.20], [-0.225, -0.265, 1.0], [0.225, -0.265, 1.0]].map(([ox, oz, k], i) => (
        <group key={"lg" + i} ref={legs[i]} position={[ox, 0, oz]}>
          <mesh position={[0, 0.44, 0]} scale={[k, 1, k]}><capsuleGeometry args={[0.090, 0.30, 4, 10]} /><meshStandardMaterial {...skin} /></mesh>
          <mesh position={[0, 0.16, 0.030]} scale={[k, 1, k]}><capsuleGeometry args={[0.076, 0.18, 4, 10]} /><meshStandardMaterial {...skin} /></mesh>
          <mesh position={[0, 0.056, 0.070]} scale={[1.3, 0.62, 1.6]}><sphereGeometry args={[0.082, 10, 8]} /><meshStandardMaterial {...skin} /></mesh>
          {[-1, 0, 1].map((c) => (
            <mesh key={c} position={[c * 0.042, 0.038, 0.140]} rotation={[1.2, 0, 0]}>
              <coneGeometry args={[0.016, 0.070, 5]} /><meshStandardMaterial color={GRG.tusk} roughness={0.45} />
            </mesh>
          ))}
        </group>
      ))}
      {[0, 1, 2, 3].map((i) => (
        <mesh key={"dr" + i} ref={(el) => (drool.current[i] = el)}>
          <sphereGeometry args={[0.018, 8, 8]} />
          <meshBasicMaterial color={GRG.bile} transparent opacity={0.5} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 1.00, color: GRG.bile, opacity: 0.04, y: 0.70, scale: [1, 0.9, 1.15] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑰ RADIANT AETERNAVIS · Phantom · Beast · Lyf 1 / Lyt 7
// ════════════════════════════════════════════════════════════
//  "Eagle Pheonix bird", O'mnia + Solaea, dual to Vyren.
//  ⚑ THE STAT LINE IS THE BRIEF (§4e): Lyf 1 / Lyt 7 means a body
//  with nothing spare on it, so there is barely any Aeternavis —
//  the torso is small and the WINGS are enormous, and the wings
//  are not feathers, they are fire held in the shape of feathers.
//  Every quill is emissive and the body between them is a coal.
const AET = { coal: "#5a2408", ash: "#2a1204", flame: "#ffb02e", hot: "#fff0c0", beak: "#ffd88a" };
export function AeternavisBody({ pal }) {
  pal = pal || getSpeciesPalette("aeternavis");
  const ember = hideTex("aet_ember_v1", "#a8481a", "#5a2408", "#1a0a02", {
    speck: "rgba(255,232,168,0.8)",
    after: (g, w, h, R) => {
      for (let i = 0; i < 220; i++) leaf(g, R() * w, R() * h, 40 + R() * 80, 6 + R() * 8, (R() - .5) * 1.2, "rgba(255,176,46,0.55)", 0.45);
      for (let i = 0; i < 120; i++) leaf(g, R() * w, R() * h, 20 + R() * 40, 4 + R() * 4, (R() - .5) * 1.2, "rgba(255,240,192,0.75)", 0.5);
    },
  });
  const eye = eyeDisc("aet_eye_v1", "#ffffff", "#ffd24a", "#5a1a00", "rgba(20,4,0,0.9)", false);
  const rig = useBeingRig();
  const wingL = useRef(); const wingR = useRef(); const motes = useRef([]); const crest = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (wingL.current) { wingL.current.rotation.z = 0.50 + Math.sin(t * 1.9) * 0.16; wingL.current.rotation.y = Math.sin(t * 1.3) * 0.10; }
    if (wingR.current) { wingR.current.rotation.z = -0.50 - Math.sin(t * 1.9) * 0.16; wingR.current.rotation.y = -Math.sin(t * 1.3) * 0.10; }
    if (crest.current) crest.current.rotation.x = Math.sin(t * 2.4) * 0.10;
    motes.current.forEach((m, i) => {
      if (!m) return;
      const f = (t * 0.65 + i * 0.14) % 1;
      m.position.set(Math.sin(i * 2.9) * 0.42 * (0.4 + f), 0.42 + f * 0.85, Math.cos(i * 2.1) * 0.30 * (0.4 + f));
      m.scale.setScalar(1 - f * 0.7);
      if (m.material) m.material.opacity = 0.8 * (1 - f);
    });
  });
  const body = { map: ember, roughness: 0.68, metalness: 0.18, emissive: AET.flame, emissiveIntensity: 0.55, toneMapped: false };
  const quill = { map: ember, color: "#ffffff", roughness: 0.5, emissive: AET.flame, emissiveIntensity: 1.15, toneMapped: false };
  return (
    <group>
      {/* there is not much bird here, and that is the card */}
      <group ref={rig.torso}>
        <mesh position={[0, 0.58, 0.02]} scale={[0.68, 0.94, 0.82]} castShadow>
          <sphereGeometry args={[0.150, 14, 12]} /><meshStandardMaterial {...body} />
        </mesh>
        <mesh position={[0, 0.40, -0.06]} scale={[0.58, 0.72, 0.72]}>
          <sphereGeometry args={[0.125, 12, 10]} /><meshStandardMaterial {...body} />
        </mesh>
        {/* the tail is a torch */}
        <group position={[0, 0.38, -0.20]} rotation={[0.55, 0, 0]}>
          {[-2, -1, 0, 1, 2].map((i) => (
            <mesh key={i} position={[i * 0.026, -0.16, 0]} rotation={[0, 0, i * 0.10]}>
              <capsuleGeometry args={[0.014, 0.34 - Math.abs(i) * 0.05, 3, 6]} /><meshStandardMaterial {...quill} />
            </mesh>
          ))}
        </group>
      </group>
      <group ref={rig.head} position={[0, 0.80, 0.06]}>
        <mesh scale={[0.90, 0.94, 0.98]}><sphereGeometry args={[0.100, 12, 10]} /><meshStandardMaterial {...body} /></mesh>
        <mesh position={[0, -0.010, 0.094]} rotation={[Math.PI / 2, 0, 0]}>
          <coneGeometry args={[0.034, 0.105, 8]} /><meshStandardMaterial color={AET.beak} roughness={0.30} metalness={0.5} emissive={AET.beak} emissiveIntensity={0.35} />
        </mesh>
        <mesh position={[0, -0.040, 0.122]} rotation={[2.4, 0, 0]}>
          <coneGeometry args={[0.016, 0.050, 6]} /><meshStandardMaterial color={AET.beak} roughness={0.30} metalness={0.5} />
        </mesh>
        {/* the crest — a running flame, not a feather comb */}
        <group ref={crest} position={[0, 0.086, -0.010]}>
          {[0, 1, 2, 3, 4].map((i) => (
            <mesh key={i} position={[0, 0.020 + i * 0.010, -0.020 - i * 0.032]} rotation={[-0.65 - i * 0.12, 0, 0]}>
              <coneGeometry args={[0.020 - i * 0.002, 0.115 - i * 0.012, 5]} /><meshStandardMaterial {...quill} />
            </mesh>
          ))}
        </group>
        {Eyes({ map: eye, x: 0.066, y: 0.026, z: 0.062, r: 0.024, yaw: 0.76, socket: "#2a0c00" })}
      </group>
      {/* THE WINGS — five courses each, and they are the being */}
      <group ref={wingL} position={[-0.115, 0.66, 0]}>
        {Pinion({ side: -1, span: 0.78, rows: 4, n: 9, mat: quill, sweep: 0.16 })}
      </group>
      <group ref={wingR} position={[0.115, 0.66, 0]}>
        {Pinion({ side: 1, span: 0.78, rows: 4, n: 9, mat: quill, sweep: 0.16 })}
      </group>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.062, 0.36, 0.02]}>
          <mesh position={[0, -0.10, 0]}><capsuleGeometry args={[0.020, 0.14, 3, 8]} /><meshStandardMaterial color={AET.beak} roughness={0.5} metalness={0.4} /></mesh>
          {[-1, 0, 1].map((c) => (
            <mesh key={c} position={[c * 0.024, -0.185, 0.026]} rotation={[1.0, c * 0.4, 0]}>
              <coneGeometry args={[0.008, 0.048, 5]} /><meshStandardMaterial color={AET.beak} roughness={0.4} metalness={0.5} />
            </mesh>
          ))}
        </group>
      ))}
      {Array.from({ length: 9 }).map((_, i) => (
        <mesh key={"m" + i} ref={(el) => (motes.current[i] = el)}>
          <sphereGeometry args={[0.017, 6, 6]} />
          <meshBasicMaterial color={AET.hot} transparent opacity={0.8} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.82, color: AET.flame, opacity: 0.10, y: 0.62 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑱ TZHAHWS · Phantom · Beast · Lyf 4 / Lyt 4
// ════════════════════════════════════════════════════════════
//  "Thieving. Their attackers are pirates, their Lord is the
//   Captain, their tank is the Buccaneer." Avarice. So this is a
//   species whose ROLES are ranks in a crew, and the Beb is the
//   deckhand: a lean scavenging thing wearing salvage — one
//   spyglass eye it did not grow, a coat cut down from something
//   larger, and a hook where a hand went missing. Every item on
//   it belonged to somebody else, which IS the species.
const TZH = { fur: "#7a5a3a", coat: "#3a2a44", brass: "#ffd24a", steel: "#c8ccd8", sash: "#c8342a" };
export function TzhahwsBody({ pal }) {
  pal = pal || getSpeciesPalette("tzhahws");
  const fur = hideTex("tzh_fur_v1", "#a07a4a", "#5a4028", "#1c1408", { fur: "rgba(30,20,10,0.6)", grain: 1400 });
  const coat = clothTex("tzh_coat_v1", "#4e3a5a", "#2e2036", "#0e0a12", "rgba(255,210,74,0.6)");
  const eye = eyeDisc("tzh_eye_v1", "#fff0c8", "#ffd24a", "#3a2000", "rgba(6,4,0,0.9)", false);
  const rig = useBeingRig();
  const glass = useRef(); const hook = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the spyglass eye RACKS FOCUS. It is always sizing you up.
    if (glass.current) glass.current.scale.z = 1 + Math.sin(t * 0.9) * 0.22;
    if (hook.current) hook.current.rotation.z = Math.sin(t * 1.6) * 0.16;
  });
  const pelt = { map: fur, roughness: 1 };
  const cloth = { map: coat, roughness: 0.92, metalness: 0.06 };
  const brass = { color: TZH.brass, metalness: 0.95, roughness: 0.26, emissive: TZH.brass, emissiveIntensity: 0.2 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.62, 0]} scale={[0.82, 1.0, 0.76]} castShadow>
          <sphereGeometry args={[0.185, 14, 12]} /><meshStandardMaterial {...pelt} />
        </mesh>
        {/* THE COAT — cut down from something much larger */}
        <mesh position={[0, 0.46, -0.01]} scale={[1.0, 1.0, 0.92]}>
          <coneGeometry args={[0.235, 0.60, 14]} /><meshStandardMaterial {...cloth} />
        </mesh>
        <mesh position={[0, 0.74, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[1, 1, 0.8]}>
          <torusGeometry args={[0.175, 0.035, 6, 18]} /><meshStandardMaterial {...cloth} />
        </mesh>
        <mesh position={[0, 0.56, 0.02]} rotation={[0, 0, 0.6]} scale={[1, 1, 0.9]}>
          <torusGeometry args={[0.175, 0.020, 6, 18]} />
          <meshStandardMaterial color={TZH.sash} roughness={0.85} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 0.92, 0.02]}>
        <mesh scale={[0.90, 0.92, 1.06]}><sphereGeometry args={[0.130, 12, 10]} /><meshStandardMaterial {...pelt} /></mesh>
        <mesh position={[0, -0.040, 0.118]} scale={[0.66, 0.58, 1.35]}>
          <sphereGeometry args={[0.082, 12, 10]} /><meshStandardMaterial {...pelt} />
        </mesh>
        <mesh position={[0, -0.036, 0.196]}><sphereGeometry args={[0.020, 8, 8]} /><meshStandardMaterial color="#2a1810" roughness={0.5} /></mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"e" + sd} position={[sd * 0.098, 0.098, -0.014]} rotation={[0, sd * 0.4, sd * 0.24]} scale={[1, 1, 0.32]}>
            <sphereGeometry args={[0.058, 10, 8]} /><meshStandardMaterial {...pelt} />
          </mesh>
        ))}
        {/* one real eye, one SPYGLASS strapped over the socket */}
        <group position={[-0.062, 0.020, 0.098]} rotation={[0, -0.45, 0]}>
          <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.032, 8, 8]} /><meshStandardMaterial color="#0e0a06" roughness={1} /></mesh>
          <mesh position={[0, 0, 0.020]}><circleGeometry args={[0.026, 16]} /><meshBasicMaterial map={eye} toneMapped={false} /></mesh>
        </group>
        <group ref={glass} position={[0.070, 0.024, 0.104]} rotation={[0, 0.42, 0]}>
          <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.030, 0.038, 0.100, 12]} /><meshStandardMaterial {...brass} /></mesh>
          <mesh position={[0, 0, 0.056]}><circleGeometry args={[0.028, 14]} />
            <meshBasicMaterial color="#8ad8ff" transparent opacity={0.75} toneMapped={false} /></mesh>
        </group>
        <mesh position={[0, 0.020, -0.02]} rotation={[0, 0, 0.1]} scale={[1.06, 1, 1.0]}>
          <torusGeometry args={[0.126, 0.014, 6, 18]} /><meshStandardMaterial color="#1a1410" roughness={0.9} />
        </mesh>
      </group>
      {/* one hand, one hook */}
      <group ref={rig.armL} position={[-0.20, 0.72, 0.04]}>
        <mesh rotation={[0.4, 0, 0.36]}><capsuleGeometry args={[0.044, 0.24, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
        <mesh position={[0.055, -0.21, 0.07]}><sphereGeometry args={[0.046, 10, 8]} /><meshStandardMaterial {...pelt} /></mesh>
      </group>
      <group ref={rig.armR} position={[0.20, 0.72, 0.04]}>
        <mesh rotation={[0.4, 0, -0.36]}><capsuleGeometry args={[0.044, 0.24, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
        <group ref={rig.prop}>
          <group ref={hook} position={[-0.052, -0.22, 0.06]}>
            <mesh position={[0, -0.03, 0]}><cylinderGeometry args={[0.026, 0.032, 0.06, 10]} /><meshStandardMaterial {...brass} /></mesh>
            <mesh position={[0, -0.10, 0.03]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[0.052, 0.011, 6, 14, Math.PI * 1.35]} />
              <meshStandardMaterial color={TZH.steel} metalness={0.95} roughness={0.18} />
            </mesh>
          </group>
        </group>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.082, 0.26, 0.02]}>
          <mesh position={[0, -0.10, 0]}><capsuleGeometry args={[0.036, 0.14, 4, 8]} /><meshStandardMaterial {...pelt} /></mesh>
          <mesh position={[0, -0.22, 0.028]} scale={[1, 0.7, 1.5]}><sphereGeometry args={[0.046, 10, 8]} /><meshStandardMaterial color="#2a2018" roughness={0.9} /></mesh>
        </group>
      ))}
      {Aura({ r: 0.62, color: pal.glow, opacity: 0.05, y: 0.60 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑲ VALTORIX · Phantom · Beast · Lyf 5 / Lyt 3
// ════════════════════════════════════════════════════════════
//  "Skeleton horse-like", Dark Art + Solaea. The Solaea half is
//  what stops this being a generic bone horse: there is a SUN
//  inside the ribcage, and it is the only organ left. The bone is
//  lit from within by it, hottest at the ribs and cooling toward
//  the hooves, and the mane is not hair — it is the light getting
//  out through the gap where the neck vertebrae do not meet.
const VLT = { bone: "#d8d0bc", boneDeep: "#8a8070", sun: "#ff8a2e", core: "#ffd88a", socket: "#1a1208" };
export function ValtorixBody({ pal }) {
  pal = pal || getSpeciesPalette("valtorix");
  const bone = hideTex("vlt_bone_v1", "#efe6d0", "#c4b9a0", "#6a604e", {
    grain: 1200,
    after: (g, w, h, R) => {
      for (let i = 0; i < 150; i++) {
        g.strokeStyle = `rgba(90,80,60,${0.15 + R() * 0.3})`; g.lineWidth = 0.8 + R() * 1.4;
        const x = R() * w, y = R() * h;
        g.beginPath(); g.moveTo(x, y); g.lineTo(x + (R() - .5) * 90, y + (R() - .5) * 30); g.stroke();
      }
    },
  });
  const rig = useBeingRig();
  const legs = [rig.armL, rig.armR, rig.legL, rig.legR];
  const sun = useRef(); const mane = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (sun.current) { sun.current.scale.setScalar(1 + Math.sin(t * 1.6) * 0.09); sun.current.rotation.y = t * 0.5; }
    mane.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.x = -0.4 + Math.sin(t * 2.1 + i * 0.6) * 0.22;
      if (m.material) m.material.opacity = 0.42 + Math.sin(t * 2.6 + i) * 0.20;
    });
  });
  const skel = { map: bone, roughness: 0.72, metalness: 0.10, emissive: VLT.sun, emissiveIntensity: 0.10 };
  return (
    <group>
      {/* THE RIBCAGE — open, and the sun is the only organ left */}
      <group ref={rig.torso}>
        <mesh position={[0, 0.72, -0.02]} rotation={[Math.PI / 2, 0, 0]}>
          <capsuleGeometry args={[0.032, 0.46, 4, 8]} /><meshStandardMaterial {...skel} />
        </mesh>
        {[0, 1, 2, 3, 4, 5, 6].map((i) => {
          const f = i / 6;
          return (
            <mesh key={"rb" + i} position={[0, 0.66 - f * 0.02, 0.20 - f * 0.44]} rotation={[Math.PI / 2, 0, 0]}
              scale={[1, 1, 0.78 + Math.sin(f * Math.PI) * 0.36]}>
              <torusGeometry args={[0.170, 0.019, 6, 20, Math.PI * 1.55]} /><meshStandardMaterial {...skel} />
            </mesh>
          );
        })}
        <mesh ref={sun} position={[0, 0.66, -0.02]}>
          <sphereGeometry args={[0.105, 14, 12]} />
          <meshBasicMaterial color={VLT.core} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.66, -0.02]}>
          <sphereGeometry args={[0.20, 12, 10]} />
          <meshBasicMaterial color={VLT.sun} transparent opacity={0.24} toneMapped={false} depthWrite={false} />
        </mesh>
        {/* pelvis and the whip of a tail — vertebrae, no hair */}
        <mesh position={[0, 0.66, -0.32]} scale={[1.1, 0.8, 0.7]}>
          <sphereGeometry args={[0.130, 12, 10]} /><meshStandardMaterial {...skel} />
        </mesh>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <mesh key={"tv" + i} position={[0, 0.64 - i * 0.032, -0.42 - i * 0.075]}>
            <sphereGeometry args={[0.030 - i * 0.004, 8, 8]} /><meshStandardMaterial {...skel} />
          </mesh>
        ))}
      </group>
      {/* THE NECK — vertebrae that do not meet, so light gets out */}
      <group ref={rig.head} position={[0, 0.86, 0.30]}>
        {[0, 1, 2, 3].map((i) => (
          <mesh key={"nv" + i} position={[0, i * 0.075, i * 0.052]}>
            <sphereGeometry args={[0.052 - i * 0.004, 10, 8]} /><meshStandardMaterial {...skel} />
          </mesh>
        ))}
        {[0, 1, 2, 3, 4].map((i) => (
          <mesh key={"mn" + i} ref={(el) => (mane.current[i] = el)} position={[0, 0.02 + i * 0.070, -0.02 + i * 0.048]}>
            <coneGeometry args={[0.036, 0.150, 6]} />
            <meshBasicMaterial color={VLT.sun} transparent opacity={0.5} toneMapped={false} depthWrite={false} />
          </mesh>
        ))}
        {/* THE SKULL — long, and the sockets are lit sun, not eyes */}
        <group position={[0, 0.30, 0.20]} rotation={[0.5, 0, 0]}>
          <mesh scale={[0.72, 0.80, 1.5]}><sphereGeometry args={[0.098, 12, 10]} /><meshStandardMaterial {...skel} /></mesh>
          <mesh position={[0, -0.028, 0.150]} scale={[0.56, 0.52, 1.05]}>
            <sphereGeometry args={[0.070, 10, 8]} /><meshStandardMaterial {...skel} />
          </mesh>
          {[-1, 1].map((sd) => (
            <group key={sd} position={[sd * 0.062, 0.020, 0.062]}>
              <mesh scale={[1, 1, 0.5]}><sphereGeometry args={[0.034, 8, 8]} /><meshStandardMaterial color={VLT.socket} roughness={1} /></mesh>
              <mesh position={[0, 0, 0.018]}><sphereGeometry args={[0.024, 8, 8]} />
                <meshBasicMaterial color={VLT.core} toneMapped={false} /></mesh>
            </group>
          ))}
          {[-1, 1].map((sd) => (
            <mesh key={"ea" + sd} position={[sd * 0.048, 0.086, -0.030]} rotation={[-0.2, 0, sd * 0.3]}>
              <coneGeometry args={[0.020, 0.086, 5]} /><meshStandardMaterial {...skel} />
            </mesh>
          ))}
        </group>
      </group>
      {/* four bone legs, cooling to the hoof */}
      {[[-0.145, 0.230], [0.145, 0.230], [-0.150, -0.270], [0.150, -0.270]].map(([ox, oz], i) => (
        <group key={"lg" + i} ref={legs[i]} position={[ox, 0, oz]}>
          <mesh position={[0, 0.46, 0]}><capsuleGeometry args={[0.044, 0.26, 4, 8]} /><meshStandardMaterial {...skel} /></mesh>
          <mesh position={[0, 0.24, 0.020]}><capsuleGeometry args={[0.030, 0.20, 4, 8]} /><meshStandardMaterial {...skel} emissiveIntensity={0.05} /></mesh>
          <mesh position={[0, 0.085, 0.028]}><capsuleGeometry args={[0.024, 0.10, 3, 8]} /><meshStandardMaterial color={VLT.boneDeep} roughness={0.8} /></mesh>
          <mesh position={[0, 0.030, 0.030]} scale={[1.2, 0.7, 1.2]}>
            <cylinderGeometry args={[0.042, 0.048, 0.055, 10]} /><meshStandardMaterial color="#3a2a1c" roughness={0.6} metalness={0.3} />
          </mesh>
        </group>
      ))}
      {Aura({ r: 0.86, color: VLT.sun, opacity: 0.06, y: 0.66, scale: [1, 0.9, 1.25] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ⑳ H-06 WARDEN · Phantom · Humanoid · Lyf 6 / Lyt 2
// ════════════════════════════════════════════════════════════
//  ⚑ THE EXCEPTION ON THE ROSTER. Every other Phantom rides with
//  Ilk Beast; the vault files H-06 under Phantom and it is
//  unmistakably an H- suit. Both are true, so both are printed —
//  and the model is built at the seam: an H- suit that the
//  Phantom school has got INTO. The suit is intact; what fills it
//  is not. Warden, so it carries no weapon, only a standing
//  shield-ring it never lowers.
const H06 = { suit: "#5a6a6a", plate: "#2a3636", ghost: "#7fe8d8", ring: "#b8a8ff", visor: "#0a1212" };
// ⚠ RESTORED 2026-09-16 — the Rush → Annihilation port (7291eab) carried the
// bodies that use this table but not the table itself, so the build failed
// no-undef and the body would have thrown on its first render. Verbatim
// from app-rush/src/ieTerrorSpecies.jsx.
const LYR = { skin: "#fff0c8", robe: "#f4e8d0", wing: "#8ad8ff", lamp: "#ffffff", halo: "#ffe8a8" };
export function LyraeiBody({ pal }) {
  pal = pal || getSpeciesPalette("lyraei");
  const robe = clothTex("lyr_robe_v1", "#fffaf0", "#efe2c8", "#bfae90", "rgba(138,216,255,0.7)");
  const face = faceTex("lyr_face_v1", "#fff2d8", "#d8b88a", "#8ad8ff", "rgba(200,140,120,0.8)");
  const rig = useBeingRig();
  const wings = useRef([]); const lamp = useRef(); const halo = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    wings.current.forEach((m, i) => {
      if (!m) return;
      const sd = i % 2 ? 1 : -1;
      m.rotation.z = sd * (0.44 + Math.sin(t * 4.2 + i * 0.9) * 0.24);
      m.rotation.y = sd * Math.sin(t * 4.2 + i) * 0.12;
    });
    if (lamp.current) { lamp.current.position.y = 0.72 + Math.sin(t * 1.1) * 0.018; lamp.current.rotation.y = t * 0.6; }
    if (halo.current) halo.current.rotation.z = t * 0.24;
  });
  const cloth = { map: robe, roughness: 0.88, metalness: 0.06, emissive: LYR.halo, emissiveIntensity: 0.14 };
  const wingMat = { color: LYR.wing, transparent: true, opacity: 0.30, side: THREE.DoubleSide, toneMapped: false, depthWrite: false };
  return (
    <group>
      {/* kneeling in the air: the robe ends in a taper, no feet */}
      <group ref={rig.torso}>
        <mesh position={[0, 0.62, 0]} scale={[1, 1, 0.9]} castShadow>
          <coneGeometry args={[0.185, 0.56, 14]} /><meshStandardMaterial {...cloth} />
        </mesh>
        <mesh position={[0, 0.34, 0]} scale={[0.7, 0.5, 0.7]}>
          <sphereGeometry args={[0.150, 12, 10]} /><meshStandardMaterial {...cloth} />
        </mesh>
        <mesh position={[0, 0.88, 0]} scale={[1.0, 0.82, 0.86]}>
          <sphereGeometry args={[0.140, 12, 10]} /><meshStandardMaterial {...cloth} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 1.06, 0.01]}>
        <mesh scale={[0.94, 1.0, 0.96]}><sphereGeometry args={[0.115, 14, 12]} />
          <meshStandardMaterial map={face} roughness={0.78} emissive={LYR.halo} emissiveIntensity={0.10} /></mesh>
        {/* the halo — a plain ring, edge-on, turning slowly */}
        <group ref={halo} position={[0, 0.15, -0.02]} rotation={[0.35, 0, 0]}>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.145, 0.008, 6, 30]} />
            <meshBasicMaterial color={LYR.halo} transparent opacity={0.85} toneMapped={false} />
          </mesh>
        </group>
      </group>
      {/* FOUR WINGS, iridescent, two pairs on different phases */}
      {[0, 1, 2, 3].map((i) => {
        const sd = i % 2 ? 1 : -1, up = i < 2;
        return (
          <group key={i} ref={(el) => (wings.current[i] = el)}
            position={[sd * 0.10, up ? 0.92 : 0.76, -0.06]}>
            <mesh rotation={[0, sd * 0.5, sd * 0.5]} scale={[1, up ? 1 : 0.78, 1]}>
              <circleGeometry args={[up ? 0.34 : 0.27, 14, 0, Math.PI]} />
              <meshBasicMaterial {...wingMat} color={i % 2 ? LYR.wing : "#e8b0ff"} />
            </mesh>
            <mesh rotation={[0, sd * 0.5, sd * 0.5]} scale={[1, up ? 1 : 0.78, 1]} position={[sd * 0.01, 0, 0.004]}>
              <circleGeometry args={[up ? 0.20 : 0.16, 12, 0, Math.PI]} />
              <meshBasicMaterial {...wingMat} opacity={0.20} color="#ffffff" />
            </mesh>
          </group>
        );
      })}
      {/* THE LAMP — tended with both hands, never looked away from */}
      <group ref={rig.prop}>
        <group ref={lamp} position={[0, 0.72, 0.20]}>
          <mesh><sphereGeometry args={[0.062, 14, 12]} /><meshBasicMaterial color={LYR.lamp} toneMapped={false} /></mesh>
          <mesh><sphereGeometry args={[0.115, 12, 10]} />
            <meshBasicMaterial color={LYR.halo} transparent opacity={0.28} toneMapped={false} depthWrite={false} /></mesh>
          {Array.from({ length: 4 }).map((_, i) => (
            <mesh key={i} rotation={[0, (i / 4) * Math.PI, 0]}>
              <torusGeometry args={[0.086, 0.004, 5, 22]} />
              <meshBasicMaterial color={LYR.halo} transparent opacity={0.55} toneMapped={false} />
            </mesh>
          ))}
        </group>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.145, 0.90, 0.04]}>
          <mesh rotation={[0.85, 0, sd * 0.35]}><capsuleGeometry args={[0.030, 0.19, 4, 8]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * -0.045, -0.155, 0.135]}><sphereGeometry args={[0.034, 10, 8]} />
            <meshStandardMaterial color={LYR.skin} roughness={0.7} emissive={LYR.halo} emissiveIntensity={0.2} /></mesh>
        </group>
      ))}
      {Aura({ r: 0.60, color: pal.glow, opacity: 0.11, y: 0.78 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉒ RADIANT MALUMORA · Space · Humanoid · Lyf 2 / Lyt 6
// ════════════════════════════════════════════════════════════
//  "Tiny devil", and the exact opposite of Lyraei — SAME SIZE,
//  SAME LIGHT, nothing else in common. So it is built as the
//  variant pair (§4f): the same small kneeling-in-air silhouette,
//  the same four wings, the same suspended light — and they
//  disagree about every one of them. Lyraei's wings are glass;
//  Malumora's are cut membrane. Lyraei tends its lamp; Malumora
//  has SWALLOWED its light, and it shows through the ribs.
const MAL = { skin: "#c8344a", horn: "#241820", wing: "#5a0c1c", coal: "#ff8a5a", eye: "#ffd24a" };
export function MalumoraBody({ pal }) {
  pal = pal || getSpeciesPalette("malumora");
  const hide = hideTex("mal_hide_v1", "#e8455a", "#8a1c2e", "#280810", {
    grain: 900, speck: "rgba(255,138,90,0.4)",
    after: (g, w, h, R) => { for (let i = 0; i < 120; i++) leaf(g, R() * w, R() * h, 40 + R() * 70, 6 + R() * 6, (R() - .5) * 1.5, "rgba(40,8,16,0.55)", 0.45); },
  });
  const face = faceTex("mal_face_v1", "#e8556a", "#3a0c18", "#ffd24a", "rgba(20,4,8,0.9)", { angry: true, slit: true });
  const rig = useBeingRig();
  const wings = useRef([]); const tail = useRef([]); const gut = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    wings.current.forEach((m, i) => {
      if (!m) return;
      const sd = i % 2 ? 1 : -1;
      m.rotation.z = sd * (0.38 + Math.sin(t * 5.4 + i * 1.4) * 0.30);
    });
    tail.current.forEach((m, i) => { if (m) m.rotation.z = Math.sin(t * 3.1 - i * 0.8) * 0.30; });
    if (gut.current) gut.current.scale.setScalar(1 + Math.sin(t * 2.4) * 0.11);
  });
  const skin = { map: hide, roughness: 0.74, metalness: 0.1, emissive: MAL.coal, emissiveIntensity: 0.12 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.62, 0]} scale={[1, 1, 0.9]} castShadow>
          <coneGeometry args={[0.170, 0.52, 12] } /><meshStandardMaterial {...skin} />
        </mesh>
        {/* THE SWALLOWED LIGHT — through the ribs, not on a lamp */}
        <mesh ref={gut} position={[0, 0.62, 0.06]}>
          <sphereGeometry args={[0.080, 12, 10]} /><meshBasicMaterial color={MAL.coal} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.62, 0.06]}><sphereGeometry args={[0.135, 12, 10]} />
          <meshBasicMaterial color={MAL.coal} transparent opacity={0.22} toneMapped={false} depthWrite={false} /></mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[0, 0.70 - i * 0.070, 0.02]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.115 - i * 0.010, 0.010, 5, 16, Math.PI * 1.2]} /><meshStandardMaterial {...skin} />
          </mesh>
        ))}
        <mesh position={[0, 0.86, 0]} scale={[1.0, 0.78, 0.84]}>
          <sphereGeometry args={[0.130, 12, 10]} /><meshStandardMaterial {...skin} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 1.03, 0.01]}>
        <mesh scale={[0.96, 1.0, 0.94]}><sphereGeometry args={[0.108, 14, 12]} />
          <meshStandardMaterial map={face} roughness={0.7} /></mesh>
        {/* the horns, swept back and forward-curling at the tip */}
        {[-1, 1].map((sd) => (
          <group key={sd} position={[sd * 0.062, 0.086, -0.020]}>
            {[0, 1, 2].map((i) => (
              <mesh key={i} position={[sd * i * 0.020, 0.045 + i * 0.052, -0.030 - i * 0.014 + (i === 2 ? 0.05 : 0)]}
                rotation={[i === 2 ? 1.0 : -0.35, 0, sd * (0.2 + i * 0.1)]}>
                <coneGeometry args={[0.024 - i * 0.006, 0.075, 6]} />
                <meshStandardMaterial color={MAL.horn} roughness={0.42} metalness={0.4} />
              </mesh>
            ))}
          </group>
        ))}
      </group>
      {/* FOUR WINGS — cut membrane, and the cuts are deliberate */}
      {[0, 1, 2, 3].map((i) => {
        const sd = i % 2 ? 1 : -1, up = i < 2;
        return (
          <group key={i} ref={(el) => (wings.current[i] = el)} position={[sd * 0.095, up ? 0.90 : 0.74, -0.06]}>
            {Membrane({
              side: sd, span: up ? 0.40 : 0.30, bones: 4,
              boneMat: { color: MAL.horn, roughness: 0.5, metalness: 0.35 },
              memMat: { color: MAL.wing, transparent: true, opacity: 0.62, toneMapped: false, depthWrite: false },
              sweep: 0.35,
            })}
          </group>
        );
      })}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.135, 0.88, 0.02]}>
          <mesh rotation={[0.2, 0, sd * 0.55]}><capsuleGeometry args={[0.026, 0.18, 4, 8]} /><meshStandardMaterial {...skin} /></mesh>
          <mesh position={[sd * 0.075, -0.155, 0.03]}><sphereGeometry args={[0.030, 8, 8]} /><meshStandardMaterial {...skin} /></mesh>
          {[-1, 0, 1].map((c) => (
            <mesh key={c} position={[sd * 0.085 + c * 0.014, -0.192, 0.038]} rotation={[0.6, 0, 0]}>
              <coneGeometry args={[0.007, 0.032, 4]} /><meshStandardMaterial color={MAL.horn} roughness={0.4} />
            </mesh>
          ))}
        </group>
      ))}
      {/* the barbed tail */}
      <group position={[0, 0.44, -0.14]}>
        <Chain refs={tail} n={5} step={[0, -0.055, -0.075]} render={(i) => (
          <mesh position={[0, -0.028, -0.038]}>
            <capsuleGeometry args={[0.019 - i * 0.003, 0.06, 3, 6]} /><meshStandardMaterial {...skin} />
          </mesh>
        )} />
        <mesh position={[0, -0.30, -0.34]} rotation={[-0.6, 0, 0]}>
          <coneGeometry args={[0.030, 0.090, 4]} /><meshStandardMaterial color={MAL.horn} roughness={0.4} metalness={0.4} />
        </mesh>
      </group>
      {Aura({ r: 0.58, color: MAL.coal, opacity: 0.09, y: 0.74 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉓ TWINKLING AEALIX · Space · Humanoid · Lyf 1 / Lyt 7
// ════════════════════════════════════════════════════════════
//  "Built on a lie. They are not the power of the sun — their
//   brightness isn't from suns and sun creation, it's from
//   DESTRUCTION and collisions of particles."
//  ⚑ The lie is the model. From a distance it is a small sun: a
//  luminous body inside a corona. Up close there is no sun in it
//  at all — the light comes from a dozen hard little masses
//  orbiting inside the shell and SMASHING into each other, each
//  collision flashing and then dimming. Lyf 1, so the shell is
//  nearly empty; there is barely a being in the middle of it.
const AEA = { shell: "#e8f0ff", flash: "#ffffff", cold: "#8a5aff", core: "#2a2050" };
export function AealixBody({ pal }) {
  pal = pal || getSpeciesPalette("aealix");
  const face = faceTex("aea_face_v1", "#dfe6ff", "#8a5aff", "#ffffff", "rgba(90,60,160,0.6)", { blank: true });
  const rig = useBeingRig();
  const parts = useRef([]); const flashes = useRef([]); const corona = useRef();
  // seeded phase constants, baked once — never a PRNG in render
  const seeds = useMemo(() => {
    const R = mulberry32(0xAEA111);
    return Array.from({ length: 12 }).map(() => ({ a: R() * 6.28, b: R() * 6.28, r: 0.14 + R() * 0.16, s: 0.6 + R() * 1.5 }));
  }, []);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    parts.current.forEach((m, i) => {
      if (!m) return;
      const q = seeds[i];
      m.position.set(
        Math.cos(t * q.s + q.a) * q.r,
        0.98 + Math.sin(t * q.s * 1.3 + q.b) * q.r,
        Math.sin(t * q.s * 0.8 + q.a * 2) * q.r
      );
    });
    flashes.current.forEach((m, i) => {
      if (!m || !m.material) return;
      // each "collision" is a fixed-phase flash that decays
      const f = (t * (0.9 + i * 0.21) + i * 0.37) % 1;
      m.material.opacity = Math.pow(1 - f, 5) * 0.9;
      m.scale.setScalar(0.4 + f * 2.2);
      const q = seeds[i];
      m.position.set(Math.cos(q.a + i) * q.r * 0.8, 0.98 + Math.sin(q.b) * q.r * 0.8, Math.sin(q.a * 2) * q.r * 0.8);
    });
    if (corona.current) { corona.current.rotation.y = t * 0.18; corona.current.rotation.x = Math.sin(t * 0.5) * 0.12; }
  });
  return (
    <group>
      {/* the being: a slight, hollow figure with almost nothing in it */}
      <group ref={rig.torso}>
        <mesh position={[0, 0.86, 0]} scale={[0.66, 1.20, 0.66]}>
          <capsuleGeometry args={[0.115, 0.34, 5, 12]} />
          <meshStandardMaterial color={AEA.core} roughness={0.5} metalness={0.35}
            transparent opacity={0.62} emissive={AEA.cold} emissiveIntensity={0.35} />
        </mesh>
        <mesh position={[0, 0.50, 0]} scale={[1, 0.9, 1]}>
          <coneGeometry args={[0.165, 0.42, 12]} />
          <meshStandardMaterial color={AEA.core} roughness={0.55} transparent opacity={0.55} emissive={AEA.cold} emissiveIntensity={0.25} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 1.24, 0]}>
        <mesh><sphereGeometry args={[0.105, 14, 12]} />
          <meshStandardMaterial map={face} roughness={0.35} metalness={0.2} transparent opacity={0.72}
            emissive={AEA.shell} emissiveIntensity={0.30} /></mesh>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.155, 1.00, 0]}>
          <mesh position={[0, -0.16, 0]} rotation={[0, 0, sd * 0.22]}>
            <capsuleGeometry args={[0.026, 0.24, 4, 8]} />
            <meshStandardMaterial color={AEA.core} roughness={0.5} transparent opacity={0.6} emissive={AEA.cold} emissiveIntensity={0.3} />
          </mesh>
        </group>
      ))}
      {/* THE COLLIDING MASSES — this is where the light comes from */}
      {seeds.map((_, i) => (
        <mesh key={"p" + i} ref={(el) => (parts.current[i] = el)}>
          <sphereGeometry args={[0.016, 8, 8]} />
          <meshStandardMaterial color="#3a3550" metalness={0.9} roughness={0.3} emissive={AEA.cold} emissiveIntensity={0.4} />
        </mesh>
      ))}
      {seeds.slice(0, 8).map((_, i) => (
        <mesh key={"f" + i} ref={(el) => (flashes.current[i] = el)}>
          <sphereGeometry args={[0.030, 8, 8]} />
          <meshBasicMaterial color={AEA.flash} transparent opacity={0} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {/* THE CORONA — the lie, seen from a distance */}
      <group ref={corona} position={[0, 0.98, 0]}>
        <mesh><sphereGeometry args={[0.36, 16, 12]} />
          <meshBasicMaterial color={AEA.shell} transparent opacity={0.10} toneMapped={false} depthWrite={false} /></mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} rotation={[i * 1.1, i * 0.7, 0]}>
            <torusGeometry args={[0.38 + i * 0.03, 0.004, 5, 40]} />
            <meshBasicMaterial color={AEA.flash} transparent opacity={0.30} toneMapped={false} />
          </mesh>
        ))}
      </group>
      {Aura({ r: 0.72, color: pal.glow, opacity: 0.10, y: 0.94 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉔ TWINKLING AEOLIX · Space · Humanoid · Lyf 3 / Lyt 5
// ════════════════════════════════════════════════════════════
//  "Niloxia, worship of X'Aol — somehow a difference in worship
//   was reversed, and so Aeolix represented X'Aol as the MOON
//   and the COLD, even though O'mnia is fire and the sun."
//  The whole species is a contradiction held on purpose, so the
//  model is one too: the body is genuinely burning (O'mnia fire
//  in the chest, visible), and every bit of it that shows is
//  FROZEN — rime on the shoulders, breath fogging, a moon-disc
//  worn at the back of the head where a sun-disc should be.
const AEO = { ice: "#8ad8ff", rime: "#e8f8ff", fire: "#ffb02e", robe: "#123a5a", moon: "#dfeaf5" };
export function AeolixBody({ pal }) {
  pal = pal || getSpeciesPalette("aeolix");
  const robe = clothTex("aeo_robe_v1", "#2a6a9a", "#123a5a", "#061826", "rgba(232,248,255,0.7)");
  const ice = crystalTex("aeo_ice_v1", "#cfeaff", "#7fb8e0", "#ffffff");
  const face = faceTex("aeo_face_v1", "#cfe8fa", "#5a8ab0", "#ffb02e", "rgba(60,110,150,0.8)");
  const rig = useBeingRig();
  const fog = useRef([]); const moon = useRef(); const heart = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (moon.current) moon.current.rotation.z = t * 0.14;
    if (heart.current) heart.current.scale.setScalar(1 + Math.sin(t * 1.9) * 0.13);
    fog.current.forEach((m, i) => {
      if (!m) return;
      const f = (t * 0.45 + i * 0.28) % 1;
      m.position.set(Math.sin(i * 2.1) * 0.05, 1.08 - f * 0.10, 0.14 + f * 0.42);
      m.scale.setScalar(0.35 + f * 1.7);
      if (m.material) m.material.opacity = 0.22 * (1 - f);
    });
  });
  const cloth = { map: robe, roughness: 0.9, metalness: 0.08 };
  const frost = { map: ice, roughness: 0.16, metalness: 0.2, transparent: true, opacity: 0.72, emissive: AEO.ice, emissiveIntensity: 0.35 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.52, 0]} castShadow><coneGeometry args={[0.235, 0.98, 16]} /><meshStandardMaterial {...cloth} /></mesh>
        <mesh position={[0, 0.98, 0]} scale={[1.05, 0.82, 0.90]}>
          <sphereGeometry args={[0.185, 12, 10]} /><meshStandardMaterial {...cloth} />
        </mesh>
        {/* THE FIRE, inside a frozen thing */}
        <mesh ref={heart} position={[0, 0.82, 0.10]}>
          <sphereGeometry args={[0.070, 12, 10]} /><meshBasicMaterial color={AEO.fire} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.82, 0.10]}><sphereGeometry args={[0.125, 12, 10]} />
          <meshBasicMaterial color={AEO.fire} transparent opacity={0.20} toneMapped={false} depthWrite={false} /></mesh>
        {/* RIME on the shoulders — the contradiction, worn */}
        {[-1, 1].map((sd) => (
          <group key={sd} position={[sd * 0.185, 1.02, 0]}>
            <mesh scale={[1.1, 0.7, 1.1]}><sphereGeometry args={[0.098, 10, 8]} /><meshStandardMaterial {...frost} /></mesh>
            {[0, 1, 2].map((i) => (
              <mesh key={i} position={[sd * (0.02 + i * 0.028), 0.055 + i * 0.020, -0.02 + i * 0.03]}
                rotation={[0.4, 0, sd * (0.3 + i * 0.2)]}>
                <coneGeometry args={[0.016 - i * 0.003, 0.090, 5]} /><meshStandardMaterial {...frost} />
              </mesh>
            ))}
          </group>
        ))}
      </group>
      <group ref={rig.head} position={[0, 1.24, 0.01]}>
        <mesh scale={[0.94, 1.0, 0.96]}><sphereGeometry args={[0.128, 14, 12]} />
          <meshStandardMaterial map={face} roughness={0.62} /></mesh>
        <mesh position={[0, 0.06, -0.02]} scale={[1.08, 0.9, 1.08]}>
          <sphereGeometry args={[0.120, 12, 10]} /><meshStandardMaterial {...cloth} />
        </mesh>
        {/* THE MOON DISC, worn where a sun disc belongs */}
        <group ref={moon} position={[0, 0.055, -0.11]}>
          <mesh><circleGeometry args={[0.215, 28]} />
            <meshBasicMaterial color={AEO.moon} transparent opacity={0.88} side={THREE.DoubleSide} toneMapped={false} /></mesh>
          <mesh position={[0, 0, -0.004]}><circleGeometry args={[0.238, 28]} />
            <meshBasicMaterial color={AEO.ice} transparent opacity={0.30} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} /></mesh>
          {[0, 1, 2, 3].map((i) => (
            <mesh key={i} position={[Math.cos(i * 1.9) * 0.09, Math.sin(i * 2.3) * 0.09, 0.003]}>
              <circleGeometry args={[0.022 + (i % 2) * 0.012, 10]} />
              <meshBasicMaterial color="#b8c8d8" transparent opacity={0.8} toneMapped={false} />
            </mesh>
          ))}
        </group>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.215, 0.98, 0.02]}>
          <mesh rotation={[0.45, 0, sd * 0.30]}><capsuleGeometry args={[0.050, 0.28, 4, 10]} /><meshStandardMaterial {...cloth} /></mesh>
          <mesh position={[sd * 0.04, -0.25, 0.10]}><sphereGeometry args={[0.046, 10, 8]} /><meshStandardMaterial {...frost} /></mesh>
        </group>
      ))}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={"fg" + i} ref={(el) => (fog.current[i] = el)}>
          <sphereGeometry args={[0.048, 8, 8]} />
          <meshBasicMaterial color={AEO.rime} transparent opacity={0.2} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.80, color: pal.glow, opacity: 0.07, y: 0.90, scale: [1, 1.15, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉕ CALYPSIS · Space · Humanoid · Lyf 4 / Lyt 4
// ════════════════════════════════════════════════════════════
//  "Mermaid", Lunaea + Solaea. Both schools at once is the
//  design: it is split at the WATERLINE, and the line is real —
//  above it the body is sun-warm and dry, below it the tail is
//  moon-cold and wet, and a permanent band of surf sits exactly
//  where the two meet. It does not have legs and does not want
//  any; the fluke hangs and sculls, and the whole being floats a
//  hand's width off whatever it is over.
const CAL = { skinHi: "#ffd8a8", scale: "#2ec8b4", scaleDeep: "#08383a", surf: "#e8fffa", moon: "#a8ffe8" };
export function CalypsisBody({ pal }) {
  pal = pal || getSpeciesPalette("calypsis");
  const tailTex = scaleTex("cal_scale_v1", "#5ae8cc", "#189a92", "#052a2c", "rgba(168,255,232,0.7)", 19);
  const face = faceTex("cal_face_v1", "#ffe0b8", "#8a5a3a", "#2ec8b4", "rgba(180,90,90,0.85)");
  const rig = useBeingRig();
  const tail = useRef([]); const surf = useRef([]); const hair = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    tail.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.x = Math.sin(t * 1.55 - i * 0.75) * (0.10 + i * 0.06);
      m.rotation.z = Math.sin(t * 1.1 - i * 0.5) * 0.05;
    });
    hair.current.forEach((m, i) => { if (m) m.rotation.x = Math.sin(t * 0.9 + i * 0.7) * 0.16; });
    surf.current.forEach((m, i) => {
      if (!m) return;
      const a = t * 0.7 + (i / 7) * Math.PI * 2;
      m.position.set(Math.cos(a) * 0.20, 0.60 + Math.sin(t * 2.1 + i) * 0.018, Math.sin(a) * 0.20);
      m.scale.setScalar(0.8 + Math.sin(t * 1.7 + i * 1.4) * 0.28);
    });
  });
  const skin = { color: CAL.skinHi, roughness: 0.7, metalness: 0.05, emissive: "#ffb06a", emissiveIntensity: 0.10 };
  const scales = { map: tailTex, roughness: 0.34, metalness: 0.55, emissive: CAL.moon, emissiveIntensity: 0.22 };
  return (
    <group>
      {/* ABOVE THE LINE — sun */}
      <group ref={rig.torso}>
        <mesh position={[0, 0.86, 0]} scale={[0.86, 1.0, 0.72]} castShadow>
          <sphereGeometry args={[0.165, 14, 12]} /><meshStandardMaterial {...skin} />
        </mesh>
        <mesh position={[0, 0.68, 0]} scale={[0.72, 0.86, 0.66]}>
          <sphereGeometry args={[0.140, 12, 10]} /><meshStandardMaterial {...skin} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 1.12, 0.01]}>
        <mesh scale={[0.94, 1.02, 0.96]}><sphereGeometry args={[0.120, 14, 12]} /><meshStandardMaterial map={face} roughness={0.66} /></mesh>
        {/* the hair is kelp — it moves like it is still underwater */}
        {Array.from({ length: 9 }).map((_, i) => {
          const a = (i / 9) * Math.PI * 2;
          return (
            <group key={i} ref={(el) => (hair.current[i] = el)} position={[Math.cos(a) * 0.10, 0.070, Math.sin(a) * 0.10 - 0.02]}>
              <mesh position={[0, -0.16, -0.03]} rotation={[0.2, 0, Math.cos(a) * 0.3]}>
                <capsuleGeometry args={[0.018, 0.26, 3, 6]} />
                <meshStandardMaterial color="#0d5a54" roughness={0.6} metalness={0.3} emissive={CAL.moon} emissiveIntensity={0.18} />
              </mesh>
            </group>
          );
        })}
        {/* the fin-ears */}
        {[-1, 1].map((sd) => (
          <mesh key={sd} position={[sd * 0.115, 0.010, -0.020]} rotation={[0, sd * 0.5, sd * 0.7]} scale={[1, 0.12, 1]}>
            <coneGeometry args={[0.062, 0.170, 3]} />
            <meshStandardMaterial {...scales} transparent opacity={0.85} side={THREE.DoubleSide} />
          </mesh>
        ))}
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.170, 0.94, 0]}>
          <mesh position={[0, -0.14, 0]} rotation={[0, 0, sd * 0.25]}>
            <capsuleGeometry args={[0.032, 0.22, 4, 8]} /><meshStandardMaterial {...skin} />
          </mesh>
          <mesh position={[sd * 0.055, -0.29, 0.02]}><sphereGeometry args={[0.036, 10, 8]} /><meshStandardMaterial {...skin} /></mesh>
        </group>
      ))}
      {/* THE WATERLINE — a real band, and it never moves off her */}
      <mesh position={[0, 0.60, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.180, 0.030, 8, 24]} />
        <meshBasicMaterial color={CAL.surf} transparent opacity={0.34} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <mesh key={"sf" + i} ref={(el) => (surf.current[i] = el)}>
          <sphereGeometry args={[0.048, 8, 8]} />
          <meshBasicMaterial color={CAL.surf} transparent opacity={0.24} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {/* BELOW THE LINE — moon */}
      <group position={[0, 0.56, 0]}>
        <Chain refs={tail} n={5} step={[0, -0.105, -0.020]} render={(i) => (
          <mesh position={[0, -0.055, 0]} scale={[1 - i * 0.09, 1, 0.86 - i * 0.06]}>
            <capsuleGeometry args={[0.115 - i * 0.016, 0.075, 4, 12]} /><meshStandardMaterial {...scales} />
          </mesh>
        )} />
        {/* the fluke */}
        <group position={[0, -0.56, -0.09]}>
          {[-1, 1].map((sd) => (
            <mesh key={sd} position={[sd * 0.085, -0.055, -0.02]} rotation={[0.5, 0, sd * 0.9]} scale={[1, 0.10, 1]}>
              <coneGeometry args={[0.090, 0.26, 3]} />
              <meshStandardMaterial {...scales} side={THREE.DoubleSide} transparent opacity={0.9} />
            </mesh>
          ))}
        </group>
      </group>
      {Aura({ r: 0.72, color: pal.glow, opacity: 0.08, y: 0.72, scale: [1, 1.2, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉖ CAELUMARI · Space · Humanoid · Lyf 5 / Lyt 3
// ════════════════════════════════════════════════════════════
//  Caelum and mare — sky and sea — "named together because a
//  Caelumari has never accepted that they are two different
//  things." So the being is one continuous volume that is a sky
//  at the top and an ocean at the bottom with NO seam anywhere,
//  and the proof is a flock of small birds that swim and a shoal
//  of small fish that fly, both circling the same body on the
//  same ring, indistinguishable at speed.
const CAE = { sky: "#4a9ad8", sea: "#0a3a5a", foam: "#f0e8c8", cloud: "#c8e8ff" };
export function CaelumariBody({ pal }) {
  pal = pal || getSpeciesPalette("caelumari");
  const grad = canvasTex("cae_grad_v1", 256, 512, (g, w, h) => {
    const b = g.createLinearGradient(0, 0, 0, h);
    b.addColorStop(0, "#a8e0ff"); b.addColorStop(0.34, "#4a9ad8");
    b.addColorStop(0.52, "#1a6a9a"); b.addColorStop(0.74, "#0a3a5a"); b.addColorStop(1, "#04182a");
    g.fillStyle = b; g.fillRect(0, 0, w, h);
    const R = mulberry32(0xCAE001);
    for (let i = 0; i < 40; i++) { // clouds up top
      g.fillStyle = `rgba(240,248,255,${0.10 + R() * 0.22})`;
      g.beginPath(); g.ellipse(R() * w, R() * h * 0.4, 20 + R() * 50, 8 + R() * 14, 0, 0, 7); g.fill();
    }
    for (let i = 0; i < 90; i++) { // caustics down below
      g.strokeStyle = `rgba(200,240,255,${0.06 + R() * 0.14})`; g.lineWidth = 1 + R() * 2;
      const y = h * 0.55 + R() * h * 0.45;
      g.beginPath(); g.moveTo(0, y); g.bezierCurveTo(w * 0.3, y - 14, w * 0.6, y + 14, w, y); g.stroke();
    }
  });
  const face = faceTex("cae_face_v1", "#7ab8e0", "#0a3a5a", "#f0e8c8", "rgba(10,40,60,0.8)");
  const rig = useBeingRig();
  const flock = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    flock.current.forEach((m, i) => {
      if (!m) return;
      const a = t * (0.55 + (i % 3) * 0.10) + (i / 10) * Math.PI * 2;
      const rr = 0.34 + (i % 4) * 0.05;
      m.position.set(Math.cos(a) * rr, 0.50 + (i % 5) * 0.16 + Math.sin(t * 1.2 + i) * 0.04, Math.sin(a) * rr);
      m.rotation.y = -a + Math.PI / 2;
      m.rotation.z = Math.sin(t * 2 + i) * 0.30;
    });
  });
  const body = { map: grad, roughness: 0.52, metalness: 0.18, emissive: CAE.sky, emissiveIntensity: 0.10 };
  return (
    <group>
      <group ref={rig.torso}>
        {/* ONE volume, no waist, no hem — a column of weather */}
        <mesh position={[0, 0.62, 0]} scale={[0.92, 1.42, 0.92]} castShadow>
          <capsuleGeometry args={[0.200, 0.42, 6, 16]} /><meshStandardMaterial {...body} />
        </mesh>
        <mesh position={[0, 0.22, 0]} scale={[1.20, 0.62, 1.20]}>
          <sphereGeometry args={[0.215, 14, 10]} /><meshStandardMaterial {...body} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 1.20, 0.01]}>
        <mesh scale={[0.96, 1.0, 0.96]}><sphereGeometry args={[0.140, 14, 12]} /><meshStandardMaterial map={face} roughness={0.55} metalness={0.15} /></mesh>
        {/* a horizon line across the face, because of course */}
        <mesh position={[0, 0.006, 0.128]} rotation={[0, 0, 0]}>
          <planeGeometry args={[0.22, 0.006]} />
          <meshBasicMaterial color={CAE.foam} transparent opacity={0.7} toneMapped={false} />
        </mesh>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.195, 0.96, 0]}>
          <mesh position={[0, -0.16, 0]} rotation={[0, 0, sd * 0.20]}>
            <capsuleGeometry args={[0.044, 0.26, 4, 10]} /><meshStandardMaterial {...body} />
          </mesh>
          <mesh position={[sd * 0.055, -0.33, 0.02]}><sphereGeometry args={[0.048, 10, 8]} /><meshStandardMaterial {...body} /></mesh>
        </group>
      ))}
      {/* THE FLOCK/SHOAL — you cannot tell which is which */}
      {Array.from({ length: 10 }).map((_, i) => (
        <group key={"fk" + i} ref={(el) => (flock.current[i] = el)}>
          <mesh scale={[1, 0.42, 1.9]}>
            <sphereGeometry args={[0.026, 8, 6]} />
            <meshStandardMaterial color={i % 2 ? CAE.foam : CAE.cloud} roughness={0.5} metalness={0.3}
              emissive={CAE.foam} emissiveIntensity={0.25} />
          </mesh>
          <mesh position={[0, 0, -0.035]} rotation={[0, 0, Math.PI / 2]} scale={[1, 0.1, 1]}>
            <coneGeometry args={[0.022, 0.05, 3]} />
            <meshStandardMaterial color={i % 2 ? CAE.foam : CAE.cloud} roughness={0.5} side={THREE.DoubleSide} />
          </mesh>
        </group>
      ))}
      {Aura({ r: 0.80, color: pal.glow, opacity: 0.07, y: 0.66, scale: [1, 1.2, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉗ RADIANT ZRIIDIANS · Space · Humanoid · Lyf 6 / Lyt 2
// ════════════════════════════════════════════════════════════
//  Astraea + O'mnia, and the opposite of Hyalayri: where one is
//  CLEAR, the Zriidians are FILLED — and what they are filled
//  with is starlight. So the shell is opaque, heavy and matte
//  gold, and the only way you ever see what is inside is the
//  seams: it is packed so full that light is forced out of every
//  join in the armour. Lyf 6 — it is the most solid thing in the
//  Space school, and it is solid because of what it contains.
const ZRI = { plate: "#c8a04a", plateHi: "#ffd88a", seam: "#4ad8ff", deep: "#3a2c10" };
export function ZriidiansBody({ pal }) {
  pal = pal || getSpeciesPalette("zriidians");
  const plate = plateTex("zri_plate_v1", "#f0cc84", "#b8903c", "#4a3a14", "#fff0c8");
  const rig = useBeingRig();
  const seams = useRef([]); const crown = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    seams.current.forEach((m, i) => {
      if (!m || !m.material) return;
      m.material.opacity = 0.45 + Math.sin(t * 1.35 + i * 0.9) * 0.28;
    });
    if (crown.current) crown.current.rotation.y = t * 0.32;
  });
  const armour = { map: plate, roughness: 0.44, metalness: 0.88, emissive: ZRI.plateHi, emissiveIntensity: 0.08 };
  const seam = { color: ZRI.seam, transparent: true, opacity: 0.6, toneMapped: false, depthWrite: false };
  const SEAM = (key, args, pos, rot) => (
    <mesh key={key} ref={(el) => (seams.current[key] = el)} position={pos} rotation={rot}>
      <torusGeometry args={args} /><meshBasicMaterial {...seam} />
    </mesh>
  );
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 1.00, 0]} scale={[1.16, 0.94, 0.86]} castShadow>
          <sphereGeometry args={[0.245, 14, 12]} /><meshStandardMaterial {...armour} />
        </mesh>
        <mesh position={[0, 0.74, 0]} scale={[0.96, 0.86, 0.80]}>
          <sphereGeometry args={[0.215, 12, 10]} /><meshStandardMaterial {...armour} />
        </mesh>
        <mesh position={[0, 0.44, 0]} scale={[1, 1, 0.88]}>
          <coneGeometry args={[0.245, 0.48, 14]} /><meshStandardMaterial {...armour} />
        </mesh>
        {[0, 1, 2].map((i) => SEAM(i, [0.205 + i * 0.012, 0.010, 6, 28], [0, 0.86 - i * 0.16, 0], [Math.PI / 2, 0, 0]))}
        {/* the chest join, forced open a hair by what is inside */}
        <mesh ref={(el) => (seams.current[8] = el)} position={[0, 0.98, 0.20]}>
          <planeGeometry args={[0.028, 0.30]} /><meshBasicMaterial {...seam} opacity={0.75} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 1.38, 0.01]}>
        <mesh scale={[0.96, 1.02, 0.94]}><sphereGeometry args={[0.155, 14, 12]} /><meshStandardMaterial {...armour} /></mesh>
        {/* no face. A seam across where one would be. */}
        <mesh ref={(el) => (seams.current[9] = el)} position={[0, 0.006, 0.148]}>
          <planeGeometry args={[0.20, 0.020]} /><meshBasicMaterial {...seam} opacity={0.8} />
        </mesh>
        {SEAM(10, [0.150, 0.008, 6, 26], [0, -0.055, 0], [Math.PI / 2, 0, 0])}
        {/* THE CROWN — Astraea. Seven points, one per star. */}
        <group ref={crown} position={[0, 0.145, 0]}>
          {Array.from({ length: 7 }).map((_, i) => {
            const a = (i / 7) * Math.PI * 2;
            return (
              <mesh key={i} position={[Math.cos(a) * 0.105, 0.045, Math.sin(a) * 0.105]} rotation={[0.22 * Math.sin(a), -a, 0.22 * Math.cos(a)]}>
                <coneGeometry args={[0.020, 0.115, 4]} /><meshStandardMaterial {...armour} emissive={ZRI.seam} emissiveIntensity={0.4} />
              </mesh>
            );
          })}
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.105, 0.012, 6, 22]} /><meshStandardMaterial {...armour} />
          </mesh>
        </group>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.290, 1.04, 0]}>
          <mesh position={[sd * 0.02, 0.02, 0]} scale={[1.1, 0.86, 1.1]}>
            <sphereGeometry args={[0.098, 10, 8]} /><meshStandardMaterial {...armour} />
          </mesh>
          <mesh position={[0, -0.18, 0]} rotation={[0, 0, sd * 0.12]}>
            <capsuleGeometry args={[0.058, 0.26, 4, 10]} /><meshStandardMaterial {...armour} />
          </mesh>
          <mesh position={[sd * 0.03, -0.38, 0.02]}><sphereGeometry args={[0.060, 10, 8]} /><meshStandardMaterial {...armour} /></mesh>
          <mesh ref={(el) => (seams.current[sd < 0 ? 11 : 12] = el)} position={[0, -0.055, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.062, 0.007, 5, 16]} /><meshBasicMaterial {...seam} />
          </mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.110, 0.34, 0]}>
          <mesh position={[0, -0.14, 0]}><capsuleGeometry args={[0.066, 0.18, 4, 10]} /><meshStandardMaterial {...armour} /></mesh>
          <mesh position={[0, -0.30, 0.030]} scale={[1, 0.62, 1.5]}><sphereGeometry args={[0.072, 10, 8]} /><meshStandardMaterial {...armour} /></mesh>
        </group>
      ))}
      {Aura({ r: 0.84, color: ZRI.seam, opacity: 0.06, y: 0.92, scale: [1, 1.1, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉘ RADIANT HYALAYRI · Space · Humanoid · Lyf 2 / Lyt 6
// ════════════════════════════════════════════════════════════
//  Infinaite + N'ihil, "opposite of Zriidians." Hyal- is glass.
//  So this is the same humanoid frame as the Zriidian and it is
//  the exact inversion: no plate, no seams, no crown, nothing
//  hidden — a body you can see straight through, with only the
//  refraction to tell you it is there at all. The one solid
//  thing in it is a small dark knot at the sternum, which is the
//  only part of a Hyalayri that has never been transparent.
const HYA = { glass: "#c8e8f0", edge: "#8affe0", knot: "#0e1418" };
export function HyalayriBody({ pal }) {
  pal = pal || getSpeciesPalette("hyalayri");
  const facet = crystalTex("hya_facet_v1", "#e8f8fc", "#a8d8e8", "#ffffff");
  const rig = useBeingRig();
  const knot = useRef(); const shards = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (knot.current) { knot.current.rotation.y = t * 0.8; knot.current.rotation.x = t * 0.44; }
    shards.current.forEach((m, i) => {
      if (!m) return;
      const a = t * 0.38 + (i / 7) * Math.PI * 2;
      m.position.set(Math.cos(a) * 0.34, 0.96 + Math.sin(t * 0.9 + i) * 0.14, Math.sin(a) * 0.34);
      m.rotation.set(t * 0.5 + i, a, t * 0.3);
    });
  });
  const glass = {
    map: facet, color: HYA.glass, roughness: 0.06, metalness: 0.12,
    transparent: true, opacity: 0.30, emissive: HYA.edge, emissiveIntensity: 0.22,
  };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 1.00, 0]} scale={[1.02, 0.96, 0.78]}>
          <sphereGeometry args={[0.220, 14, 12]} /><meshStandardMaterial {...glass} />
        </mesh>
        <mesh position={[0, 0.74, 0]} scale={[0.86, 0.88, 0.72]}>
          <sphereGeometry args={[0.190, 12, 10]} /><meshStandardMaterial {...glass} />
        </mesh>
        <mesh position={[0, 0.44, 0]} scale={[1, 1, 0.84]}>
          <coneGeometry args={[0.215, 0.46, 12]} /><meshStandardMaterial {...glass} />
        </mesh>
        {/* THE KNOT — the one part that was never clear */}
        <mesh ref={knot} position={[0, 1.00, 0.03]}>
          <icosahedronGeometry args={[0.058, 0]} />
          <meshStandardMaterial color={HYA.knot} roughness={0.35} metalness={0.7} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 1.36, 0.01]}>
        <mesh scale={[0.94, 1.04, 0.94]}><sphereGeometry args={[0.145, 16, 12]} /><meshStandardMaterial {...glass} /></mesh>
        {/* an internal facet cluster where a brain would be —
            visible only because you can see into the head */}
        <mesh rotation={[0.4, 0.3, 0]}>
          <icosahedronGeometry args={[0.082, 0]} />
          <meshStandardMaterial color={HYA.edge} roughness={0.1} metalness={0.3} transparent opacity={0.35} emissive={HYA.edge} emissiveIntensity={0.45} />
        </mesh>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.250, 1.02, 0]}>
          <mesh position={[0, -0.17, 0]} rotation={[0, 0, sd * 0.14]}>
            <capsuleGeometry args={[0.046, 0.26, 4, 10]} /><meshStandardMaterial {...glass} />
          </mesh>
          <mesh position={[sd * 0.03, -0.36, 0.02]}><icosahedronGeometry args={[0.052, 0]} /><meshStandardMaterial {...glass} opacity={0.42} /></mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.100, 0.34, 0]}>
          <mesh position={[0, -0.14, 0]}><capsuleGeometry args={[0.054, 0.18, 4, 10]} /><meshStandardMaterial {...glass} /></mesh>
          <mesh position={[0, -0.30, 0.026]} scale={[1, 0.6, 1.5]}><sphereGeometry args={[0.062, 10, 8]} /><meshStandardMaterial {...glass} /></mesh>
        </group>
      ))}
      {/* seven loose facets it has shed and never lost */}
      {Array.from({ length: 7 }).map((_, i) => (
        <mesh key={"sh" + i} ref={(el) => (shards.current[i] = el)}>
          <tetrahedronGeometry args={[0.034, 0]} />
          <meshStandardMaterial color={HYA.glass} roughness={0.05} metalness={0.2} transparent opacity={0.45}
            emissive={HYA.edge} emissiveIntensity={0.5} />
        </mesh>
      ))}
      {Aura({ r: 0.78, color: pal.glow, opacity: 0.055, y: 0.92, scale: [1, 1.1, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉙ RIVEN · Space · Humanoid · Lyf 3 / Lyt 5
// ════════════════════════════════════════════════════════════
//  Arcane Art + Astraea, "enemies to Kraalok." Kraalok is a
//  species that puts EVERYTHING into one purpose; a Riven is the
//  opposite argument — it is genuinely two pieces, split down the
//  midline, held apart by the same force that holds them
//  together. The gap is the character: you can see daylight
//  through the middle of a Riven, and the two halves never quite
//  agree about where to be, drifting a few millimetres and
//  correcting all day.
const RIV = { skinA: "#8a6ad8", skinB: "#c8a0ff", rift: "#ff5a8a", star: "#ffe8a8" };
export function RivenBody({ pal }) {
  pal = pal || getSpeciesPalette("riven");
  const hide = hideTex("riv_hide_v1", "#a88aff", "#6a4ab8", "#1a1030", { speck: "rgba(255,232,168,0.55)" });
  const rig = useBeingRig();
  const halfL = useRef(); const halfR = useRef(); const rift = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the halves disagree, and correct, and disagree again
    if (halfL.current) { halfL.current.position.x = -0.030 - Math.sin(t * 0.85) * 0.014; halfL.current.rotation.z = Math.sin(t * 0.62) * 0.035; }
    if (halfR.current) { halfR.current.position.x = 0.030 + Math.sin(t * 0.85 + 0.7) * 0.014; halfR.current.rotation.z = -Math.sin(t * 0.62 + 0.4) * 0.035; }
    rift.current.forEach((m, i) => {
      if (!m || !m.material) return;
      m.material.opacity = 0.35 + Math.sin(t * 2.4 + i * 1.1) * 0.25;
      m.scale.y = 1 + Math.sin(t * 1.7 + i) * 0.12;
    });
  });
  const skin = { map: hide, roughness: 0.66, metalness: 0.2, emissive: RIV.skinA, emissiveIntensity: 0.12 };
  // one half, mirrored — the geometry is authored ONCE
  const Half = ({ sd }) => (
    <group>
      <mesh position={[sd * 0.085, 1.00, 0]} scale={[0.52, 0.96, 0.80]}>
        <sphereGeometry args={[0.215, 12, 12]} /><meshStandardMaterial {...skin} />
      </mesh>
      <mesh position={[sd * 0.075, 0.72, 0]} scale={[0.46, 0.86, 0.74]}>
        <sphereGeometry args={[0.190, 10, 10]} /><meshStandardMaterial {...skin} />
      </mesh>
      <mesh position={[sd * 0.090, 0.42, 0]} scale={[0.52, 1, 0.86]}>
        <coneGeometry args={[0.180, 0.46, 10]} /><meshStandardMaterial {...skin} />
      </mesh>
      <mesh position={[sd * 0.255, 1.02, 0]} rotation={[0, 0, sd * 0.16]}>
        <capsuleGeometry args={[0.046, 0.30, 4, 10]} /><meshStandardMaterial {...skin} />
      </mesh>
      <mesh position={[sd * 0.290, 0.80, 0.02]}><sphereGeometry args={[0.050, 10, 8]} /><meshStandardMaterial {...skin} /></mesh>
      <mesh position={[sd * 0.105, 0.20, 0]}><capsuleGeometry args={[0.056, 0.20, 4, 10]} /><meshStandardMaterial {...skin} /></mesh>
      <mesh position={[sd * 0.105, 0.045, 0.028]} scale={[1, 0.6, 1.5]}><sphereGeometry args={[0.064, 10, 8]} /><meshStandardMaterial {...skin} /></mesh>
      {/* half a head, and half a face */}
      <mesh position={[sd * 0.070, 1.34, 0]} scale={[0.50, 1.0, 0.94]}>
        <sphereGeometry args={[0.145, 12, 12]} /><meshStandardMaterial {...skin} />
      </mesh>
      <mesh position={[sd * 0.078, 1.36, 0.118]}>
        <circleGeometry args={[0.030, 16]} />
        <meshBasicMaterial color={RIV.star} toneMapped={false} />
      </mesh>
    </group>
  );
  return (
    <group>
      <group ref={rig.torso}>
        {/* ⚑ THE ARM SLOTS ARE DELIBERATELY LEFT UNBOUND. A rig slot
            wrapped around half a Riven is not an arm — a host game's
            limb solver would swing the entire left side of the being
            at a steering wheel. There is nothing here to grip with,
            and that is the correct answer, not a missing one. */}
        <group ref={halfL}><Half sd={-1} /></group>
        <group ref={halfR}><Half sd={1} /></group>
      </group>
      <group ref={rig.head} position={[0, 0, 0]} />
      {/* THE RIFT — the force doing the holding, made visible */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={"rf" + i} ref={(el) => (rift.current[i] = el)} position={[0, 0.34 + i * 0.21, 0]}>
          <planeGeometry args={[0.055, 0.185]} />
          <meshBasicMaterial color={RIV.rift} transparent opacity={0.4} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      <mesh position={[0, 0.86, 0]}>
        <planeGeometry args={[0.030, 1.30]} />
        <meshBasicMaterial color={RIV.star} transparent opacity={0.30} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {Aura({ r: 0.80, color: pal.glow, opacity: 0.06, y: 0.86, scale: [1, 1.15, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉚ DEVORIX · Space · Humanoid · Lyf 5 / Lyt 3
// ════════════════════════════════════════════════════════════
//  Aeternal (Astraea) + Avarice. "There is no version of a
//  Devorix that is finished eating." Aeternal appetite is not a
//  mouth — a mouth can be full. So a Devorix has a WELL instead:
//  its torso opens on a hole with no back to it, ringed with
//  inward-facing teeth, and everything near it drifts toward the
//  opening and does not come out. It is otherwise a perfectly
//  ordinary, calm, well-dressed humanoid, which is worse.
const DVX = { coat: "#3a2a4a", well: "#050308", tooth: "#e8dcc0", pull: "#ff8a2e", skin: "#6a5a7a" };
export function DevorixBody({ pal }) {
  pal = pal || getSpeciesPalette("devorix");
  const cloth = clothTex("dvx_cloth_v1", "#5a4470", "#33253f", "#0e0a14", "rgba(138,226,60,0.55)");
  const face = faceTex("dvx_face_v1", "#7a6a8a", "#241830", "#8ae23c", "rgba(20,10,26,0.85)", { frown: false });
  const rig = useBeingRig();
  const swirl = useRef([]); const ring = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (ring.current) ring.current.rotation.z = -t * 1.15;
    swirl.current.forEach((m, i) => {
      if (!m) return;
      // everything spirals IN and is not seen again
      const f = 1 - ((t * 0.55 + i * 0.16) % 1);
      const a = t * 2.4 + i * 1.3 + (1 - f) * 6;
      const rr = 0.05 + f * 0.42;
      m.position.set(Math.cos(a) * rr, 0.92 + Math.sin(a * 0.7) * rr * 0.6, 0.22 + f * 0.10);
      m.scale.setScalar(0.3 + f * 0.9);
      if (m.material) m.material.opacity = 0.75 * f;
    });
  });
  const coat = { map: cloth, roughness: 0.9, metalness: 0.08 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.98, 0]} scale={[1.10, 0.98, 0.82]} castShadow>
          <sphereGeometry args={[0.235, 14, 12]} /><meshStandardMaterial {...coat} />
        </mesh>
        <mesh position={[0, 0.50, 0]} castShadow><coneGeometry args={[0.245, 0.66, 14]} /><meshStandardMaterial {...coat} /></mesh>
        {/* THE WELL — an opening with nothing behind it */}
        <mesh position={[0, 0.94, 0.185]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.115, 0.055, 0.36, 18, 1, true]} />
          <meshStandardMaterial color={DVX.well} roughness={1} side={THREE.DoubleSide} />
        </mesh>
        <mesh position={[0, 0.94, 0.205]}>
          <circleGeometry args={[0.112, 20]} /><meshBasicMaterial color="#000000" toneMapped={false} />
        </mesh>
        <group ref={ring} position={[0, 0.94, 0.200]}>
          {Array.from({ length: 14 }).map((_, i) => {
            const a = (i / 14) * Math.PI * 2;
            return (
              <mesh key={i} position={[Math.cos(a) * 0.108, Math.sin(a) * 0.108, 0.006]} rotation={[0, 0, -a - Math.PI / 2]}>
                <coneGeometry args={[0.011, 0.046, 4]} /><meshStandardMaterial color={DVX.tooth} roughness={0.4} />
              </mesh>
            );
          })}
          <mesh><torusGeometry args={[0.126, 0.010, 6, 26]} /><meshStandardMaterial color={DVX.tooth} roughness={0.5} metalness={0.3} /></mesh>
        </group>
      </group>
      <group ref={rig.head} position={[0, 1.34, 0.01]}>
        <mesh scale={[0.94, 1.02, 0.94]}><sphereGeometry args={[0.140, 14, 12]} /><meshStandardMaterial map={face} roughness={0.72} /></mesh>
        {/* a very tidy collar. It is dressed for dinner. */}
        <mesh position={[0, -0.135, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.135, 0.030, 6, 20]} /><meshStandardMaterial {...coat} />
        </mesh>
        <mesh position={[0, 0.115, -0.01]} scale={[1.16, 0.44, 1.16]}>
          <sphereGeometry args={[0.130, 12, 10]} /><meshStandardMaterial {...coat} />
        </mesh>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.255, 1.02, 0]}>
          <mesh position={[0, -0.18, 0.02]} rotation={[0.2, 0, sd * 0.16]}>
            <capsuleGeometry args={[0.052, 0.28, 4, 10]} /><meshStandardMaterial {...coat} />
          </mesh>
          <mesh position={[sd * 0.02, -0.38, 0.08]}><sphereGeometry args={[0.052, 10, 8]} />
            <meshStandardMaterial color={DVX.skin} roughness={0.75} /></mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.100, 0.24, 0]}>
          <mesh position={[0, -0.10, 0]}><capsuleGeometry args={[0.056, 0.14, 4, 10]} /><meshStandardMaterial {...coat} /></mesh>
          <mesh position={[0, -0.22, 0.030]} scale={[1, 0.6, 1.6]}><sphereGeometry args={[0.062, 10, 8]} /><meshStandardMaterial color="#1a1220" roughness={0.6} metalness={0.3} /></mesh>
        </group>
      ))}
      {Array.from({ length: 9 }).map((_, i) => (
        <mesh key={"sw" + i} ref={(el) => (swirl.current[i] = el)}>
          <sphereGeometry args={[0.017, 6, 6]} />
          <meshBasicMaterial color={DVX.pull} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.82, color: pal.glow, opacity: 0.05, y: 0.90, scale: [1, 1.1, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉛ TWINKLING H-00 · Space · Humanoid · Lyf 4 / Lyt 4
// ════════════════════════════════════════════════════════════
//  The zeroth. Before H-01 chose diplomacy and H-09 chose
//  militancy there was one that HAD NOT CHOSEN, and per §4f the
//  cheapest strong character work available is to build it as the
//  third member of that pair — same suit, same helmet, same
//  collar ring — and let it disagree with both by having neither
//  prop. H-01 has an open hand; H-09 has a fist; H-00 has its
//  hands in front of it holding a blank slate, and its chest
//  readout is switched off. Perfectly balanced, 4/4, undecided.
const H00 = { suit: "#c8ccd8", plate: "#6a7080", off: "#2a2e36", live: "#4ad8ff" };
// ⚠ RESTORED 2026-09-16 — the Rush → Annihilation port (7291eab) carried the
// bodies that use this table but not the table itself, so the build failed
// no-undef and the body would have thrown on its first render. Verbatim
// from app-rush/src/ieTerrorSpecies.jsx.
const KRK = { flesh: "#6a8a5a", pore: "#1a2a12", steel: "#9aa0a8", hot: "#c8f08a", rod: "#5a4a3a" };
export function KraalokBody({ pal }) {
  pal = pal || getSpeciesPalette("kraalok");
  const sponge = hideTex("krk_sponge_v1", "#8aae72", "#4a6a3c", "#131c0e", {
    grain: 400,
    after: (g, w, h, R) => {
      // the pores ARE the texture — hundreds of dark wells
      for (let i = 0; i < 700; i++) {
        const x = R() * w, y = R() * h, rr = 2 + R() * 11;
        const s = g.createRadialGradient(x, y, 0.5, x, y, rr);
        s.addColorStop(0, "rgba(8,14,6,0.9)"); s.addColorStop(0.7, "rgba(20,32,14,0.55)"); s.addColorStop(1, "rgba(0,0,0,0)");
        g.fillStyle = s; g.beginPath(); g.arc(x, y, rr, 0, 7); g.fill();
      }
      for (let i = 0; i < 120; i++) {
        g.fillStyle = `rgba(200,240,138,${0.05 + R() * 0.12})`;
        g.beginPath(); g.arc(R() * w, R() * h, 1 + R() * 3, 0, 7); g.fill();
      }
    },
  });
  const rig = useBeingRig();
  const piston = useRef(); const squish = useRef(); const eyeR = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // ONE beat, dead regular, never varying. That is the purpose.
    const beat = Math.max(0, Math.sin(t * 2.0));
    if (piston.current) piston.current.position.z = 0.10 + Math.pow(beat, 3) * 0.30;
    if (squish.current) squish.current.scale.set(1 + beat * 0.05, 1 - beat * 0.06, 1 + beat * 0.05);
    if (eyeR.current) eyeR.current.rotation.z = Math.sin(t * 0.4) * 0.12;
  });
  const flesh = { map: sponge, roughness: 1, metalness: 0.0, emissive: KRK.hot, emissiveIntensity: 0.06 };
  const steel = { color: KRK.steel, roughness: 0.28, metalness: 0.95 };
  return (
    <group>
      <group ref={rig.torso}>
        <group ref={squish}>
          <mesh position={[0, 0.46, 0]} scale={[1.12, 1.0, 1.0]} castShadow>
            <sphereGeometry args={[0.320, 16, 14]} /><meshStandardMaterial {...flesh} />
          </mesh>
          <mesh position={[-0.16, 0.66, 0.06]} scale={[0.9, 0.9, 0.9]}>
            <sphereGeometry args={[0.185, 12, 10]} /><meshStandardMaterial {...flesh} />
          </mesh>
          <mesh position={[0.20, 0.58, -0.10]} scale={[0.9, 0.9, 0.9]}>
            <sphereGeometry args={[0.170, 12, 10]} /><meshStandardMaterial {...flesh} />
          </mesh>
          <mesh position={[0.02, 0.20, 0.14]} scale={[1.2, 0.7, 1.1]}>
            <sphereGeometry args={[0.235, 12, 10]} /><meshStandardMaterial {...flesh} />
          </mesh>
        </group>
      </group>
      {/* THE MECHANISM — hard, straight through, driven forever */}
      <group ref={rig.prop} position={[0, 0.48, 0]}>
        <mesh position={[0, 0, -0.24]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.086, 0.086, 0.30, 14]} /><meshStandardMaterial {...steel} />
        </mesh>
        <mesh position={[0, 0, -0.40]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.105, 0.105, 0.09, 16]} /><meshStandardMaterial {...steel} />
        </mesh>
        <group ref={piston}>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.036, 0.036, 0.62, 12]} />
            <meshStandardMaterial color="#e8ecf0" roughness={0.12} metalness={1.0} />
          </mesh>
          <mesh position={[0, 0, 0.34]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.062, 0.052, 0.075, 12]} /><meshStandardMaterial {...steel} />
          </mesh>
        </group>
        {/* the collar where the hard thing enters the soft thing */}
        <mesh position={[0, 0, 0.14]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.072, 0.018, 6, 18]} /><meshStandardMaterial color={KRK.rod} roughness={0.5} metalness={0.7} />
        </mesh>
      </group>
      {/* one eye, and it is on a stalk that never looks at the piston */}
      <group ref={rig.head} position={[-0.10, 0.80, 0.10]}>
        <group ref={eyeR}>
          <mesh position={[0, 0.10, 0]}><capsuleGeometry args={[0.026, 0.14, 4, 8]} /><meshStandardMaterial {...flesh} /></mesh>
          <mesh position={[0, 0.21, 0.020]}><sphereGeometry args={[0.070, 12, 10]} />
            <meshStandardMaterial color="#0f1a0a" roughness={0.35} metalness={0.4} /></mesh>
          <mesh position={[0, 0.21, 0.082]}>
            <circleGeometry args={[0.046, 18]} />
            <meshBasicMaterial map={eyeDisc("krk_eye_v1", "#eaffc8", "#8ae23c", "#1a2a06", "rgba(0,0,0,0.9)", false)} toneMapped={false} />
          </mesh>
        </group>
      </group>
      {/* it does not walk. It has four short props and shuffles. */}
      {[[-0.185, 0.155], [0.185, 0.155], [-0.185, -0.165], [0.185, -0.165]].map(([ox, oz], i) => (
        <group key={"lg" + i} ref={[rig.armL, rig.armR, rig.legL, rig.legR][i]} position={[ox, 0, oz]}>
          <mesh position={[0, 0.12, 0]}><capsuleGeometry args={[0.052, 0.10, 4, 8]} /><meshStandardMaterial {...flesh} /></mesh>
          <mesh position={[0, 0.045, 0.010]} scale={[1.2, 0.5, 1.3]}><sphereGeometry args={[0.058, 10, 8]} /><meshStandardMaterial color={KRK.pore} roughness={1} /></mesh>
        </group>
      ))}
      {Aura({ r: 0.80, color: pal.glow, opacity: 0.05, y: 0.46, scale: [1.1, 0.9, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉞ LYRIXI · Time · Machine · Lyf 2 / Lyt 6
// ════════════════════════════════════════════════════════════
//  "A realm of eternal twilight, skies painted sapphire and
//   amethyst, crystalline homes that RESONATE with the harmonies
//   of the universe... slender forms adorned in iridescent
//   scales... delicate wings that shimmer like stardust... their
//   society is a network of RESONANT FREQUENCIES."
//  So it is not a musician. It is an INSTRUMENT: a slender
//  crystalline body strung with seven tuned filaments that are
//  visibly vibrating at seven different rates, and the wings are
//  a stardust field rather than a membrane.
const LYX = { crys: "#6a5ad8", crysHi: "#a8ffe8", string: "#e8a8ff", dust: "#c8b0ff" };
export function LyrixiBody({ pal }) {
  pal = pal || getSpeciesPalette("lyrixi");
  const crys = crystalTex("lyx_crys_v1", "#8a78f0", "#3a2c90", "#a8ffe8");
  const rig = useBeingRig();
  const strings = useRef([]); const dust = useRef([]); const wings = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // seven strings, seven frequencies — the society, worn
    strings.current.forEach((m, i) => {
      if (!m) return;
      const f = 6 + i * 2.4;
      m.scale.x = 1 + Math.sin(t * f) * 0.55;
      if (m.material) m.material.opacity = 0.45 + Math.abs(Math.sin(t * f)) * 0.45;
    });
    dust.current.forEach((m, i) => {
      if (!m) return;
      const a = t * 0.4 + i * 0.9;
      m.position.set(Math.cos(a) * (0.28 + (i % 3) * 0.10), 0.60 + (i % 5) * 0.14 + Math.sin(t + i) * 0.05, Math.sin(a) * (0.22 + (i % 3) * 0.08));
      if (m.material) m.material.opacity = 0.30 + Math.sin(t * 2.6 + i * 1.4) * 0.28;
    });
    wings.current.forEach((m, i) => { if (m) m.rotation.z = (i % 2 ? 1 : -1) * (0.40 + Math.sin(t * 2.6 + i) * 0.16); });
  });
  const body = { map: crys, color: LYX.crys, roughness: 0.12, metalness: 0.42, transparent: true, opacity: 0.86, emissive: LYX.crysHi, emissiveIntensity: 0.28 };
  return (
    <group>
      {/* slender. Every mass on this body is narrow. */}
      <group ref={rig.torso}>
        <mesh position={[0, 0.86, 0]} scale={[0.62, 1.26, 0.52]} castShadow>
          <sphereGeometry args={[0.190, 12, 14]} /><meshStandardMaterial {...body} />
        </mesh>
        <mesh position={[0, 0.46, 0]} scale={[0.60, 1.0, 0.56]}>
          <coneGeometry args={[0.145, 0.52, 10]} /><meshStandardMaterial {...body} />
        </mesh>
        {/* THE SEVEN STRINGS — the instrument, and the society */}
        {Array.from({ length: 7 }).map((_, i) => (
          <mesh key={i} ref={(el) => (strings.current[i] = el)}
            position={[0, 0.62 + i * 0.070, 0.115]} rotation={[0, 0, 0]}>
            <planeGeometry args={[0.014, 0.062]} />
            <meshBasicMaterial color={LYX.string} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
          </mesh>
        ))}
        {[-1, 1].map((sd) => (
          <mesh key={"br" + sd} position={[sd * 0.088, 0.86, 0.10]}>
            <capsuleGeometry args={[0.011, 0.50, 3, 6]} />
            <meshStandardMaterial color={LYX.crysHi} roughness={0.1} metalness={0.6} emissive={LYX.crysHi} emissiveIntensity={0.5} />
          </mesh>
        ))}
      </group>
      <group ref={rig.head} position={[0, 1.26, 0]}>
        {/* the head is a tuned crystal — a bell, not a face */}
        <mesh scale={[0.80, 1.15, 0.80]}>
          <octahedronGeometry args={[0.135, 0]} /><meshStandardMaterial {...body} opacity={0.78} />
        </mesh>
        <mesh scale={[0.4, 0.7, 0.4]}>
          <octahedronGeometry args={[0.135, 0]} />
          <meshBasicMaterial color={LYX.crysHi} transparent opacity={0.55} toneMapped={false} />
        </mesh>
        {/* two lit facets read as eyes without being eyes */}
        {[-1, 1].map((sd) => (
          <mesh key={sd} position={[sd * 0.055, 0.020, 0.082]} rotation={[0, sd * 0.4, 0]}>
            <planeGeometry args={[0.028, 0.048]} />
            <meshBasicMaterial color={LYX.string} transparent opacity={0.85} toneMapped={false} />
          </mesh>
        ))}
      </group>
      {/* WINGS OF STARDUST — a field, not a sheet */}
      {[0, 1, 2, 3].map((i) => {
        const sd = i % 2 ? 1 : -1, up = i < 2;
        return (
          <group key={i} ref={(el) => (wings.current[i] = el)} position={[sd * 0.09, up ? 0.98 : 0.80, -0.06]}>
            <mesh rotation={[0, sd * 0.45, sd * 0.4]}>
              <circleGeometry args={[up ? 0.36 : 0.27, 16, 0, Math.PI]} />
              <meshBasicMaterial color={LYX.dust} transparent opacity={0.16} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
            </mesh>
            {Array.from({ length: 6 }).map((_, k) => (
              <mesh key={k} position={[sd * (0.06 + k * 0.05), -0.02 - k * 0.03, -0.01]}>
                <sphereGeometry args={[0.011, 6, 6]} />
                <meshBasicMaterial color={LYX.crysHi} transparent opacity={0.7} toneMapped={false} />
              </mesh>
            ))}
          </group>
        );
      })}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.135, 0.98, 0.02]}>
          <mesh position={[0, -0.15, 0]} rotation={[0, 0, sd * 0.22]}>
            <capsuleGeometry args={[0.024, 0.24, 4, 8]} /><meshStandardMaterial {...body} />
          </mesh>
          <mesh position={[sd * 0.045, -0.30, 0.02]}><octahedronGeometry args={[0.038, 0]} /><meshStandardMaterial {...body} /></mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.070, 0.24, 0]}>
          <mesh position={[0, -0.10, 0]}><capsuleGeometry args={[0.026, 0.16, 4, 8]} /><meshStandardMaterial {...body} /></mesh>
          <mesh position={[0, -0.21, 0.024]} scale={[1, 0.6, 1.5]}><octahedronGeometry args={[0.044, 0]} /><meshStandardMaterial {...body} /></mesh>
        </group>
      ))}
      {Array.from({ length: 10 }).map((_, i) => (
        <mesh key={"d" + i} ref={(el) => (dust.current[i] = el)}>
          <sphereGeometry args={[0.010, 6, 6]} />
          <meshBasicMaterial color={LYX.dust} transparent opacity={0.4} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.78, color: pal.glow, opacity: 0.08, y: 0.86, scale: [1, 1.2, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㉟ MYGZORI · Time · Machine · Lyf 3 / Lyt 5
// ════════════════════════════════════════════════════════════
//  "They understand that they are now extra-dimensional beings
//   as a result, and there is a CYCLED LOOP of feedback."
//  A being that knows it is in a loop and loves the loop is a
//  gift of a modelling brief: the body is a closed circuit. The
//  head is not on top of the torso, it is on the FAR SIDE of a
//  torus that runs through the chest, so a Mygzori looking
//  forward is also looking back at itself, and the light runs
//  around that ring in one direction forever without arriving.
const MYG = { case: "#3a5a7a", ring: "#8ad8ff", gild: "#ffd24a", dark: "#0a1420" };
export function MygzoriBody({ pal }) {
  pal = pal || getSpeciesPalette("mygzori");
  const plate = plateTex("myg_plate_v1", "#5a86ae", "#2e4c68", "#0a1420", "#a8d8f0");
  const rig = useBeingRig();
  const pulses = useRef([]); const ring = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (ring.current) ring.current.rotation.z = t * 0.22;
    pulses.current.forEach((m, i) => {
      if (!m) return;
      // one direction, forever, never arriving
      const a = (t * 0.9 + i / 6) * Math.PI * 2;
      m.position.set(Math.cos(a) * 0.285, 0.92 + Math.sin(a) * 0.285, 0);
      if (m.material) m.material.opacity = 0.45 + Math.sin(a * 3) * 0.30;
    });
  });
  const shell = { map: plate, roughness: 0.42, metalness: 0.8 };
  const gild = { color: MYG.gild, metalness: 0.95, roughness: 0.24, emissive: MYG.gild, emissiveIntensity: 0.25 };
  return (
    <group>
      <group ref={rig.torso}>
        {/* THE LOOP — a real torus through the middle of it */}
        <group ref={ring} position={[0, 0.92, 0]}>
          <mesh><torusGeometry args={[0.285, 0.048, 12, 40]} /><meshStandardMaterial {...shell} /></mesh>
          <mesh><torusGeometry args={[0.285, 0.014, 8, 40]} />
            <meshBasicMaterial color={MYG.ring} transparent opacity={0.45} toneMapped={false} /></mesh>
        </group>
        <mesh position={[0, 0.92, 0]} scale={[0.86, 0.86, 1.30]}>
          <sphereGeometry args={[0.150, 12, 10]} /><meshStandardMaterial {...shell} />
        </mesh>
        <mesh position={[0, 0.48, 0]} scale={[1, 1, 0.86]}>
          <coneGeometry args={[0.215, 0.52, 12]} /><meshStandardMaterial {...shell} />
        </mesh>
      </group>
      {/* the head sits on the FAR SIDE of the ring, facing back */}
      <group ref={rig.head} position={[0, 1.21, -0.02]}>
        <mesh scale={[0.90, 0.94, 0.90]}><sphereGeometry args={[0.130, 14, 12]} /><meshStandardMaterial {...shell} /></mesh>
        <mesh position={[0, 0, -0.104]} scale={[0.9, 0.5, 0.4]}>
          <sphereGeometry args={[0.105, 12, 10]} /><meshStandardMaterial color={MYG.dark} roughness={0.2} metalness={0.5} />
        </mesh>
        {/* it is looking BACKWARD down its own loop */}
        <mesh position={[0, 0.004, -0.140]}>
          <planeGeometry args={[0.140, 0.020]} />
          <meshBasicMaterial color={MYG.ring} transparent opacity={0.85} toneMapped={false} />
        </mesh>
        {/* and a second, smaller face forward, watching you watch */}
        <mesh position={[0, 0.010, 0.118]}>
          <planeGeometry args={[0.070, 0.012]} />
          <meshBasicMaterial color={MYG.gild} transparent opacity={0.7} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.115, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.086, 0.010, 6, 18]} /><meshStandardMaterial {...gild} />
        </mesh>
      </group>
      {Array.from({ length: 6 }).map((_, i) => (
        <mesh key={"p" + i} ref={(el) => (pulses.current[i] = el)}>
          <sphereGeometry args={[0.024, 8, 8]} />
          <meshBasicMaterial color={MYG.ring} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.265, 0.98, 0]}>
          <mesh position={[0, -0.17, 0]} rotation={[0, 0, sd * 0.18]}>
            <capsuleGeometry args={[0.046, 0.26, 4, 10]} /><meshStandardMaterial {...shell} />
          </mesh>
          <mesh position={[sd * 0.03, -0.36, 0.02]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.046, 0.012, 6, 14]} /><meshStandardMaterial {...gild} />
          </mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.100, 0.30, 0]}>
          <mesh position={[0, -0.13, 0]}><capsuleGeometry args={[0.056, 0.18, 4, 10]} /><meshStandardMaterial {...shell} /></mesh>
          <mesh position={[0, -0.28, 0.026]} scale={[1, 0.62, 1.5]}><sphereGeometry args={[0.064, 10, 8]} /><meshStandardMaterial {...shell} /></mesh>
        </group>
      ))}
      {Aura({ r: 0.82, color: MYG.ring, opacity: 0.06, y: 0.88, scale: [1, 1.1, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㊱ ROSALUNA · Time · Machine · Lyf 4 / Lyt 4
// ════════════════════════════════════════════════════════════
//  "Moon Rose species. Has a move called MALAROSA. Opposite of
//   Auroraeth." Time-school and Machine-ilk, so this is not a
//   flower — it is a CLOCKWORK flower, and the difference is
//   that its bloom opens and closes on a mechanism you can see.
//   Twelve petals on twelve escapement arms, a moon-disc for a
//   pistil, and thorns that are cut steel. Malarosa is the moment
//   the bloom snaps shut, so the idle is the winding before it.
const ROS = { petal: "#c86a9a", petalDeep: "#5a1e3a", brass: "#c8a86a", steel: "#c8ccd8", moon: "#e8c8ff" };
export function RosalunaBody({ pal }) {
  pal = pal || getSpeciesPalette("rosaluna");
  const petalTex = hideTex("ros_petal_v1", "#e88ab4", "#a03a68", "#3a0e24", {
    speck: "rgba(232,200,255,0.5)",
    after: (g, w, h, R) => { for (let i = 0; i < 90; i++) leaf(g, R() * w, R() * h, 120 + R() * 140, 12 + R() * 10, (R() - .5) * 0.5, "rgba(90,20,50,0.4)", 0.5); },
  });
  const brass = plateTex("ros_brass_v1", "#e8c88a", "#a8863a", "#3a2c10", "#fff0c8");
  const rig = useBeingRig();
  const arms = useRef([]); const pistil = useRef(); const gear = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // wind, wind, wind — MALAROSA is the snap, and it is not idle
    const open = 0.62 + Math.sin(t * 0.55) * 0.34;
    arms.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.x = -open - Math.sin(t * 0.9 + i * 0.5) * 0.05;
      m.rotation.y = (i / 12) * Math.PI * 2;
    });
    if (pistil.current) pistil.current.rotation.y = t * 0.5;
    if (gear.current) gear.current.rotation.y = -t * 0.9;
  });
  const petal = { map: petalTex, roughness: 0.72, metalness: 0.18, side: THREE.DoubleSide, emissive: ROS.moon, emissiveIntensity: 0.10 };
  const metal = { map: brass, roughness: 0.34, metalness: 0.9 };
  return (
    <group>
      {/* the stem is a column of escapement, not a stalk */}
      <group ref={rig.torso}>
        <mesh position={[0, 0.44, 0]}><cylinderGeometry args={[0.056, 0.075, 0.86, 12]} /><meshStandardMaterial {...metal} /></mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[0, 0.22 + i * 0.24, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.086, 0.014, 6, 18]} /><meshStandardMaterial {...metal} />
          </mesh>
        ))}
        <group ref={gear} position={[0, 0.34, 0.075]}>
          <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.070, 0.070, 0.018, 16]} /><meshStandardMaterial {...metal} /></mesh>
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i / 12) * Math.PI * 2;
            return (
              <mesh key={i} position={[Math.cos(a) * 0.078, Math.sin(a) * 0.078, 0]} rotation={[0, 0, a]}>
                <boxGeometry args={[0.020, 0.014, 0.018]} /><meshStandardMaterial {...metal} />
              </mesh>
            );
          })}
        </group>
        {/* THORNS — cut steel, and they are on a spiral */}
        {Array.from({ length: 7 }).map((_, i) => {
          const a = i * 1.4;
          return (
            <mesh key={"th" + i} position={[Math.cos(a) * 0.072, 0.16 + i * 0.095, Math.sin(a) * 0.072]}
              rotation={[Math.sin(a) * 0.8, -a, -0.7]}>
              <coneGeometry args={[0.020, 0.086, 4]} />
              <meshStandardMaterial color={ROS.steel} roughness={0.18} metalness={1.0} />
            </mesh>
          );
        })}
        {/* two leaves — brass, hinged */}
        {[-1, 1].map((sd) => (
          <group key={"lf" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.070, 0.52, 0]}>
            <mesh rotation={[0.3, 0, sd * 1.1]} scale={[1, 0.10, 1]}>
              <coneGeometry args={[0.075, 0.26, 3]} /><meshStandardMaterial {...metal} side={THREE.DoubleSide} />
            </mesh>
          </group>
        ))}
      </group>
      {/* THE BLOOM — twelve petals on twelve escapement arms */}
      <group ref={rig.head} position={[0, 0.92, 0]}>
        <mesh><sphereGeometry args={[0.086, 12, 10]} /><meshStandardMaterial {...metal} /></mesh>
        {Array.from({ length: 12 }).map((_, i) => (
          <group key={i} ref={(el) => (arms.current[i] = el)}>
            <group position={[0, 0.020, 0]}>
              <mesh position={[0, 0.05, 0.086]} rotation={[0.2, 0, 0]}>
                <boxGeometry args={[0.010, 0.10, 0.010]} /><meshStandardMaterial {...metal} />
              </mesh>
              <mesh position={[0, 0.14, 0.115]} rotation={[0.35, 0, 0]}>
                <circleGeometry args={[0.115, 12, 0, Math.PI]} /><meshStandardMaterial {...petal} />
              </mesh>
            </group>
          </group>
        ))}
        {/* THE PISTIL — a moon, and it is the only soft thing */}
        <group ref={pistil} position={[0, 0.075, 0]}>
          <mesh><sphereGeometry args={[0.062, 14, 12]} />
            <meshStandardMaterial color={ROS.moon} roughness={0.9} emissive={ROS.moon} emissiveIntensity={0.45} /></mesh>
          <mesh><sphereGeometry args={[0.105, 12, 10]} />
            <meshBasicMaterial color={ROS.moon} transparent opacity={0.20} toneMapped={false} depthWrite={false} /></mesh>
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return (
              <mesh key={i} position={[Math.cos(a) * 0.045, 0.055, Math.sin(a) * 0.045]}>
                <capsuleGeometry args={[0.005, 0.05, 3, 5]} />
                <meshStandardMaterial color={ROS.brass} metalness={0.9} roughness={0.3} />
              </mesh>
            );
          })}
        </group>
      </group>
      {/* the root-foot: a brass tripod, planted */}
      {[0, 1, 2].map((i) => {
        const a = (i / 3) * Math.PI * 2;
        return (
          <group key={"ft" + i} ref={i === 0 ? rig.legL : i === 1 ? rig.legR : undefined}
            position={[Math.cos(a) * 0.055, 0.10, Math.sin(a) * 0.055]} rotation={[0, -a, 0]}>
            <mesh position={[0.070, -0.045, 0]} rotation={[0, 0, -0.9]}>
              <capsuleGeometry args={[0.020, 0.14, 4, 8]} /><meshStandardMaterial {...metal} />
            </mesh>
          </group>
        );
      })}
      {Aura({ r: 0.62, color: pal.glow, opacity: 0.08, y: 0.78, scale: [1, 1.15, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㊲ AURORAETH · Time · Machine · Lyf 4 / Lyt 4
// ════════════════════════════════════════════════════════════
//  "Sun Rose species, opposite of Rosaluna." The variant-pair law
//  again (§4f): the SAME clockwork rose — same escapement column,
//  same twelve arms, same tripod — and it disagrees about every
//  single one. Rosaluna's bloom closes; Auroraeth's is jammed
//  permanently OPEN. Rosaluna's pistil is a cold moon; this one
//  is a working sun that throws real heat-shimmer. Rosaluna's
//  thorns point down at the stem; Auroraeth's point out at you.
const AUR = { petal: "#e88a3a", petalDeep: "#7a2a08", brass: "#ffd88a", sun: "#ffe0a8", steel: "#e8b06a" };
export function AuroraethBody({ pal }) {
  pal = pal || getSpeciesPalette("auroraeth");
  const petalTex = hideTex("aur_petal_v1", "#ffb060", "#c85a1a", "#3a1204", {
    speck: "rgba(255,224,168,0.6)",
    after: (g, w, h, R) => { for (let i = 0; i < 90; i++) leaf(g, R() * w, R() * h, 120 + R() * 140, 12 + R() * 10, (R() - .5) * 0.5, "rgba(255,224,168,0.35)", 0.5); },
  });
  const brass = plateTex("aur_brass_v1", "#ffe2a8", "#c89a48", "#4a3414", "#fff8e0");
  const rig = useBeingRig();
  const arms = useRef([]); const sun = useRef(); const shimmer = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    arms.current.forEach((m, i) => {
      if (!m) return;
      // JAMMED OPEN — it only ever trembles against the stop
      m.rotation.x = -1.15 + Math.sin(t * 2.4 + i * 0.7) * 0.022;
      m.rotation.y = (i / 12) * Math.PI * 2;
    });
    if (sun.current) { sun.current.rotation.y = t * 0.7; sun.current.scale.setScalar(1 + Math.sin(t * 2.1) * 0.07); }
    shimmer.current.forEach((m, i) => {
      if (!m) return;
      const f = (t * 0.5 + i * 0.17) % 1;
      m.position.set(Math.sin(i * 2.7) * 0.16, 1.02 + f * 0.55, Math.cos(i * 1.9) * 0.16);
      m.scale.setScalar(0.4 + f * 1.6);
      if (m.material) m.material.opacity = 0.16 * (1 - f);
    });
  });
  const petal = { map: petalTex, roughness: 0.66, metalness: 0.2, side: THREE.DoubleSide, emissive: AUR.sun, emissiveIntensity: 0.22 };
  const metal = { map: brass, roughness: 0.30, metalness: 0.92 };
  return (
    <group>
      <group ref={rig.torso}>
        <mesh position={[0, 0.44, 0]}><cylinderGeometry args={[0.056, 0.075, 0.86, 12]} /><meshStandardMaterial {...metal} /></mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[0, 0.22 + i * 0.24, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.086, 0.014, 6, 18]} /><meshStandardMaterial {...metal} />
          </mesh>
        ))}
        {/* THORNS POINTING OUT — the disagreement, in one detail */}
        {Array.from({ length: 7 }).map((_, i) => {
          const a = i * 1.4;
          return (
            <mesh key={"th" + i} position={[Math.cos(a) * 0.078, 0.16 + i * 0.095, Math.sin(a) * 0.078]}
              rotation={[Math.sin(a) * 0.4, -a, 1.35]}>
              <coneGeometry args={[0.022, 0.098, 4]} />
              <meshStandardMaterial color={AUR.steel} roughness={0.22} metalness={0.95} emissive={AUR.sun} emissiveIntensity={0.2} />
            </mesh>
          );
        })}
        {[-1, 1].map((sd) => (
          <group key={"lf" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.070, 0.56, 0]}>
            <mesh rotation={[-0.4, 0, sd * 1.3]} scale={[1, 0.10, 1]}>
              <coneGeometry args={[0.075, 0.26, 3]} /><meshStandardMaterial {...metal} side={THREE.DoubleSide} />
            </mesh>
          </group>
        ))}
      </group>
      <group ref={rig.head} position={[0, 0.92, 0]}>
        <mesh><sphereGeometry args={[0.086, 12, 10]} /><meshStandardMaterial {...metal} /></mesh>
        {Array.from({ length: 12 }).map((_, i) => (
          <group key={i} ref={(el) => (arms.current[i] = el)}>
            <group position={[0, 0.020, 0]}>
              <mesh position={[0, 0.05, 0.086]} rotation={[0.2, 0, 0]}>
                <boxGeometry args={[0.010, 0.10, 0.010]} /><meshStandardMaterial {...metal} />
              </mesh>
              <mesh position={[0, 0.14, 0.115]} rotation={[0.35, 0, 0]}>
                <circleGeometry args={[0.125, 12, 0, Math.PI]} /><meshStandardMaterial {...petal} />
              </mesh>
            </group>
          </group>
        ))}
        {/* THE SUN — working, and throwing real heat */}
        <group ref={sun} position={[0, 0.075, 0]}>
          <mesh><sphereGeometry args={[0.070, 14, 12]} /><meshBasicMaterial color={AUR.sun} toneMapped={false} /></mesh>
          <mesh><sphereGeometry args={[0.125, 12, 10]} />
            <meshBasicMaterial color="#ff8a2e" transparent opacity={0.26} toneMapped={false} depthWrite={false} /></mesh>
          {Array.from({ length: 10 }).map((_, i) => {
            const a = (i / 10) * Math.PI * 2;
            return (
              <mesh key={i} position={[Math.cos(a) * 0.105, 0.010, Math.sin(a) * 0.105]} rotation={[0, -a, -1.2]}>
                <coneGeometry args={[0.012, 0.098, 4]} />
                <meshBasicMaterial color={AUR.sun} transparent opacity={0.72} toneMapped={false} />
              </mesh>
            );
          })}
        </group>
      </group>
      {[0, 1, 2].map((i) => {
        const a = (i / 3) * Math.PI * 2 + 0.5;
        return (
          <group key={"ft" + i} ref={i === 0 ? rig.legL : i === 1 ? rig.legR : undefined}
            position={[Math.cos(a) * 0.055, 0.10, Math.sin(a) * 0.055]} rotation={[0, -a, 0]}>
            <mesh position={[0.070, -0.045, 0]} rotation={[0, 0, -0.9]}>
              <capsuleGeometry args={[0.020, 0.14, 4, 8]} /><meshStandardMaterial {...metal} />
            </mesh>
          </group>
        );
      })}
      {Array.from({ length: 6 }).map((_, i) => (
        <mesh key={"sh" + i} ref={(el) => (shimmer.current[i] = el)}>
          <sphereGeometry args={[0.052, 8, 8]} />
          <meshBasicMaterial color={AUR.sun} transparent opacity={0.14} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.68, color: pal.glow, opacity: 0.10, y: 0.80, scale: [1, 1.15, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㊳ ZYLARAEON · Time · Machine · Lyf 1 / Lyt 7
// ════════════════════════════════════════════════════════════
//  "They understand the cycled loop and mean only to RUIN the
//   Writer and his God." Mygzori's dual, and the loop is the
//   shared silhouette — but where Mygzori's ring is closed and
//   its light runs forever, a Zylaraeon's ring is CUT. There is a
//   gap in it, the gap is where the machine works, and what it is
//   working on is the ring itself: a small cutting head grinds
//   away at the open end, forever, throwing sparks that fall.
//   Lyf 1 — it has spent everything it had on the cut.
const ZYL = { case: "#1a1a22", edge: "#ff2e4a", grind: "#ff8a3a", steel: "#8a8a96" };
export function ZylaraeonBody({ pal }) {
  pal = pal || getSpeciesPalette("zylaraeon");
  const plate = plateTex("zyl_plate_v1", "#33333f", "#191921", "#050508", "#5a5a68");
  const rig = useBeingRig();
  const head = useRef(); const sparks = useRef([]); const ring = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (ring.current) ring.current.rotation.z = -t * 0.14;
    if (head.current) head.current.rotation.x = t * 22;
    sparks.current.forEach((m, i) => {
      if (!m) return;
      const f = (t * 1.6 + i * 0.13) % 1;
      m.position.set(0.24 + f * 0.16, 1.06 - f * f * 0.85, Math.sin(i * 2.3) * 0.06 * f);
      m.scale.setScalar(1 - f * 0.8);
      if (m.material) m.material.opacity = 0.9 * (1 - f);
    });
  });
  const shell = { map: plate, roughness: 0.5, metalness: 0.75 };
  return (
    <group>
      <group ref={rig.torso}>
        {/* THE CUT LOOP — a torus with a bite out of it */}
        <group ref={ring} position={[0, 0.92, 0]}>
          <mesh><torusGeometry args={[0.285, 0.048, 12, 40, Math.PI * 1.68]} /><meshStandardMaterial {...shell} /></mesh>
          <mesh><torusGeometry args={[0.285, 0.014, 8, 40, Math.PI * 1.68]} />
            <meshBasicMaterial color={ZYL.edge} transparent opacity={0.55} toneMapped={false} /></mesh>
          {/* the severed end, glowing where it was cut */}
          <mesh position={[0.285, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
            <circleGeometry args={[0.048, 14]} />
            <meshBasicMaterial color={ZYL.grind} transparent opacity={0.9} toneMapped={false} />
          </mesh>
        </group>
        <mesh position={[0, 0.92, 0]} scale={[0.86, 0.86, 1.30]}>
          <sphereGeometry args={[0.150, 12, 10]} /><meshStandardMaterial {...shell} />
        </mesh>
        <mesh position={[0, 0.48, 0]} scale={[1, 1, 0.86]}>
          <coneGeometry args={[0.200, 0.52, 12]} /><meshStandardMaterial {...shell} />
        </mesh>
      </group>
      {/* THE CUTTING HEAD — the whole purpose, and it never stops */}
      <group ref={rig.prop} position={[0.30, 1.06, 0]}>
        <mesh ref={head} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.086, 0.086, 0.020, 20]} />
          <meshStandardMaterial color={ZYL.steel} roughness={0.16} metalness={1.0} />
        </mesh>
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <torusGeometry args={[0.088, 0.006, 6, 24]} />
          <meshBasicMaterial color={ZYL.grind} transparent opacity={0.7} toneMapped={false} />
        </mesh>
        <mesh position={[0.055, 0.045, 0]} rotation={[0, 0, -0.7]}>
          <boxGeometry args={[0.030, 0.135, 0.026]} /><meshStandardMaterial {...shell} />
        </mesh>
      </group>
      <group ref={rig.head} position={[0, 1.21, -0.02]}>
        <mesh scale={[0.90, 0.94, 0.90]}><sphereGeometry args={[0.125, 14, 12]} /><meshStandardMaterial {...shell} /></mesh>
        {/* one narrow red aperture, and it is aimed at the cut */}
        <mesh position={[0.030, 0.010, 0.108]} rotation={[0, 0.4, 0.25]}>
          <planeGeometry args={[0.098, 0.012]} />
          <meshBasicMaterial color={ZYL.edge} transparent opacity={0.9} toneMapped={false} />
        </mesh>
      </group>
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.250, 0.98, 0]}>
          <mesh position={[0, -0.17, 0.03]} rotation={[0.3, 0, sd * 0.20]}>
            <capsuleGeometry args={[0.040, 0.26, 4, 10]} /><meshStandardMaterial {...shell} />
          </mesh>
          <mesh position={[sd * 0.02, -0.34, 0.12]}><sphereGeometry args={[0.042, 10, 8]} /><meshStandardMaterial {...shell} /></mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.095, 0.30, 0]}>
          <mesh position={[0, -0.13, 0]}><capsuleGeometry args={[0.048, 0.18, 4, 10]} /><meshStandardMaterial {...shell} /></mesh>
          <mesh position={[0, -0.28, 0.026]} scale={[1, 0.6, 1.5]}><sphereGeometry args={[0.058, 10, 8]} /><meshStandardMaterial {...shell} /></mesh>
        </group>
      ))}
      {Array.from({ length: 10 }).map((_, i) => (
        <mesh key={"sp" + i} ref={(el) => (sparks.current[i] = el)}>
          <sphereGeometry args={[0.010, 6, 6]} />
          <meshBasicMaterial color={ZYL.grind} transparent opacity={0.8} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.80, color: ZYL.edge, opacity: 0.055, y: 0.88, scale: [1, 1.1, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㊴ MAGMANTUS · Time · Machine · Lyf 7 / Lyt 1
// ════════════════════════════════════════════════════════════
//  "Lava Golem. Magmantus = the Lord; baby version called
//   Magmori."  Solaea. A golem is a Machine that was never
//   built, which is why it belongs in Time and not anywhere
//   else — so the body is SLAG: cooled black crust in plates
//   with live magma showing in every crack, and the crust is
//   thickest where it has been still longest. The strange
//   load-bearing detail is that it is visibly COOLING — the
//   extremities are almost black and the core is white — so you
//   can see how long it has been standing there.
const MGM = { crust: "#241a18", crustHi: "#4a3630", core: "#ff6a1a", white: "#ffe8b0", ash: "#8a8078" };
export function MagmantusBody({ pal }) {
  pal = pal || getSpeciesPalette("magmantus");
  const crust = hideTex("mgm_crust_v1", "#4a3630", "#241a18", "#0a0605", {
    grain: 1600,
    after: (g, w, h, R) => {
      // the cracks, and the light in them
      for (let i = 0; i < 130; i++) {
        let x = R() * w, y = R() * h;
        g.strokeStyle = `rgba(255,${90 + R() * 100 | 0},26,${0.35 + R() * 0.5})`;
        g.lineWidth = 1 + R() * 3.5;
        g.beginPath(); g.moveTo(x, y);
        for (let k = 0; k < 5; k++) { x += (R() - .5) * 70; y += (R() - .5) * 70; g.lineTo(x, y); }
        g.stroke();
      }
      for (let i = 0; i < 500; i++) {
        g.fillStyle = `rgba(10,6,4,${0.15 + R() * 0.4})`;
        g.beginPath(); g.arc(R() * w, R() * h, 2 + R() * 9, 0, 7); g.fill();
      }
    },
  });
  const rig = useBeingRig();
  const coreRef = useRef(); const embers = useRef([]); const drip = useRef([]);
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (coreRef.current) coreRef.current.scale.setScalar(1 + Math.sin(t * 0.85) * 0.10);
    embers.current.forEach((m, i) => {
      if (!m) return;
      const f = (t * 0.32 + i * 0.19) % 1;
      m.position.set(Math.sin(i * 2.4) * 0.24, 1.05 + f * 0.85, Math.cos(i * 1.7) * 0.20);
      m.scale.setScalar(1 - f * 0.6);
      if (m.material) m.material.opacity = 0.7 * (1 - f);
    });
    // and it DRIPS, because a golem of lava obviously does
    drip.current.forEach((m, i) => {
      if (!m) return;
      const f = (t * 0.75 + i * 0.34) % 1;
      m.position.set(-0.22 + i * 0.16, 0.72 - f * 0.62, 0.16);
      m.scale.setScalar(1 - f * 0.4);
      if (m.material) m.material.opacity = 0.85 * (1 - f * 0.7);
    });
  });
  const rock = { map: crust, roughness: 1, metalness: 0.06, emissive: MGM.core, emissiveIntensity: 0.30 };
  const cold = { map: crust, color: "#5a5048", roughness: 1, metalness: 0.05, emissive: MGM.core, emissiveIntensity: 0.05 };
  return (
    <group>
      <group ref={rig.torso}>
        {/* four overlapping slabs of cooled crust, not a barrel */}
        <mesh position={[0, 0.98, 0]} scale={[1.24, 0.92, 0.92]} castShadow>
          <sphereGeometry args={[0.290, 12, 10]} /><meshStandardMaterial {...rock} />
        </mesh>
        <mesh position={[-0.08, 0.74, 0.05]} scale={[1.06, 0.90, 0.98]} castShadow>
          <sphereGeometry args={[0.255, 12, 10]} /><meshStandardMaterial {...rock} />
        </mesh>
        <mesh position={[0.10, 0.56, -0.04]} scale={[1.0, 0.86, 0.96]}>
          <sphereGeometry args={[0.235, 12, 10]} /><meshStandardMaterial {...rock} />
        </mesh>
        {/* THE CORE — white, and the whole reason it is standing */}
        <mesh ref={coreRef} position={[0, 0.86, 0.06]}>
          <sphereGeometry args={[0.115, 14, 12]} /><meshBasicMaterial color={MGM.white} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.86, 0.06]}><sphereGeometry args={[0.205, 12, 10]} />
          <meshBasicMaterial color={MGM.core} transparent opacity={0.26} toneMapped={false} depthWrite={false} /></mesh>
        {/* the vents on the back, ash-choked */}
        {[-1, 0, 1].map((i) => (
          <mesh key={"v" + i} position={[i * 0.145, 1.10 - Math.abs(i) * 0.06, -0.22]} rotation={[-0.5, 0, i * 0.2]}>
            <cylinderGeometry args={[0.048, 0.062, 0.16, 8]} /><meshStandardMaterial color={MGM.ash} roughness={1} />
          </mesh>
        ))}
      </group>
      <group ref={rig.head} position={[0, 1.30, 0.02]}>
        <mesh scale={[1.10, 0.86, 0.94]}><sphereGeometry args={[0.165, 12, 10]} /><meshStandardMaterial {...rock} /></mesh>
        {/* no eyes. A split across the face, and light in it. */}
        <mesh position={[0, -0.010, 0.128]} rotation={[0, 0, 0.14]}>
          <planeGeometry args={[0.235, 0.030]} />
          <meshBasicMaterial color={MGM.white} transparent opacity={0.9} toneMapped={false} />
        </mesh>
        {[0, 1, 2, 3].map((i) => (
          <mesh key={"h" + i} position={[(i - 1.5) * 0.075, 0.135, -0.03]} rotation={[-0.4, 0, (i - 1.5) * 0.24]}>
            <coneGeometry args={[0.030 - Math.abs(i - 1.5) * 0.006, 0.135, 5]} /><meshStandardMaterial {...cold} />
          </mesh>
        ))}
      </group>
      {/* arms and legs COOL toward the extremity — the age read */}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.330, 1.00, 0]}>
          <mesh scale={[1.15, 0.9, 1.15]}><sphereGeometry args={[0.125, 10, 8]} /><meshStandardMaterial {...rock} /></mesh>
          <mesh position={[sd * 0.05, -0.22, 0]} rotation={[0, 0, sd * 0.18]}>
            <capsuleGeometry args={[0.080, 0.24, 4, 10]} /><meshStandardMaterial {...rock} emissiveIntensity={0.18} />
          </mesh>
          <mesh position={[sd * 0.08, -0.46, 0.02]} scale={[1.2, 1, 1.1]}>
            <sphereGeometry args={[0.098, 10, 8]} /><meshStandardMaterial {...cold} />
          </mesh>
        </group>
      ))}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.145, 0.34, 0]}>
          <mesh position={[0, -0.14, 0]}><capsuleGeometry args={[0.086, 0.20, 4, 10]} /><meshStandardMaterial {...rock} emissiveIntensity={0.14} /></mesh>
          <mesh position={[0, -0.32, 0.030]} scale={[1.2, 0.62, 1.5]}><sphereGeometry args={[0.098, 10, 8]} /><meshStandardMaterial {...cold} /></mesh>
        </group>
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <mesh key={"em" + i} ref={(el) => (embers.current[i] = el)}>
          <sphereGeometry args={[0.016, 6, 6]} />
          <meshBasicMaterial color={MGM.core} transparent opacity={0.7} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {[0, 1, 2].map((i) => (
        <mesh key={"dp" + i} ref={(el) => (drip.current[i] = el)}>
          <sphereGeometry args={[0.020, 8, 8]} />
          <meshBasicMaterial color={MGM.white} transparent opacity={0.85} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {Aura({ r: 0.95, color: MGM.core, opacity: 0.08, y: 0.90, scale: [1, 1.1, 1] })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㊵ V'IVUS DRAGON · Dragon · Dragon · Lyf 4 / Lyt 4
// ════════════════════════════════════════════════════════════
//  The fourth Dragon, canon since Session 7 and never built:
//  Premier is holy, Chaos is evil, Lucky is J'aeonxi, and V'ivus is
//  ENLIGHTENED. So it is kin geometry with the other three — the
//  house `Membrane` wing, the seated hatchling stance, the horn
//  pair — and its one disagreement is that it has OPENED. The
//  chest plates are hinged apart on a lit interior, the wings are
//  clear rather than leathered, and it sits in the full lotus a
//  hatchling has no business knowing how to sit in.
const VIV = { hide: "#4ad8b4", hideDeep: "#0c2c28", light: "#e8fff8", gild: "#ffd88a", horn: "#dff5ee" };
export function VivusDragonBody({ pal }) {
  pal = pal || getSpeciesPalette("vivus_dragon");
  const hide = scaleTex("viv_scale_v1", "#7aeed0", "#2a9a84", "#07211f", "rgba(232,255,248,0.6)", 16);
  const eye = eyeDisc("viv_eye_v1", "#ffffff", "#ffd88a", "#1a4a42", null, false);
  const rig = useBeingRig();
  const rings = useRef([]); const heart = useRef(); const wingL = useRef(); const wingR = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    rings.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.y = t * (0.3 + i * 0.16) * (i % 2 ? -1 : 1);
      m.rotation.x = 0.4 + Math.sin(t * 0.5 + i) * 0.18;
    });
    if (heart.current) heart.current.scale.setScalar(1 + Math.sin(t * 1.15) * 0.12);
    if (wingL.current) wingL.current.rotation.z = 0.30 + Math.sin(t * 1.05) * 0.09;
    if (wingR.current) wingR.current.rotation.z = -0.30 - Math.sin(t * 1.05) * 0.09;
  });
  const skin = { map: hide, roughness: 0.55, metalness: 0.25, emissive: VIV.light, emissiveIntensity: 0.12 };
  return (
    <group>
      {/* SEATED — the full lotus, which is the entire character */}
      <group ref={rig.torso}>
        <mesh position={[0, 0.34, 0]} scale={[1.30, 0.52, 1.15]} castShadow>
          <sphereGeometry args={[0.255, 14, 12]} /><meshStandardMaterial {...skin} />
        </mesh>
        <mesh position={[0, 0.62, 0.02]} scale={[0.92, 1.0, 0.88]}>
          <sphereGeometry args={[0.225, 14, 12]} /><meshStandardMaterial {...skin} />
        </mesh>
        {/* THE OPENED CHEST — hinged plates on a lit interior */}
        {[-1, 1].map((sd) => (
          <mesh key={"pl" + sd} position={[sd * 0.145, 0.64, 0.115]} rotation={[0, sd * -0.85, 0]} scale={[0.32, 1, 1]}>
            <sphereGeometry args={[0.165, 12, 10]} /><meshStandardMaterial {...skin} side={THREE.DoubleSide} />
          </mesh>
        ))}
        <mesh ref={heart} position={[0, 0.64, 0.10]}>
          <sphereGeometry args={[0.078, 14, 12]} /><meshBasicMaterial color={VIV.light} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.64, 0.10]}><sphereGeometry args={[0.140, 12, 10]} />
          <meshBasicMaterial color={pal.main} transparent opacity={0.24} toneMapped={false} depthWrite={false} /></mesh>
      </group>
      {/* crossed legs, resting on the haunch */}
      {[-1, 1].map((sd) => (
        <group key={"l" + sd} ref={sd < 0 ? rig.legL : rig.legR} position={[sd * 0.135, 0.20, 0.10]}>
          <mesh rotation={[0, sd * 0.9, Math.PI / 2]}>
            <capsuleGeometry args={[0.062, 0.20, 4, 10]} /><meshStandardMaterial {...skin} />
          </mesh>
          <mesh position={[sd * -0.10, 0.020, 0.075]} scale={[1.1, 0.7, 1.2]}>
            <sphereGeometry args={[0.070, 10, 8]} /><meshStandardMaterial {...skin} />
          </mesh>
        </group>
      ))}
      {/* hands open on the knees, palms up */}
      {[-1, 1].map((sd) => (
        <group key={"a" + sd} ref={sd < 0 ? rig.armL : rig.armR} position={[sd * 0.230, 0.62, 0.04]}>
          <mesh rotation={[0.3, 0, sd * 0.55]}><capsuleGeometry args={[0.050, 0.22, 4, 10]} /><meshStandardMaterial {...skin} /></mesh>
          <mesh position={[sd * 0.075, -0.185, 0.075]} scale={[1.2, 0.55, 1.2]}>
            <sphereGeometry args={[0.058, 10, 8]} /><meshStandardMaterial {...skin} />
          </mesh>
          <mesh position={[sd * 0.075, -0.155, 0.075]}>
            <sphereGeometry args={[0.030, 8, 8]} />
            <meshBasicMaterial color={VIV.light} transparent opacity={0.75} toneMapped={false} />
          </mesh>
        </group>
      ))}
      <group ref={rig.head} position={[0, 0.94, 0.04]}>
        <mesh scale={[0.98, 0.94, 1.14]}><sphereGeometry args={[0.165, 14, 12]} /><meshStandardMaterial {...skin} /></mesh>
        <mesh position={[0, -0.045, 0.145]} scale={[0.72, 0.60, 1.20]}>
          <sphereGeometry args={[0.115, 12, 10]} /><meshStandardMaterial {...skin} />
        </mesh>
        {/* the house horn pair, but grown straight up like a crown */}
        {[-1, 1].map((sd) => (
          <group key={"h" + sd} position={[sd * 0.088, 0.115, -0.02]}>
            {[0, 1, 2].map((i) => (
              <mesh key={i} position={[sd * i * 0.010, 0.045 + i * 0.058, -0.006 * i]} rotation={[-0.10, 0, sd * 0.10]}>
                <coneGeometry args={[0.028 - i * 0.007, 0.086, 6]} />
                <meshStandardMaterial color={VIV.horn} roughness={0.3} metalness={0.4} emissive={VIV.light} emissiveIntensity={0.25} />
              </mesh>
            ))}
          </group>
        ))}
        {Eyes({ map: eye, x: 0.088, y: 0.020, z: 0.128, r: 0.030, yaw: 0.40, socket: "#08201c" })}
        {/* the third eye, closed — enlightened, not awake */}
        <mesh position={[0, 0.075, 0.148]} rotation={[0, 0, 0]}>
          <planeGeometry args={[0.062, 0.010]} />
          <meshBasicMaterial color={VIV.gild} transparent opacity={0.85} toneMapped={false} />
        </mesh>
      </group>
      {/* CLEAR WINGS — the house Membrane, and you can see through */}
      <group ref={wingL} position={[-0.185, 0.72, -0.10]}>
        {Membrane({
          side: -1, span: 0.56, bones: 4,
          boneMat: { color: VIV.horn, roughness: 0.35, metalness: 0.45 },
          memMat: { color: VIV.light, transparent: true, opacity: 0.22, toneMapped: false, depthWrite: false },
          sweep: 0.30,
        })}
      </group>
      <group ref={wingR} position={[0.185, 0.72, -0.10]}>
        {Membrane({
          side: 1, span: 0.56, bones: 4,
          boneMat: { color: VIV.horn, roughness: 0.35, metalness: 0.45 },
          memMat: { color: VIV.light, transparent: true, opacity: 0.22, toneMapped: false, depthWrite: false },
          sweep: 0.30,
        })}
      </group>
      {/* three haloes, on three axes, none of them level */}
      {[0, 1, 2].map((i) => (
        <mesh key={"hr" + i} ref={(el) => (rings.current[i] = el)} position={[0, 0.94, 0.04]}>
          <torusGeometry args={[0.30 + i * 0.075, 0.005, 5, 40]} />
          <meshBasicMaterial color={i === 1 ? VIV.gild : VIV.light} transparent opacity={0.42} toneMapped={false} />
        </mesh>
      ))}
      {Aura({ r: 0.80, color: pal.glow, opacity: 0.09, y: 0.62 })}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  ㊶ H-11 · Dragon · Dragon · Lyf 5 / Lyt 3
// ════════════════════════════════════════════════════════════
//  Filed with the Dragons and shaped like an H-. "Nobody in the
//  vault has volunteered which of those is the mistake" — so the
//  model refuses to resolve it: an H- suit from the collar down,
//  built to the same stations as H-00 and H-06, with a DRAGON
//  wearing it from the collar up. The join is not disguised. The
//  eleven is stamped on the chest, and the suit has been let out
//  at every seam to fit what got into it.
const H11 = { suit: "#8a8ad8", hide: "#3a3a70", horn: "#ffb02e", seam: "#c8c8ff", ember: "#ff8a3a" };
export const ASCENT = {
  xolibrix:    { mantle: "#0f5a4a", crown: ["disc", 1, "#a8ffe0"], rings: 2, light: "#2ee8a8", lift: 0.06 },
  ursaeva:     { mantle: "#2a2450", crown: ["antlers", 6, "#b8a8ff"], rings: 1, light: "#b8a8ff", lift: 0.10 },
  bunnovian:   { mantle: "#e8dcf4", crown: ["disc", 1, "#ffffff"], rings: 3, light: "#ff9ec4", lift: 0.06 },
  skuryz:      { mantle: "#3a3020", crown: ["points", 5, "#ffd24a"], rings: 1, light: "#ffd24a", lift: 0.05 },
  trunaran:    { mantle: "#3a3e48", crown: ["points", 7, "#e8b04a"], rings: 1, light: "#c8d4e0", lift: 0.12 },
  kyrium:      { mantle: "#150e28", crown: ["points", 9, "#a86aff"], rings: 3, light: "#8a5aff", lift: 0.14 },
  slijj:       { mantle: "#2a1c10", crown: ["antlers", 8, "#ffb04a"], rings: 1, light: "#ffb04a", lift: 0.10 },
  velkor:      { mantle: "#7fa8c8", crown: ["disc", 1, "#e8f4ff"], rings: 2, light: "#e8f4ff", lift: 0.10 },
  noxura:      { mantle: "#14142c", crown: ["disc", 1, "#e8e0ff"], rings: 3, light: "#ffd88a", lift: 0.08 },
  abyssia:     { mantle: "#2a1840", crown: ["thorns", 7, "#a86aff"], rings: 2, light: "#5aff9e", lift: 0.08 },
  embearaea:   { mantle: "#5a2a10", crown: ["points", 7, "#ffd24a"], rings: 1, light: "#ffd24a", lift: 0.07 },
  carcharion:  { mantle: "#16303a", crown: ["points", 6, "#d8f0ff"], rings: 1, light: "#ff4a4a", lift: 0.06 },
  carnifex:    { mantle: "#16140f", crown: ["points", 3, "#8a1a1a"], rings: 1, light: "#ff6a1a", lift: 0.12 },
  geulaoin:    { mantle: "#5a1008", crown: ["points", 7, "#ffb02e"], rings: 1, light: "#ffb02e", lift: 0.09 },
  rapaxi:      { mantle: "#0f2c50", crown: ["points", 9, "#ffd24a"], rings: 2, light: "#4ad8ff", lift: 0.06 },
  gorgxonox:   { mantle: "#241a0c", crown: ["antlers", 8, "#c8bc9a"], rings: 1, light: "#8ae23c", lift: 0.12 },
  aeternavis:  { mantle: "#5a2408", crown: ["disc", 1, "#ffe8a8"], rings: 3, light: "#ffb02e", lift: 0.10 },
  tzhahws:     { mantle: "#2a1830", crown: ["points", 5, "#ffd24a"], rings: 1, light: "#ffd24a", lift: 0.08 },
  valtorix:    { mantle: "#1a1208", crown: ["antlers", 10, "#d8d0bc"], rings: 2, light: "#ff8a2e", lift: 0.10 },
  lyraei:      { mantle: "#efe2c8", crown: ["disc", 1, "#ffe8a8"], rings: 3, light: "#ffffff", lift: 0.08 },
  malumora:    { mantle: "#2a0810", crown: ["antlers", 6, "#241820"], rings: 1, light: "#ff8a5a", lift: 0.07 },
  aealix:      { mantle: "#1a2038", crown: ["disc", 1, "#ffffff"], rings: 3, light: "#8a5aff", lift: 0.10 },
  aeolix:      { mantle: "#0e2038", crown: ["points", 9, "#8ad8ff"], rings: 2, light: "#e8f8ff", lift: 0.10 },
  calypsis:    { mantle: "#08282a", crown: ["points", 7, "#a8ffe8"], rings: 2, light: "#a8ffe8", lift: 0.08 },
  caelumari:   { mantle: "#0a1e34", crown: ["disc", 1, "#c8e8ff"], rings: 2, light: "#f0e8c8", lift: 0.10 },
  zriidians:   { mantle: "#3a2c10", crown: ["points", 11, "#ffd88a"], rings: 3, light: "#4ad8ff", lift: 0.12 },
  hyalayri:    { mantle: "#1c2a30", crown: ["thorns", 9, "#c8e8f0"], rings: 3, light: "#8affe0", lift: 0.10 },
  riven:       { mantle: "#1a1030", crown: ["points", 6, "#c8b0ff"], rings: 2, light: "#ff5a8a", lift: 0.10 },
  devorix:     { mantle: "#0c0812", crown: ["thorns", 11, "#e8dcc0"], rings: 1, light: "#ff8a2e", lift: 0.10 },
  kraalok:     { mantle: "#1a2a10", crown: ["points", 4, "#c8f08a"], rings: 1, light: "#c8f08a", lift: 0.06 },
  lyrixi:      { mantle: "#120e30", crown: ["points", 7, "#a8ffe8"], rings: 3, light: "#e8a8ff", lift: 0.10 },
  mygzori:     { mantle: "#0a1420", crown: ["disc", 1, "#8ad8ff"], rings: 3, light: "#ffd24a", lift: 0.10 },
  rosaluna:    { mantle: "#2c0e1e", crown: ["thorns", 9, "#c8ccd8"], rings: 2, light: "#e8c8ff", lift: 0.09 },
  auroraeth:   { mantle: "#3a1a08", crown: ["points", 10, "#ffd88a"], rings: 2, light: "#ffe0a8", lift: 0.09 },
  zylaraeon:   { mantle: "#050508", crown: ["thorns", 8, "#8a1a2e"], rings: 1, light: "#ff2e4a", lift: 0.10 },
  magmantus:   { mantle: "#0e0808", crown: ["points", 6, "#ff6a1a"], rings: 1, light: "#ff6a1a", lift: 0.12 },
  vivus_dragon:{ mantle: "#0c2c28", crown: ["disc", 1, "#e8fff8"], rings: 3, light: "#4ad8b4", lift: 0.08 },
};
const FALLBACK_ASCENT = { mantle: "#241c12", crown: ["points", 7, "#f0d060"], rings: 1, light: "#f0d060", lift: 0.08 };

// The heights the regalia hangs at, per body. MEASURED off each
// body's own head group, not guessed — a crown on a Trunaran and
// a crown on a Malumora are two very different altitudes.
// crown idioms — one per shape language on the roster
// ⚠ RESTORED 2026-09-16 — the Rush → Annihilation port (7291eab) carried the
// bodies that use this table but not the table itself, so the build failed
// no-undef and the body would have thrown on its first render. Verbatim
// from app-rush/src/ieTerrorSpecies.jsx.
const CROWN = {
  points: (c, n, r) => Array.from({ length: n }).map((_, i) => {
    const a = (i / n) * Math.PI * 2;
    return (
      <mesh key={i} position={[Math.cos(a) * r, 0.045, Math.sin(a) * r]} rotation={[0.18 * Math.sin(a), -a, 0.18 * Math.cos(a)]}>
        <coneGeometry args={[r * 0.20, r * 1.05, 4]} />
        <meshStandardMaterial color={c} metalness={0.92} roughness={0.24} emissive={c} emissiveIntensity={0.30} />
      </mesh>
    );
  }),
  antlers: (c, n, r) => Array.from({ length: n }).map((_, i) => {
    const sd = i % 2 ? 1 : -1, k = Math.floor(i / 2);
    return (
      <mesh key={i} position={[sd * (r * 0.5 + k * 0.03), 0.05 + k * 0.05, -k * 0.03]} rotation={[-0.4 - k * 0.2, 0, sd * (0.5 + k * 0.2)]}>
        <coneGeometry args={[r * 0.13 - k * 0.008, r * 0.9, 5]} />
        <meshStandardMaterial color={c} roughness={0.4} metalness={0.4} emissive={c} emissiveIntensity={0.2} />
      </mesh>
    );
  }),
  disc: (c, n, r) => (
    <mesh position={[0, 0.02, -r * 0.6]}>
      <circleGeometry args={[r * 1.7, 26]} />
      <meshBasicMaterial color={c} transparent opacity={0.55} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
    </mesh>
  ),
  thorns: (c, n, r) => Array.from({ length: n }).map((_, i) => {
    const a = (i / n) * Math.PI * 2;
    return (
      <mesh key={i} position={[Math.cos(a) * r, 0.02, Math.sin(a) * r]} rotation={[Math.sin(a) * 1.1, -a, 1.25]}>
        <coneGeometry args={[r * 0.16, r * 0.75, 4]} />
        <meshStandardMaterial color={c} roughness={0.3} metalness={0.85} />
      </mesh>
    );
  }),
};

const CROWN_Y = {
  xolibrix: 0.60, ursaeva: 0.72, bunnovian: 0.78, skuryz: 0.50, trunaran: 1.14,
  kyrium: 1.34, slijj: 1.12, velkor: 0.90, noxura: 1.12, abyssia: 1.02,
  embearaea: 0.72, carcharion: 0.62, carnifex: 1.34, geulaoin: 0.92, rapaxi: 0.44,
  gorgxonox: 0.86, aeternavis: 0.92, tzhahws: 1.07, valtorix: 1.30, 
  lyraei: 1.20, malumora: 1.15, aealix: 1.36, aeolix: 1.40, calypsis: 1.26,
  caelumari: 1.36, zriidians: 1.55, hyalayri: 1.52, riven: 1.50, devorix: 1.50,
  kraalok: 1.04, lyrixi: 1.42, mygzori: 1.36,
  rosaluna: 1.08, auroraeth: 1.08, zylaraeon: 1.36, magmantus: 1.48,
  vivus_dragon: 1.12, 
};
const SHOULDER_Y = {
  xolibrix: 0.46, ursaeva: 0.60, bunnovian: 0.44, skuryz: 0.32, trunaran: 0.82,
  kyrium: 0.86, slijj: 0.76, velkor: 0.60, noxura: 0.78, abyssia: 0.62,
  embearaea: 0.50, carcharion: 0.46, carnifex: 0.92, geulaoin: 0.66, rapaxi: 0.34,
  gorgxonox: 0.78, aeternavis: 0.66, tzhahws: 0.78, valtorix: 0.78, 
  lyraei: 0.90, malumora: 0.88, aealix: 1.00, aeolix: 1.02, calypsis: 0.92,
  caelumari: 0.98, zriidians: 1.06, hyalayri: 1.04, riven: 1.04, devorix: 1.02,
  kraalok: 0.60, lyrixi: 0.98, mygzori: 0.98,
  rosaluna: 0.66, auroraeth: 0.66, zylaraeon: 0.98, magmantus: 1.02,
  vivus_dragon: 0.66, 
};

// ── THE MANTLE ──────────────────────────────────────────────
// A Champion is DRESSED. One shoulder yoke and a fall of cloth
// behind, hung off the measured shoulder height — never a scale.
function Mantle({ id, spec }) {
  const y = SHOULDER_Y[id] ?? 0.90;
  const cloth = clothTex("asc_mantle_" + id, spec.mantle, spec.mantle, "#000000", spec.light);
  const cape = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (cape.current) { cape.current.rotation.x = -0.10 + Math.sin(t * 0.9) * 0.045; cape.current.rotation.z = Math.sin(t * 0.62) * 0.030; }
  });
  const mat = { map: cloth, color: spec.mantle, roughness: 0.92, metalness: 0.08, side: THREE.DoubleSide };
  return (
    <group position={[0, y, 0]}>
      <mesh rotation={[Math.PI / 2, 0, 0]} scale={[1.18, 1.0, 0.86]}>
        <torusGeometry args={[0.185, 0.048, 8, 22]} /><meshStandardMaterial {...mat} />
      </mesh>
      <group ref={cape} position={[0, -0.02, -0.11]}>
        <mesh position={[0, -0.30, -0.10]} rotation={[0.26, 0, 0]}>
          <coneGeometry args={[0.255, 0.72, 14, 1, true]} /><meshStandardMaterial {...mat} />
        </mesh>
        <mesh position={[0, -0.62, -0.19]} rotation={[Math.PI / 2 + 0.26, 0, 0]}>
          <torusGeometry args={[0.225, 0.014, 6, 24]} />
          <meshStandardMaterial color={spec.light} metalness={0.8} roughness={0.3} emissive={spec.light} emissiveIntensity={0.3} />
        </mesh>
      </group>
      {[-1, 1].map((sd) => (
        <mesh key={sd} position={[sd * 0.185, 0.030, 0]} scale={[1.1, 0.62, 1.1]}>
          <sphereGeometry args={[0.098, 10, 8]} />
          <meshStandardMaterial color={spec.light} metalness={0.85} roughness={0.28} emissive={spec.light} emissiveIntensity={0.20} />
        </mesh>
      ))}
    </group>
  );
}

// ── THE REGALIA ─────────────────────────────────────────────
// A Lord is the form other beings kneel to: the mantle, plus a
// crown in the species' own idiom, plus haloes, plus a herald
// light at the floor that makes the ground read as a dais.
function Regalia({ id, spec }) {
  const y = CROWN_Y[id] ?? 1.20;
  const rings = useRef([]); const dais = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    rings.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.y = t * (0.22 + i * 0.13) * (i % 2 ? -1 : 1);
      m.rotation.x = 0.30 + Math.sin(t * 0.45 + i) * 0.14;
    });
    if (dais.current && dais.current.material) dais.current.material.opacity = 0.16 + Math.sin(t * 1.1) * 0.06;
  });
  const [idiom, n, c] = spec.crown;
  const draw = CROWN[idiom] || CROWN.points;
  return (
    <group>
      <group position={[0, y, 0]}>
        {draw(c, n, 0.135)}
        {idiom !== "disc" && (
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.135, 0.014, 6, 22]} />
            <meshStandardMaterial color={c} metalness={0.92} roughness={0.24} emissive={c} emissiveIntensity={0.28} />
          </mesh>
        )}
        {Array.from({ length: spec.rings }).map((_, i) => (
          <mesh key={i} ref={(el) => (rings.current[i] = el)}>
            <torusGeometry args={[0.255 + i * 0.078, 0.005, 5, 40]} />
            <meshBasicMaterial color={i === 1 ? "#ffffff" : spec.light} transparent opacity={0.40} toneMapped={false} />
          </mesh>
        ))}
      </group>
      {/* the dais — the floor answers to it now */}
      <mesh ref={dais} position={[0, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.34, 0.62, 36]} />
        <meshBasicMaterial color={spec.light} transparent opacity={0.18} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  THE DISPATCH TABLE — a row, never a branch per member
// ════════════════════════════════════════════════════════════
const SPECIES_BODIES = {
  xolibrix: XolibrixBody, ursaeva: UrsaevaBody, bunnovian: BunnovianBody,
  skuryz: SkuryzBody, trunaran: TrunaranBody, kyrium: KyriumBody,
  slijj: SlijjBody, velkor: VelkorBody, noxura: NoxuraBody,
  abyssia: AbyssiaBody, embearaea: EmbearaeaBody, carcharion: CarcharionBody,
  carnifex: CarnifexBody, geulaoin: GeulaoinBody, rapaxi: RapaxiBody,
  gorgxonox: GorgxonoxBody, aeternavis: AeternavisBody, tzhahws: TzhahwsBody,
  valtorix: ValtorixBody, lyraei: LyraeiBody,
  malumora: MalumoraBody, aealix: AealixBody, aeolix: AeolixBody,
  calypsis: CalypsisBody, caelumari: CaelumariBody, zriidians: ZriidiansBody,
  hyalayri: HyalayriBody, riven: RivenBody, devorix: DevorixBody,
  kraalok: KraalokBody,
  lyrixi: LyrixiBody, mygzori: MygzoriBody, rosaluna: RosalunaBody,
  auroraeth: AuroraethBody, zylaraeon: ZylaraeonBody, magmantus: MagmantusBody,
  vivus_dragon: VivusDragonBody, 
};

export const hasSpecies = (id) => Object.prototype.hasOwnProperty.call(SPECIES_BODIES, id);

// The public door, same shape as the first kit's `IEBeing`.
//   <TerrorSpecies being="valtorix" tier="lord" />
export function TerrorSpeciesMesh({ beingId, pal, tier = TIERS.BEB }) {
  pal = pal || getSpeciesPalette(beingId);
  const Body = SPECIES_BODIES[beingId];
  const spec = ASCENT[beingId] || FALLBACK_ASCENT;
  if (!Body) return null;
  const champion = tier === TIERS.CHAMPION || tier === TIERS.LORD;
  const lord = tier === TIERS.LORD;
  return (
    <group>
      {/* the Beb body is NEVER touched by the Ascent — it is lifted
          on its own wrapper and dressed, and that is all. */}
      <group position={[0, champion ? spec.lift : 0, 0]}>
        <Body pal={pal} />
      </group>
      {champion && <Mantle id={beingId} spec={spec} />}
      {lord && <Regalia id={beingId} spec={spec} />}
    </group>
  );
}

export function TerrorSpecies({ being, tier = TIERS.BEB, pal }) {
  return <TerrorSpeciesMesh beingId={being} tier={tier} pal={pal} />;
}

export default TerrorSpecies;
